import React, { useState, useEffect, useRef, createContext, useContext } from "react";
import "@/App.css";
import axios from "axios";
import { 
  FileText, BarChart3, MessageSquare, Library, Home, Download, Send, AlertTriangle,
  CheckCircle, ChevronRight, Menu, X, Plus, Clock, Shield, Zap, User, LogOut, LogIn,
  GitCompare, TrendingUp, AlertCircle, Calendar, DollarSign, Target, Info, History,
  MessageCircle, Upload, FileDown, ThumbsUp, ThumbsDown, RotateCcw, Eye, ChevronDown, Pencil,
  Gauge, BookOpen, Flag, Activity, PieChart, ArrowUp, ArrowDown, Minus, Edit, Trash2,
  Lightbulb, Link, Tag, HelpCircle, Users, Star, Check, Filter, Search, Layers,
  Bot, Sparkles, Loader2, GitBranch, FileCheck, Workflow, Timer, TrendingDown, 
  AlertOctagon, ClipboardList, ListChecks, BarChart2, RefreshCw, Globe, Radio, Mail, Swords, Calculator
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Toaster, toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { ScrollArea } from "./components/ui/scroll-area";
import { Badge } from "./components/ui/badge";
import { Progress } from "./components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./components/ui/tooltip";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./components/ui/collapsible";
import RealTimeCollabView from "./views/RealTimeCollabView";
import EnhancedAIView from "./views/EnhancedAIView";
import PMIntegrationView from "./views/PMIntegrationView";
import ExpandedClauseLibraryView from "./views/ExpandedClauseLibraryView";
import CorrespondencesView from "./views/CorrespondencesView";
import NegotiationSimulatorView from "./views/NegotiationSimulatorView";
import ScenarioSimulatorView from "./views/ScenarioSimulatorView";
import FinancialSummaryView from "./views/FinancialSummaryView";
import DashboardAnalyticsView from "./views/DashboardAnalyticsView";
import DelayAnalysisView from "./views/DelayAnalysisView";
import DashboardView from "./views/DashboardView";
import StructProView from "./views/StructProView";
import SignInPage from "./views/SignInPage";
import KPIDashboardView from "./views/KPIDashboardView";
import OrganizationView from "./views/OrganizationView";
import { FormDataProvider, useFormData } from "./contexts/FormDataContext";
import ExportToolbar, { exportToCSV, exportToExcel, exportToPDF } from "./components/ExportToolbar";
import { getClauseImpact } from "./components/ClauseImpactData";
import DataSecurityDisclaimer from "./components/DataSecurityDisclaimer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to extract user-friendly error messages from API errors
const getErrorMessage = (error, fallback = "Something went wrong") => {
  if (error.response?.data?.detail) return error.response.data.detail;
  if (error.response?.status === 502 || error.response?.status === 503) return "Server is temporarily busy. Please try again in a moment.";
  if (error.message?.includes("not valid JSON") || error.message?.includes("Network Error")) return "Server is temporarily unavailable. Please try again.";
  if (typeof error.response?.data === "string" && error.response.data.length < 200) return error.response.data;
  return fallback;
};

// Information Ribbon Component
const InfoRibbon = ({ title, steps, tips, color = "blue" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const colors = {
    blue: "border-[#0047AB] bg-blue-50 text-[#0047AB]",
    orange: "border-[#FF4F00] bg-orange-50 text-[#FF4F00]",
    green: "border-emerald-500 bg-emerald-50 text-emerald-700",
    purple: "border-purple-500 bg-purple-50 text-purple-700"
  };
  
  return (
    <div className={`border-l-4 ${colors[color]} p-4 mb-6`}>
      <div className="flex items-center justify-between cursor-pointer" onClick={() => setIsOpen(!isOpen)}>
        <div className="flex items-center gap-2">
          <HelpCircle size={18} />
          <span className="font-bold text-sm uppercase tracking-wide">{title}</span>
        </div>
        <ChevronDown size={18} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </div>
      {isOpen && (
        <div className="mt-3 space-y-3">
          {steps && steps.length > 0 && (
            <div>
              <p className="text-xs uppercase font-bold mb-2">Steps:</p>
              <ol className="text-sm space-y-1 ml-4">
                {steps.map((step, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="bg-current text-white rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0">{i + 1}</span>
                    <span className="text-zinc-700">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
          {tips && tips.length > 0 && (
            <div className="pt-2 border-t border-current/20">
              <p className="text-xs uppercase font-bold mb-2">Pro Tips:</p>
              <ul className="text-sm space-y-1">
                {tips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-2 text-zinc-600">
                    <Lightbulb size={14} className="flex-shrink-0 mt-0.5" />
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Info Tooltip Component - reusable throughout the app
const InfoTip = ({ text, side = "top" }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button type="button" className="inline-flex items-center justify-center ml-1 text-zinc-400 hover:text-[#0047AB] transition-colors focus:outline-none" data-testid="info-tip">
        <Info size={14} />
      </button>
    </TooltipTrigger>
    <TooltipContent side={side} className="max-w-xs text-xs leading-relaxed bg-zinc-900 text-white border-none px-3 py-2">
      {text}
    </TooltipContent>
  </Tooltip>
);

// KPI Descriptions for info icons
const KPI_INFO = {
  financial: {
    _category: "Financial KPIs measure cost performance. Enter your budgeted (planned) costs and actual spend to track financial health.",
    "Cost Performance Index (CPI)": "Ratio of earned value to actual cost. CPI > 1.0 means under budget. Enter Earned Value and Actual Cost to calculate.",
    "Budget Variance": "Difference between planned budget and actual expenditure. Negative values indicate overrun.",
    "Cost at Completion": "Projected total cost at project completion based on current spending trends.",
    "Cash Flow": "Net movement of money in/out of the project over a period.",
    "Revenue": "Total income generated or billed for the project to date.",
    "Profit Margin": "Percentage of revenue remaining after costs. Higher is better.",
  },
  schedule: {
    _category: "Schedule KPIs track timeline performance. Enter your baseline plan dates and actual progress to monitor delays.",
    "Schedule Performance Index (SPI)": "Ratio of earned value to planned value. SPI > 1.0 means ahead of schedule.",
    "Milestone Completion": "Percentage of planned milestones achieved on or before their target dates.",
    "Critical Path Variance": "Days of variance on critical path activities. Negative means behind schedule.",
    "Schedule Variance": "Difference in $ value between planned and actual progress at a point in time.",
    "Float Consumption": "Rate at which schedule float (buffer) is being used. High consumption signals risk.",
  },
  risk: {
    _category: "Risk KPIs quantify project exposure. Enter risk probability scores and severity ratings to track overall risk posture.",
    "Risk Score": "Overall risk index combining probability and impact. Scale 0-100, lower is better.",
    "Open Risks": "Count of identified risks that remain unmitigated.",
    "Risk Mitigation Rate": "Percentage of identified risks with active mitigation plans in place.",
    "Risk Exposure": "Monetary value at risk calculated as probability x impact for all open risks.",
  },
  quality: {
    _category: "Quality KPIs measure workmanship standards. Enter inspection results, NCRs raised, and rework data.",
    "NCR Rate": "Non-Conformance Reports per unit of work. Lower is better. Enter total NCRs and work units.",
    "Rework Percentage": "Proportion of completed work requiring correction. Target below 3%.",
    "First Pass Yield": "Percentage of work passing quality inspection on the first attempt.",
    "Defects per Unit": "Average defects found per work package or inspection lot.",
    "Customer Satisfaction": "Rating from client/stakeholder quality assessments. Scale 1-10.",
  },
  performance: {
    _category: "Performance KPIs measure productivity. Enter planned and actual output rates for work packages.",
    "Productivity Rate": "Actual output vs planned output per unit time. Ratio above 1.0 is good.",
    "Labour Efficiency": "Ratio of productive hours to total hours worked. Target above 85%.",
    "Equipment Utilization": "Percentage of available equipment hours actually used productively.",
    "Safety Incident Rate": "Recordable incidents per 200,000 work hours. Lower is better.",
    "Resource Utilization": "Percentage of allocated resources actively contributing to project tasks.",
  },
  contract: {
    _category: "Contract KPIs track compliance and obligations. Enter contractual deadlines, notice dates, and obligation status.",
    "Claims Submitted": "Total value of claims submitted under the contract to date.",
    "Variation Rate": "Total value of variations as a percentage of original contract value.",
    "Compliance Score": "Percentage of contractual obligations met on time. Target 100%.",
    "Dispute Count": "Number of active disputes or unresolved notices between the parties.",
    "Payment Cycle": "Average days from invoice submission to payment receipt. Compare to contract terms.",
  }
};

// KPI Sparkline - small inline SVG chart
const KPISparkline = ({ data, width = 60, height = 20 }) => {
  if (!data || data.length < 2) return null;
  const values = data.map(d => d.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(" ");
  const lastStatus = data[data.length - 1]?.status || "on_track";
  const color = lastStatus === "on_track" ? "#10b981" : lastStatus === "at_risk" ? "#f59e0b" : "#ef4444";
  return (
    <svg width={width} height={height} className="inline-block" data-testid="kpi-sparkline">
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={(values.length - 1) / (values.length - 1) * width} cy={height - ((values[values.length - 1] - min) / range) * (height - 4) - 2} r="2" fill={color} />
    </svg>
  );
};

// Auth Context
const AuthContext = createContext(null);
const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('gpi_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (e) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const loginStep1 = async (email, password) => {
    // Step 1: Send credentials, get OTP sent to email
    const res = await axios.post(`${API}/auth/login`, { email, password });
    return res.data; // Returns { message, session_token, email, otp_required }
  };

  const verifyOTP = async (email, otp, sessionToken) => {
    // Step 2: Verify OTP and complete login
    const res = await axios.post(`${API}/auth/verify-otp`, { 
      email, 
      otp, 
      session_token: sessionToken 
    });
    const { access_token, user } = res.data;
    localStorage.setItem('gpi_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(user);
    return user;
  };

  const resendOTP = async (email, sessionToken) => {
    const res = await axios.post(`${API}/auth/resend-otp`, { 
      email, 
      session_token: sessionToken 
    });
    return res.data;
  };

  const register = async (data) => {
    const res = await axios.post(`${API}/auth/register`, data);
    const { access_token, user } = res.data;
    localStorage.setItem('gpi_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(user);
    return user;
  };

  const logout = () => {
    localStorage.removeItem('gpi_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    window.location.reload();
  };

  const checkSubscriptionStatus = () => {
    if (!user) return { status: 'none', hasFullAccess: false };
    
    const now = new Date();
    const trialEnds = user.trial_ends_at ? new Date(user.trial_ends_at) : null;
    const subEnds = user.subscription_ends_at ? new Date(user.subscription_ends_at) : null;
    
    if (user.subscription_status === 'active' && subEnds && subEnds > now) {
      return { status: 'active', hasFullAccess: true, expiresAt: subEnds };
    }
    
    if (trialEnds && trialEnds > now) {
      const daysLeft = Math.ceil((trialEnds - now) / (1000 * 60 * 60 * 24));
      return { status: 'trial', hasFullAccess: true, expiresAt: trialEnds, daysLeft };
    }
    
    return { status: 'expired', hasFullAccess: false };
  };

  return (
    <AuthContext.Provider value={{ 
      user, token, loginStep1, verifyOTP, resendOTP, register, logout, loading,
      checkSubscriptionStatus, refreshUser: fetchUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Info Popup Component
const ClauseInfoPopup = ({ clause }) => {
  if (!clause.info_popup) return null;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button className="p-1 hover:bg-zinc-100 rounded-full transition-colors" data-testid={`info-btn-${clause.id}`}>
            <Info size={16} className="text-[#0047AB]" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-sm p-4 bg-white border-2 border-black shadow-lg">
          <h4 className="font-bold text-sm mb-2">{clause.info_popup.title}</h4>
          <p className="text-xs text-zinc-600 mb-3">{clause.info_popup.description}</p>
          <div className="space-y-1 mb-3">
            {clause.info_popup.key_points?.map((point, i) => (
              <div key={i} className="flex items-start gap-2 text-xs">
                <CheckCircle size={12} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>{point}</span>
              </div>
            ))}
          </div>
          {clause.info_popup.risk_warning && (
            <div className="p-2 bg-amber-50 border-l-2 border-amber-500 text-xs">
              <AlertTriangle size={12} className="inline mr-1 text-amber-500" />
              {clause.info_popup.risk_warning}
            </div>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Auth Modal with 2FA OTP
const AuthModal = ({ isOpen, onClose }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({ email: '', password: '', name: '', company: '' });
  const [loading, setLoading] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [otp, setOtp] = useState('');
  const [sessionToken, setSessionToken] = useState('');
  const [otpEmail, setOtpEmail] = useState('');
  const { loginStep1, verifyOTP, resendOTP, register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        // Step 1: Send credentials
        const response = await loginStep1(formData.email, formData.password);
        if (response.otp_required) {
          setSessionToken(response.session_token);
          setOtpEmail(response.email);
          setOtpStep(true);
          toast.success("OTP sent to your email!");
        }
      } else {
        await register(formData);
        toast.success("Account created! 7-day trial started.");
        onClose();
      }
    } catch (error) {
      const msg = getErrorMessage(error, "Authentication failed");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleOTPVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verifyOTP(otpEmail, otp, sessionToken);
      toast.success("Welcome back!");
      onClose();
      resetForm();
    } catch (error) {
      const msg = getErrorMessage(error, "Invalid OTP");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    try {
      await resendOTP(otpEmail, sessionToken);
      toast.success("New OTP sent!");
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to resend OTP"));
    }
  };

  const resetForm = () => {
    setFormData({ email: '', password: '', name: '', company: '' });
    setOtpStep(false);
    setOtp('');
    setSessionToken('');
    setOtpEmail('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border-2 border-black brutal-shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="auth-modal">
        <div className="p-6 border-b border-zinc-200 flex items-center justify-between">
          <h2 className="text-xl font-black font-['Chivo'] uppercase">
            {otpStep ? 'Verify OTP' : isLogin ? 'Sign In' : 'Create Account'}
          </h2>
          <button onClick={() => { onClose(); resetForm(); }} className="p-2 hover:bg-zinc-100"><X size={20} /></button>
        </div>
        
        {otpStep ? (
          <form onSubmit={handleOTPVerify} className="p-6 space-y-4">
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-[#0047AB] rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="text-white" size={32} />
              </div>
              <p className="text-zinc-600">Enter the 6-digit code sent to</p>
              <p className="font-bold text-[#0047AB]">{otpEmail}</p>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2">OTP Code *</label>
              <input 
                type="text" 
                value={otp} 
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} 
                className="underline-input w-full text-center text-2xl tracking-widest font-mono" 
                placeholder="000000"
                maxLength={6}
                required 
                data-testid="input-otp" 
              />
            </div>
            <Button type="submit" disabled={loading || otp.length !== 6} className="btn-brutal w-full bg-black text-white hover:bg-zinc-800 py-3 h-auto" data-testid="btn-verify-otp">
              {loading ? 'Verifying...' : 'Verify & Sign In'}
            </Button>
            <div className="flex items-center justify-between text-sm">
              <button type="button" onClick={handleResendOTP} className="text-[#0047AB] hover:underline">Resend OTP</button>
              <button type="button" onClick={() => setOtpStep(false)} className="text-zinc-500 hover:underline">Back to Login</button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {!isLogin && (
              <>
                <div className="bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-700 mb-4">
                  <span className="font-bold">🎉 7-Day Free Trial!</span> Full access to all features.
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2">Name *</label>
                  <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="underline-input w-full" required data-testid="input-name" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2">Company</label>
                  <input type="text" value={formData.company} onChange={(e) => setFormData({...formData, company: e.target.value})} className="underline-input w-full" />
                </div>
              </>
            )}
            <div>
              <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2">Email *</label>
              <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="underline-input w-full" required data-testid="input-email" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2">Password *</label>
              <input type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} className="underline-input w-full" required data-testid="input-password" />
            </div>
            <Button type="submit" disabled={loading} className="btn-brutal w-full bg-black text-white hover:bg-zinc-800 py-3 h-auto" data-testid="btn-auth-submit">
              {loading ? '...' : isLogin ? 'Continue to OTP' : 'Start Free Trial'}
            </Button>
            <p className="text-center text-sm text-zinc-500">
              {isLogin ? "Don't have an account?" : "Already have an account?"}{' '}
              <button type="button" onClick={() => setIsLogin(!isLogin)} className="text-[#0047AB] hover:underline font-medium">{isLogin ? 'Sign up' : 'Sign in'}</button>
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

// Subscription Banner Component
const SubscriptionBanner = ({ onUpgrade }) => {
  const { user, checkSubscriptionStatus } = useAuth();
  
  if (!user) return null;
  
  const status = checkSubscriptionStatus();
  
  if (status.status === 'active') {
    return (
      <div className="bg-emerald-50 border-b border-emerald-200 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-emerald-700">
          <CheckCircle size={16} />
          <span>Subscription active until {new Date(status.expiresAt).toLocaleDateString()}</span>
        </div>
      </div>
    );
  }
  
  if (status.status === 'trial') {
    return (
      <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-amber-700">
          <Clock size={16} />
          <span><strong>{status.daysLeft} days</strong> left in your free trial</span>
        </div>
        <button onClick={onUpgrade} className="text-sm font-medium text-amber-800 hover:underline flex items-center gap-1">
          Upgrade Now <ChevronRight size={14} />
        </button>
      </div>
    );
  }
  
  // Expired
  return (
    <div className="bg-red-50 border-b border-red-200 px-4 py-2 flex items-center justify-between">
      <div className="flex items-center gap-2 text-sm text-red-700">
        <AlertCircle size={16} />
        <span>Your trial has expired. Subscribe to continue creating contracts.</span>
      </div>
      <button onClick={onUpgrade} className="bg-red-600 text-white px-4 py-1 text-sm font-medium hover:bg-red-700">
        Subscribe Now
      </button>
    </div>
  );
};

// Subscription Modal
const SubscriptionModal = ({ isOpen, onClose }) => {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [selectedTier, setSelectedTier] = useState(null); // null, 'individual', 'organization'
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    if (isOpen) { setSelectedTier(null); setSelectedPlan(null); setPlans([]); }
  }, [isOpen]);

  const fetchPlans = async (tier) => {
    setSelectedTier(tier);
    setSelectedPlan(null);
    try {
      const res = await axios.get(`${API}/subscription/plans?tier=${tier}`);
      setPlans(res.data.plans);
    } catch (e) {
      console.error("Failed to fetch plans");
    }
  };

  const handleSubscribe = async () => {
    if (!selectedPlan) { toast.error("Please select a plan"); return; }
    setLoading(true);
    try {
      const orderRes = await axios.post(`${API}/subscription/create-order`, { plan_id: selectedPlan });
      const order = orderRes.data;

      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "GPI.Contracts - Project Command Center",
        description: order.description,
        order_id: order.order_id,
        prefill: { name: order.user_name, email: order.user_email },
        theme: { color: "#0047AB" },
        handler: async function (response) {
          try {
            await axios.post(`${API}/subscription/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan_id: selectedPlan
            });
            toast.success("Subscription activated!");
            refreshUser();
            onClose();
          } catch (e) { toast.error("Payment verification failed"); }
        }
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create order");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const periodLabel = (months) => {
    if (months === 1) return "/month";
    if (months === 3) return "/quarter";
    if (months === 6) return "/half-year";
    if (months === 12) return "/year";
    return `/${months}mo`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border-2 border-black brutal-shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="subscription-modal">
        <div className="p-6 border-b border-zinc-200 flex items-center justify-between">
          <h2 className="text-xl font-black font-['Chivo'] uppercase">Upgrade Your Plan</h2>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100"><X size={20} /></button>
        </div>
        <div className="p-6">
          {/* Step 1: Choose Tier */}
          {!selectedTier ? (
            <div className="space-y-4" data-testid="tier-selector">
              <p className="text-sm text-zinc-600 mb-4">How will you be using GPI.Contracts?</p>
              <div className="grid grid-cols-2 gap-4">
                <button onClick={() => fetchPlans('individual')} className="border-2 border-zinc-200 hover:border-[#0047AB] p-6 text-center transition-colors group" data-testid="btn-tier-individual">
                  <User size={32} className="mx-auto mb-3 text-zinc-400 group-hover:text-[#0047AB] transition-colors" />
                  <h3 className="font-bold text-lg">Individual</h3>
                  <p className="text-xs text-zinc-500 mt-1">For solo professionals</p>
                  <p className="text-xs text-zinc-400 mt-2">Starting from $25/mo</p>
                </button>
                <button onClick={() => fetchPlans('organization')} className="border-2 border-zinc-200 hover:border-[#FF4F00] p-6 text-center transition-colors group" data-testid="btn-tier-organization">
                  <Users size={32} className="mx-auto mb-3 text-zinc-400 group-hover:text-[#FF4F00] transition-colors" />
                  <h3 className="font-bold text-lg">Organization</h3>
                  <p className="text-xs text-zinc-500 mt-1">For teams up to 4 members</p>
                  <p className="text-xs text-zinc-400 mt-2">Starting from $300/mo</p>
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4" data-testid="plan-selector">
              {/* Back button */}
              <button onClick={() => { setSelectedTier(null); setPlans([]); setSelectedPlan(null); }} className="flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-800 mb-2" data-testid="btn-back-tier">
                <ChevronRight size={14} className="rotate-180" /> Change plan type
              </button>

              <h3 className="font-bold uppercase text-sm tracking-wider">{selectedTier === 'individual' ? 'Individual' : 'Organization'} Plans</h3>

              {/* Features list */}
              {plans.length > 0 && (
                <div className="bg-zinc-50 border border-zinc-200 p-4 mb-4">
                  <p className="text-xs font-bold uppercase text-zinc-500 mb-2">Included Features</p>
                  <ul className="space-y-1.5">
                    {plans[0].features.map((f, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <CheckCircle size={14} className="text-emerald-500 flex-shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Plan options */}
              <div className="space-y-2">
                {plans.map((plan) => {
                  const isSelected = selectedPlan === plan.id;
                  const monthly = (plan.amount / plan.period_months).toFixed(0);
                  return (
                    <button
                      key={plan.id}
                      onClick={() => setSelectedPlan(plan.id)}
                      className={`w-full border-2 p-4 flex items-center justify-between transition-all ${isSelected ? 'border-[#0047AB] bg-blue-50' : 'border-zinc-200 hover:border-zinc-400'}`}
                      data-testid={`plan-option-${plan.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${isSelected ? 'border-[#0047AB]' : 'border-zinc-300'}`}>
                          {isSelected && <div className="w-3 h-3 rounded-full bg-[#0047AB]" />}
                        </div>
                        <div className="text-left">
                          <p className="font-bold text-sm">{plan.period_months === 1 ? 'Monthly' : plan.period_months === 3 ? 'Quarterly' : plan.period_months === 6 ? 'Half-Yearly' : 'Yearly'}</p>
                          {plan.period_months > 1 && (
                            <p className="text-[10px] text-emerald-600 font-medium">~${monthly}/mo effective</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-black font-['Chivo']">${plan.amount}</p>
                        <p className="text-[10px] text-zinc-500">{periodLabel(plan.period_months)}</p>
                      </div>
                    </button>
                  );
                })}
              </div>

              <Button 
                onClick={handleSubscribe} 
                disabled={loading || !selectedPlan} 
                className="btn-brutal w-full bg-[#0047AB] text-white hover:bg-[#003380] py-4 h-auto text-lg mt-4"
                data-testid="btn-subscribe"
              >
                {loading ? 'Processing...' : `Subscribe Now${selectedPlan ? ` - $${plans.find(p => p.id === selectedPlan)?.amount || ''}` : ''}`}
              </Button>
              <p className="text-center text-xs text-zinc-500 mt-2">
                Secure payment powered by Razorpay. Cancel anytime.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Sidebar
const Sidebar = ({ activeView, setActiveView, isOpen, setIsOpen }) => {
  const { user, logout } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  
  const navGroups = [
    { items: [{ id: "dashboard", label: "Dashboard", icon: Home }] },
    { group: "Contract Management", items: [
      { id: "generator", label: "Contract Generator", icon: FileText },
      { id: "analyzer", label: "Contract Analyzer", icon: Search },
      { id: "compare", label: "Compare Contracts", icon: GitCompare },
      { id: "clauses", label: "Clause Library", icon: Library },
      { id: "expanded-clauses", label: "Expanded Library", icon: Globe },
      { id: "contracts", label: "My Contracts", icon: Download },
    ]},
    { group: "Planning", items: [
      { id: "timelines", label: "Timelines", icon: Timer },
      { id: "structpro", label: "StructPro Estimator", icon: Calculator },
      { id: "pm-integration", label: "Programme Integration", icon: Layers },
    ]},
    { group: "Project Controls", items: [
      { id: "controls", label: "Project Controls", icon: Gauge },
      { id: "kpi-dashboard", label: "KPI Dashboard", icon: Target },
      { id: "financial", label: "Financial Summary", icon: DollarSign },
      { id: "analytics", label: "Analytics Dashboard", icon: BarChart3 },
    ]},
    { group: "Change & Risk Management", items: [
      { id: "delay-analysis", label: "Delay Analysis", icon: Clock },
      { id: "delay", label: "Delay Prediction", icon: TrendingDown },
      { id: "risk-analysis", label: "Risk Analysis", icon: AlertOctagon },
      { id: "claims", label: "Claim Prevention", icon: Shield },
      { id: "changes", label: "Change Management", icon: GitBranch },
      { id: "correspondences", label: "Correspondences", icon: Mail },
    ]},
    { group: "PM & Collaboration", items: [
      { id: "pmtools", label: "PM Tools", icon: Upload },
      { id: "realtime", label: "Real-Time Collab", icon: Radio },
      { id: "copilot", label: "AI Copilot", icon: Bot },
      { id: "ai-engine", label: "AI Analysis Engine", icon: Zap },
      { id: "organization", label: "Organization", icon: Users },
    ]},
    { group: "Negotiation Simulators", items: [
      { id: "negotiation", label: "Negotiation Sim", icon: Swords },
      { id: "scenarios", label: "Scenario Simulator", icon: Activity },
    ]},
  ];

  const [expandedGroups, setExpandedGroups] = useState(() => {
    const all = {};
    navGroups.forEach(g => { if (g.group) all[g.group] = true; });
    return all;
  });

  const toggleGroup = (group) => setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));

  return (
    <>
      <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} />
      {isOpen && <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setIsOpen(false)} />}
      <aside className={`sidebar fixed md:static h-screen w-64 z-50 flex flex-col transition-transform duration-300 ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`} data-testid="sidebar">
        <div className="p-6 border-b border-zinc-800">
          <h1 className="text-xl font-black tracking-tight uppercase font-['Chivo']">GPI<span className="text-[#FF4F00]">.</span>Contracts</h1>
          <p className="text-xs text-zinc-500 mt-1 tracking-wide">Contract Management</p>
        </div>
        <nav className="flex-1 p-3 overflow-y-auto">
          {navGroups.map((section, gi) => (
            <div key={gi} className={section.group ? "mb-1" : "mb-2"}>
              {section.group ? (
                <>
                  <button onClick={() => toggleGroup(section.group)}
                    className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 hover:text-zinc-300 transition-colors">
                    <span>{section.group}</span>
                    <ChevronDown size={12} className={`transition-transform duration-200 ${expandedGroups[section.group] ? "" : "-rotate-90"}`} />
                  </button>
                  {expandedGroups[section.group] && (
                    <div className="space-y-0.5 ml-1">
                      {section.items.map((item) => {
                        const Icon = item.icon;
                        return (
                          <button key={item.id} onClick={() => { setActiveView(item.id); setIsOpen(false); }}
                            className={`sidebar-link w-full flex items-center gap-3 px-3 py-2 text-[13px] font-medium transition-all ${activeView === item.id ? 'active' : 'text-zinc-400 hover:text-white'}`}
                            data-testid={`nav-${item.id}`}>
                            <Icon size={16} /><span>{item.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              ) : (
                section.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button key={item.id} onClick={() => { setActiveView(item.id); setIsOpen(false); }}
                      className={`sidebar-link w-full flex items-center gap-3 px-3 py-3 text-sm font-medium transition-all ${activeView === item.id ? 'active' : 'text-zinc-400 hover:text-white'}`}
                      data-testid={`nav-${item.id}`}>
                      <Icon size={18} /><span>{item.label}</span>
                    </button>
                  );
                })
              )}
            </div>
          ))}
        </nav>
        <div className="p-4 border-t border-zinc-800">
          {user ? (
            <div className="space-y-3">
              <div className="px-4 py-2 bg-zinc-900 text-xs">
                <p className="text-zinc-500 uppercase tracking-wider">Signed in as</p>
                <p className="text-white font-medium truncate">{user.name}</p>
              </div>
              <button onClick={logout} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-zinc-400 hover:text-white transition-colors" data-testid="btn-logout">
                <LogOut size={16} />Sign Out
              </button>
            </div>
          ) : (
            <button onClick={() => setShowAuth(true)} className="w-full flex items-center gap-2 px-4 py-3 bg-[#FF4F00] text-white text-sm font-medium" data-testid="btn-signin">
              <LogIn size={16} />Sign In
            </button>
          )}
        </div>
      </aside>
    </>
  );
};

// DashboardView is now imported from ./views/DashboardView

// Contract Analyzer View
const ContractAnalyzerView = () => {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState("upload");
  const [selectedHistoryId, setSelectedHistoryId] = useState(null);
  const [askQuestion, setAskQuestion] = useState("");
  const [additionalInsight, setAdditionalInsight] = useState(null);
  const [insightLoading, setInsightLoading] = useState(false);
  const [generatedContracts, setGeneratedContracts] = useState([]);
  const [selectedExisting, setSelectedExisting] = useState("");
  const [existingLoading, setExistingLoading] = useState(false);

  useEffect(() => {
    if (user) { fetchHistory(); fetchGeneratedContracts(); }
  }, [user]);

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/analyze/history`);
      setHistory(res.data);
    } catch (e) { console.log("Failed to fetch history"); }
  };

  const fetchGeneratedContracts = async () => {
    try {
      const res = await axios.get(`${API}/contracts`);
      setGeneratedContracts(res.data || []);
    } catch (e) { console.log("Failed to fetch contracts"); }
  };

  const analyzeExistingContract = async () => {
    if (!selectedExisting) { toast.error("Select a contract"); return; }
    setExistingLoading(true);
    setAnalysis(null);
    try {
      const contractRes = await axios.get(`${API}/contracts/${selectedExisting}`);
      const contract = contractRes.data;
      // Build text from contract data for analysis
      const clauseTexts = (contract.clauses || []).map((c, i) => `${i+1}. ${c.title || c}: ${c.text || ''}`).join('\n');
      const contractText = `Contract: ${contract.project_name || contract.name}\nFramework: ${contract.framework}\nSector: ${contract.sector}\nDelivery: ${contract.delivery_model}\nValue: $${contract.contract_value || 'N/A'}\nDuration: ${contract.duration_months || 'N/A'} months\n\nClauses:\n${clauseTexts}`;
      
      // Use the analyze endpoint with constructed text
      const formData = new FormData();
      const blob = new Blob([contractText], { type: 'text/plain' });
      formData.append('file', blob, `${contract.project_name || 'contract'}.txt`);
      const response = await axios.post(`${API}/analyze/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      });
      setAnalysis(response.data);
      setActiveTab("results");
      toast.success("Contract analyzed!");
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to analyze");
    } finally { setExistingLoading(false); }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a file");
      return;
    }
    setLoading(true);
    setAnalysis(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/analyze/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000 // 2 min timeout for large documents
      });
      setAnalysis(response.data);
      setActiveTab("results");
      toast.success("Contract analyzed successfully!");
      if (user) fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to analyze contract");
    } finally {
      setLoading(false);
    }
  };

  const loadFromHistory = async (id) => {
    try {
      const res = await axios.get(`${API}/analyze/${id}`);
      setAnalysis(res.data);
      setSelectedHistoryId(id);
      setActiveTab("results");
    } catch (e) { toast.error("Failed to load analysis"); }
  };

  const askAdditionalQuestion = async () => {
    if (!askQuestion.trim() || !analysis) return;
    setInsightLoading(true);
    try {
      const res = await axios.post(`${API}/analyze/${analysis.id}/additional-insights`, null, {
        params: { question: askQuestion }
      });
      setAdditionalInsight(res.data);
      setAskQuestion("");
    } catch (e) { toast.error("Failed to get insights"); }
    finally { setInsightLoading(false); }
  };

  const getRiskColor = (score) => {
    if (score >= 70) return "text-red-500";
    if (score >= 50) return "text-amber-500";
    return "text-emerald-500";
  };

  const getRiskBgColor = (level) => {
    if (level === "high" || level === "critical") return "bg-red-100 border-red-500";
    if (level === "medium") return "bg-amber-100 border-amber-500";
    return "bg-emerald-100 border-emerald-500";
  };

  const tabs = [
    { id: "upload", label: "Upload Contract", icon: Upload },
    { id: "results", label: "Analysis Results", icon: BarChart3 },
    { id: "clauses", label: "Clauses", icon: FileText },
    { id: "kpis", label: "KPIs & Requirements", icon: Target },
    { id: "impact", label: "Impact Analysis", icon: TrendingUp },
    { id: "recommendations", label: "Recommendations", icon: Lightbulb },
    { id: "history", label: "History", icon: History }
  ];

  return (
    <div className="space-y-6" data-testid="analyzer-view" data-export-region>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight uppercase font-['Chivo'] mb-2">Contract Analyzer</h1>
          <p className="text-zinc-500">Upload any contract to analyze, summarize, and extract insights for better decision making</p>
        </div>
        <ExportToolbar title="Contract Analysis" pdfFilename="contract-analysis" />
      </div>

      <InfoRibbon
        title="How to Use Contract Analyzer"
        color="purple"
        steps={[
          "Upload your contract document (PDF, DOCX, or TXT format)",
          "Wait for AI analysis to complete (may take 1-2 minutes for large documents)",
          "Review extracted clauses, KPIs, and requirements",
          "Check impact factors and predictive analysis",
          "Use recommendations for project planning and risk mitigation"
        ]}
        tips={[
          "For best results, upload contracts with clear text formatting",
          "Use 'Ask AI' feature for specific questions about the contract",
          "Review impact factors before project kickoff"
        ]}
      />

      {/* Tabs */}
      <div className="border-b border-zinc-200 flex overflow-x-auto">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === tab.id ? 'border-black text-black' : 'border-transparent text-zinc-500 hover:text-zinc-700'}`}>
              <Icon size={16} />{tab.label}
            </button>
          );
        })}
      </div>

      {/* Upload Tab */}
      {activeTab === "upload" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="border border-zinc-200 bg-white brutal-shadow p-6">
            <h2 className="font-bold font-['Chivo'] uppercase tracking-wide mb-6 pb-4 border-b border-zinc-200">Upload Contract Document</h2>
            <div className="space-y-4">
              <div className="border-2 border-dashed border-zinc-300 p-8 text-center hover:border-[#0047AB] transition-colors">
                <Search className="mx-auto mb-4 text-zinc-400" size={48} />
                <input type="file" accept=".pdf,.docx,.doc,.txt" onChange={(e) => setFile(e.target.files[0])} className="hidden" id="contract-upload" />
                <label htmlFor="contract-upload" className="cursor-pointer">
                  <span className="text-[#0047AB] hover:underline font-medium">Choose file</span>
                  <span className="text-zinc-500"> or drag and drop</span>
                </label>
                <p className="text-xs text-zinc-400 mt-2">Supports PDF, DOCX, DOC, TXT files</p>
              </div>
              {file && (
                <div className="p-3 bg-zinc-50 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText size={18} className="text-[#0047AB]" />
                    <span className="text-sm font-medium">{file.name}</span>
                    <span className="text-xs text-zinc-400">({(file.size / 1024).toFixed(1)} KB)</span>
                  </div>
                  <button onClick={() => setFile(null)} className="text-zinc-400 hover:text-zinc-600"><X size={16} /></button>
                </div>
              )}
              <Button onClick={handleUpload} disabled={loading || !file} className="btn-brutal w-full bg-black text-white py-3 h-auto" data-testid="btn-analyze">
                {loading ? (
                  <span className="flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Analyzing Contract...</span>
                ) : 'Analyze Contract'}
              </Button>
              {loading && (
                <div className="text-center text-sm text-zinc-500">
                  <p>AI is analyzing your contract. This may take 1-2 minutes for large documents.</p>
                  <Progress value={undefined} className="mt-2 h-1" />
                </div>
              )}
            </div>
          </div>

          <div className="space-y-6">
            {/* Select from Generated Contracts */}
            <div className="border-2 border-black brutal-shadow bg-white p-6">
              <h3 className="font-bold font-['Chivo'] uppercase mb-4 flex items-center gap-2"><Layers size={18} /> Analyze Generated Contract</h3>
              <p className="text-xs text-zinc-500 mb-4">Select any previously generated contract to run a full analysis</p>
              {generatedContracts.length > 0 ? (
                <div className="space-y-3">
                  <select value={selectedExisting} onChange={e => setSelectedExisting(e.target.value)} className="w-full border-2 border-zinc-200 p-2.5 text-sm focus:border-black outline-none" data-testid="select-existing-contract">
                    <option value="">Choose a generated contract...</option>
                    {generatedContracts.map(c => (
                      <option key={c.id} value={c.id}>{c.project_name || c.name} ({c.framework})</option>
                    ))}
                  </select>
                  {selectedExisting && (() => { const c = generatedContracts.find(x => x.id === selectedExisting); return c ? (
                    <div className="p-3 bg-zinc-50 border border-zinc-200 text-sm space-y-1">
                      <p><strong>{c.project_name || c.name}</strong></p>
                      <p className="text-zinc-500">{c.framework} | {c.sector} | {c.delivery_model}</p>
                      <p className="text-zinc-500">Value: {c.contract_value ? `$${parseInt(c.contract_value).toLocaleString()}` : 'N/A'} | Clauses: {c.clauses?.length || 0}</p>
                    </div>
                  ) : null; })()}
                  <Button onClick={analyzeExistingContract} disabled={existingLoading || !selectedExisting} className="w-full bg-[#0047AB] text-white py-2.5 h-auto" data-testid="btn-analyze-existing">
                    {existingLoading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Analyzing...</> : 'Analyze This Contract'}
                  </Button>
                </div>
              ) : (
                <div className="text-center p-4 text-zinc-400 border border-dashed border-zinc-300">
                  <FileText size={24} className="mx-auto mb-2 opacity-50" />
                  <p className="text-xs">No generated contracts found yet</p>
                </div>
              )}
            </div>

            <div className="border-2 border-[#0047AB] bg-blue-50 p-6">
              <h3 className="font-bold font-['Chivo'] uppercase mb-4 text-[#0047AB] flex items-center gap-2"><Lightbulb size={20} /> What You'll Get</h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2"><CheckCircle size={16} className="text-emerald-500 mt-0.5" /> Executive summary and key highlights</li>
                <li className="flex items-start gap-2"><CheckCircle size={16} className="text-emerald-500 mt-0.5" /> Categorized clause extraction</li>
                <li className="flex items-start gap-2"><CheckCircle size={16} className="text-emerald-500 mt-0.5" /> KPIs and performance metrics</li>
                <li className="flex items-start gap-2"><CheckCircle size={16} className="text-emerald-500 mt-0.5" /> Predictive impact & risk assessment</li>
                <li className="flex items-start gap-2"><CheckCircle size={16} className="text-emerald-500 mt-0.5" /> PM and Director recommendations</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Results Tab */}
      {activeTab === "results" && analysis && (
        <div className="space-y-6">
          {/* Contract Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="col-span-2 border border-zinc-200 bg-white brutal-shadow p-6">
              <h3 className="font-bold font-['Chivo'] uppercase mb-4">Contract Overview</h3>
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-xs uppercase text-zinc-500">Title</p><p className="font-medium">{analysis.contract_title || 'Not specified'}</p></div>
                <div><p className="text-xs uppercase text-zinc-500">Type</p><p className="font-medium">{analysis.contract_type || 'Not specified'}</p></div>
                <div><p className="text-xs uppercase text-zinc-500">Value</p><p className="font-medium">{analysis.contract_value || 'Not specified'}</p></div>
                <div><p className="text-xs uppercase text-zinc-500">Duration</p><p className="font-medium">{analysis.contract_duration || 'Not specified'}</p></div>
                <div><p className="text-xs uppercase text-zinc-500">Governing Law</p><p className="font-medium">{analysis.governing_law || 'Not specified'}</p></div>
                <div><p className="text-xs uppercase text-zinc-500">Parties</p><p className="font-medium">{analysis.parties?.map(p => p.name).join(', ') || 'Not specified'}</p></div>
              </div>
            </div>
            <div className={`border-2 p-6 text-center ${analysis.overall_risk_score >= 70 ? 'border-red-500 bg-red-50' : analysis.overall_risk_score >= 50 ? 'border-amber-500 bg-amber-50' : 'border-emerald-500 bg-emerald-50'}`}>
              <p className="text-xs uppercase text-zinc-500 mb-2">Overall Risk Score</p>
              <p className={`text-5xl font-black font-['Chivo'] ${getRiskColor(analysis.overall_risk_score)}`}>{analysis.overall_risk_score || 50}</p>
              <Badge className={`mt-2 ${analysis.overall_risk_score >= 70 ? 'bg-red-500' : analysis.overall_risk_score >= 50 ? 'bg-amber-500' : 'bg-emerald-500'} text-white border-none`}>
                {analysis.overall_risk_score >= 70 ? 'High Risk' : analysis.overall_risk_score >= 50 ? 'Medium Risk' : 'Low Risk'}
              </Badge>
            </div>
          </div>

          {/* Executive Summary */}
          <div className="border border-zinc-200 bg-white brutal-shadow p-6">
            <h3 className="font-bold font-['Chivo'] uppercase mb-4">Executive Summary</h3>
            <p className="text-sm leading-relaxed whitespace-pre-wrap">{analysis.executive_summary}</p>
          </div>

          {/* Key Highlights */}
          {analysis.key_highlights?.length > 0 && (
            <div className="border-2 border-[#0047AB] bg-blue-50 p-6">
              <h3 className="font-bold font-['Chivo'] uppercase mb-4 text-[#0047AB]">Key Highlights</h3>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {analysis.key_highlights.map((highlight, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm bg-white p-3">
                    <CheckCircle size={16} className="text-[#0047AB] mt-0.5 flex-shrink-0" />
                    {highlight}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Risk Breakdown */}
          {analysis.risk_breakdown && Object.keys(analysis.risk_breakdown).length > 0 && (
            <div className="border border-zinc-200 bg-white brutal-shadow p-6">
              <h3 className="font-bold font-['Chivo'] uppercase mb-4">Risk Breakdown</h3>
              <div className="grid grid-cols-5 gap-4">
                {Object.entries(analysis.risk_breakdown).map(([key, value]) => (
                  <div key={key} className="text-center">
                    <p className="text-xs uppercase text-zinc-500 mb-2">{key}</p>
                    <div className="relative w-16 h-16 mx-auto">
                      <svg className="w-16 h-16 transform -rotate-90">
                        <circle cx="32" cy="32" r="28" fill="none" stroke="#e5e7eb" strokeWidth="6" />
                        <circle cx="32" cy="32" r="28" fill="none" stroke={value >= 70 ? '#ef4444' : value >= 50 ? '#f59e0b' : '#10b981'} strokeWidth="6" strokeDasharray={`${value * 1.76} 176`} />
                      </svg>
                      <span className={`absolute inset-0 flex items-center justify-center font-bold ${getRiskColor(value)}`}>{value}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Critical Dates */}
          {analysis.critical_dates?.length > 0 && (
            <div className="border border-zinc-200 bg-white brutal-shadow p-6">
              <h3 className="font-bold font-['Chivo'] uppercase mb-4">Critical Dates</h3>
              <div className="space-y-2">
                {analysis.critical_dates.map((date, i) => (
                  <div key={i} className={`p-3 border-l-4 ${date.importance === 'high' ? 'border-red-500 bg-red-50' : date.importance === 'medium' ? 'border-amber-500 bg-amber-50' : 'border-zinc-300 bg-zinc-50'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Calendar size={16} />
                        <span className="font-medium">{date.date}</span>
                      </div>
                      <Badge variant="outline">{date.importance}</Badge>
                    </div>
                    <p className="text-sm text-zinc-600 mt-1">{date.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Ask AI */}
          <div className="border-2 border-purple-500 bg-purple-50 p-6">
            <h3 className="font-bold font-['Chivo'] uppercase mb-4 text-purple-700 flex items-center gap-2"><MessageSquare size={20} /> Ask AI About This Contract</h3>
            <div className="flex gap-2">
              <input type="text" value={askQuestion} onChange={(e) => setAskQuestion(e.target.value)} placeholder="Ask any question about this contract..." className="flex-1 px-4 py-2 border border-purple-300 bg-white" onKeyPress={(e) => e.key === 'Enter' && askAdditionalQuestion()} />
              <Button onClick={askAdditionalQuestion} disabled={insightLoading} className="bg-purple-600 text-white">
                {insightLoading ? '...' : <Send size={16} />}
              </Button>
            </div>
            {additionalInsight && (
              <div className="mt-4 p-4 bg-white border border-purple-200">
                <p className="text-xs text-purple-600 mb-2"><strong>Q:</strong> {additionalInsight.question}</p>
                <p className="text-sm whitespace-pre-wrap">{additionalInsight.response}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Clauses Tab */}
      {activeTab === "clauses" && analysis && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold font-['Chivo'] uppercase">Extracted Clauses ({analysis.clauses?.length || 0})</h3>
          </div>
          {analysis.clauses?.length > 0 ? (
            <div className="space-y-3">
              {analysis.clauses.map((clause, i) => (
                <div key={i} className={`border-l-4 ${clause.risk_level === 'high' ? 'border-red-500 bg-red-50' : clause.risk_level === 'medium' ? 'border-amber-500 bg-amber-50' : 'border-emerald-500 bg-emerald-50'} p-4`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {clause.clause_number && <Badge variant="outline">{clause.clause_number}</Badge>}
                        <Badge className={`${clause.risk_level === 'high' ? 'bg-red-500' : clause.risk_level === 'medium' ? 'bg-amber-500' : 'bg-emerald-500'} text-white border-none`}>{clause.risk_level}</Badge>
                        <Badge variant="outline">{clause.category}</Badge>
                        {clause.framework_match && clause.framework_match !== "None" && (
                          <Badge className="bg-[#0047AB] text-white border-none">{clause.framework_match}</Badge>
                        )}
                      </div>
                      <h4 className="font-bold">{clause.title}</h4>
                      <p className="text-sm text-zinc-600 mt-1">{clause.content}</p>
                      {clause.key_obligations?.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs uppercase text-zinc-500">Key Obligations:</p>
                          <ul className="text-xs text-zinc-600 ml-4 mt-1">
                            {clause.key_obligations.map((ob, j) => <li key={j} className="list-disc">{ob}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="border border-dashed border-zinc-300 p-12 text-center">
              <FileText className="mx-auto mb-4 text-zinc-400" size={48} />
              <p className="text-zinc-500">No clauses extracted. Upload a contract first.</p>
            </div>
          )}
        </div>
      )}

      {/* KPIs & Requirements Tab */}
      {activeTab === "kpis" && analysis && (
        <div className="space-y-6">
          {/* KPIs */}
          <div className="border border-zinc-200 bg-white brutal-shadow p-6">
            <h3 className="font-bold font-['Chivo'] uppercase mb-4">KPIs & Metrics ({analysis.kpis?.length || 0})</h3>
            {analysis.kpis?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {analysis.kpis.map((kpi, i) => (
                  <div key={i} className="p-4 border border-zinc-200 hover:border-zinc-400 transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="outline">{kpi.category}</Badge>
                      <Badge className={`${kpi.priority === 'high' ? 'bg-[#FF4F00]' : kpi.priority === 'medium' ? 'bg-amber-500' : 'bg-zinc-400'} text-white border-none`}>{kpi.priority}</Badge>
                    </div>
                    <h4 className="font-bold">{kpi.name}</h4>
                    <p className="text-sm text-zinc-600 mt-1">{kpi.description}</p>
                    {kpi.target_value && <p className="text-xs text-[#0047AB] mt-2"><strong>Target:</strong> {kpi.target_value}</p>}
                    {kpi.measurement_criteria && <p className="text-xs text-zinc-500 mt-1"><strong>Measurement:</strong> {kpi.measurement_criteria}</p>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-zinc-500">No KPIs identified</p>
            )}
          </div>

          {/* Requirements */}
          <div className="border border-zinc-200 bg-white brutal-shadow p-6">
            <h3 className="font-bold font-['Chivo'] uppercase mb-4">Requirements & Obligations ({analysis.requirements?.length || 0})</h3>
            {analysis.requirements?.length > 0 ? (
              <div className="space-y-3">
                {analysis.requirements.map((req, i) => (
                  <div key={i} className="p-4 border border-zinc-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline">{req.category}</Badge>
                      <Badge className={`${req.responsible_party === 'contractor' ? 'bg-blue-500' : req.responsible_party === 'employer' ? 'bg-purple-500' : 'bg-zinc-500'} text-white border-none`}>{req.responsible_party}</Badge>
                      <Badge className={`${req.priority === 'high' ? 'bg-[#FF4F00]' : req.priority === 'medium' ? 'bg-amber-500' : 'bg-zinc-400'} text-white border-none`}>{req.priority}</Badge>
                    </div>
                    <h4 className="font-bold">{req.title}</h4>
                    <p className="text-sm text-zinc-600 mt-1">{req.description}</p>
                    {req.deadline && <p className="text-xs text-red-500 mt-2 flex items-center gap-1"><Calendar size={12} /> {req.deadline}</p>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-zinc-500">No requirements identified</p>
            )}
          </div>
        </div>
      )}

      {/* Impact Analysis Tab */}
      {activeTab === "impact" && analysis && (
        <div className="space-y-6">
          <div className="border-2 border-[#FF4F00] bg-orange-50 p-6">
            <h3 className="font-bold font-['Chivo'] uppercase mb-4 text-[#FF4F00] flex items-center gap-2"><AlertTriangle size={20} /> Impact Factors ({analysis.impact_factors?.length || 0})</h3>
            <p className="text-sm text-zinc-600 mb-4">These factors may significantly impact project execution. Review carefully during planning.</p>
          </div>

          {analysis.impact_factors?.length > 0 ? (
            <div className="space-y-4">
              {analysis.impact_factors.map((factor, i) => (
                <div key={i} className={`border-l-4 ${getRiskBgColor(factor.impact_level)} p-4`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline">{factor.category}</Badge>
                        <Badge className={`${factor.impact_level === 'critical' ? 'bg-red-600' : factor.impact_level === 'high' ? 'bg-red-500' : factor.impact_level === 'medium' ? 'bg-amber-500' : 'bg-emerald-500'} text-white border-none`}>{factor.impact_level} impact</Badge>
                        <Badge className={`${factor.probability === 'certain' ? 'bg-red-500' : factor.probability === 'likely' ? 'bg-amber-500' : 'bg-zinc-400'} text-white border-none`}>{factor.probability}</Badge>
                      </div>
                      <h4 className="font-bold text-lg">{factor.factor}</h4>
                      <p className="text-sm text-zinc-600 mt-2">{factor.description}</p>
                      {factor.mitigation_suggestions?.length > 0 && (
                        <div className="mt-3 p-3 bg-white border">
                          <p className="text-xs uppercase text-emerald-600 font-bold mb-2">Mitigation Suggestions:</p>
                          <ul className="space-y-1">
                            {factor.mitigation_suggestions.map((s, j) => (
                              <li key={j} className="flex items-start gap-2 text-sm">
                                <CheckCircle size={14} className="text-emerald-500 mt-0.5" />{s}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="border border-dashed border-zinc-300 p-12 text-center">
              <TrendingUp className="mx-auto mb-4 text-zinc-400" size={48} />
              <p className="text-zinc-500">No impact factors identified. Upload a contract first.</p>
            </div>
          )}
        </div>
      )}

      {/* Recommendations Tab */}
      {activeTab === "recommendations" && analysis && (
        <div className="space-y-6">
          {/* PM Recommendations */}
          <div className="border-2 border-[#0047AB] bg-blue-50 p-6">
            <h3 className="font-bold font-['Chivo'] uppercase mb-4 text-[#0047AB] flex items-center gap-2"><Users size={20} /> Project Manager Recommendations</h3>
            {analysis.pm_recommendations?.length > 0 ? (
              <ul className="space-y-3">
                {analysis.pm_recommendations.map((rec, i) => (
                  <li key={i} className="flex items-start gap-3 bg-white p-3">
                    <span className="bg-[#0047AB] text-white rounded-full w-6 h-6 flex items-center justify-center text-xs flex-shrink-0">{i + 1}</span>
                    <span className="text-sm">{rec}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-zinc-500">No recommendations available</p>
            )}
          </div>

          {/* Director Insights */}
          <div className="border-2 border-purple-500 bg-purple-50 p-6">
            <h3 className="font-bold font-['Chivo'] uppercase mb-4 text-purple-700 flex items-center gap-2"><Star size={20} /> Director Strategic Insights</h3>
            {analysis.director_insights?.length > 0 ? (
              <ul className="space-y-3">
                {analysis.director_insights.map((insight, i) => (
                  <li key={i} className="flex items-start gap-3 bg-white p-3">
                    <Star size={16} className="text-purple-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">{insight}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-zinc-500">No insights available</p>
            )}
          </div>

          {/* Action Items */}
          {analysis.action_items?.length > 0 && (
            <div className="border border-zinc-200 bg-white brutal-shadow p-6">
              <h3 className="font-bold font-['Chivo'] uppercase mb-4">Action Items</h3>
              <div className="space-y-3">
                {analysis.action_items.map((item, i) => (
                  <div key={i} className={`p-4 border-l-4 ${item.priority === 'immediate' ? 'border-red-500 bg-red-50' : item.priority === 'short-term' ? 'border-amber-500 bg-amber-50' : 'border-zinc-300 bg-zinc-50'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={`${item.priority === 'immediate' ? 'bg-red-500' : item.priority === 'short-term' ? 'bg-amber-500' : 'bg-zinc-500'} text-white border-none`}>{item.priority}</Badge>
                      <Badge variant="outline">{item.responsible}</Badge>
                    </div>
                    <p className="font-medium">{item.action}</p>
                    {item.deadline_suggestion && <p className="text-xs text-zinc-500 mt-1"><Clock size={12} className="inline mr-1" />{item.deadline_suggestion}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === "history" && (
        <div className="space-y-4">
          {!user ? (
            <div className="border border-dashed border-zinc-300 p-12 text-center">
              <User className="mx-auto mb-4 text-zinc-400" size={48} />
              <p className="text-zinc-500">Sign in to view your analysis history</p>
            </div>
          ) : history.length === 0 ? (
            <div className="border border-dashed border-zinc-300 p-12 text-center">
              <History className="mx-auto mb-4 text-zinc-400" size={48} />
              <p className="text-zinc-500">No analysis history yet</p>
            </div>
          ) : (
            <div className="border border-zinc-200 bg-white brutal-shadow overflow-hidden">
              <table className="brutal-table">
                <thead><tr><th>Document</th><th>Type</th><th>Contract Title</th><th>Risk Score</th><th>Date</th><th>Actions</th></tr></thead>
                <tbody>
                  {history.map(h => (
                    <tr key={h.id} className={selectedHistoryId === h.id ? 'bg-blue-50' : ''}>
                      <td className="font-medium">{h.filename}</td>
                      <td><Badge variant="outline">{h.file_type?.toUpperCase()}</Badge></td>
                      <td>{h.contract_title || 'N/A'}</td>
                      <td><span className={`font-bold ${getRiskColor(h.overall_risk_score)}`}>{h.overall_risk_score || 'N/A'}</span></td>
                      <td className="text-sm text-zinc-500">{new Date(h.upload_date).toLocaleDateString()}</td>
                      <td>
                        <Button onClick={() => loadFromHistory(h.id)} variant="outline" size="sm"><Eye size={14} className="mr-1" /> View</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* No Analysis State */}
      {activeTab !== "upload" && activeTab !== "history" && !analysis && (
        <div className="border border-dashed border-zinc-300 p-12 text-center">
          <Search className="mx-auto mb-4 text-zinc-400" size={48} />
          <p className="text-zinc-500">No analysis available. Upload a contract first or select from history.</p>
          <Button onClick={() => setActiveTab("upload")} className="mt-4 bg-black text-white">Upload Contract</Button>
        </div>
      )}
    </div>
  );
};

// Contract Generator with subclauses preview
const ContractGeneratorView = ({ onContractGenerated, clauses, setActiveView }) => {
  const { user } = useAuth();
  const { contractFormData, setContractFormData, resetContractForm } = useFormData();
  const [activeStep, setActiveStep] = useState(1);
  const [formData, setFormData] = useState(contractFormData);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [previewClauses, setPreviewClauses] = useState([]);
  const [selectedClauses, setSelectedClauses] = useState([]);
  const [userDefinedClauses, setUserDefinedClauses] = useState([]);
  const [showAddClause, setShowAddClause] = useState(false);
  const [newUserClause, setNewUserClause] = useState({ title: "", text: "", category: "general" });
  const [availableClauses, setAvailableClauses] = useState(null);
  const [clauseCategories, setClauseCategories] = useState([]);
  const [savedUserClauses, setSavedUserClauses] = useState([]);
  const [includeAllSubclauses, setIncludeAllSubclauses] = useState(true);
  const [keyDates, setKeyDates] = useState([]);
  const [showAddKeyDate, setShowAddKeyDate] = useState(false);
  const [newKeyDate, setNewKeyDate] = useState({ title: "", date: "", type: "milestone" });
  // New state for expanded config
  const [sectors, setSectors] = useState({});
  const [contractTypes, setContractTypes] = useState({});
  const [projectTypes, setProjectTypes] = useState({});
  const [frameworks, setFrameworks] = useState([]);
  const [frameworkClauses, setFrameworkClauses] = useState(null);
  const [expandedFwCategories, setExpandedFwCategories] = useState({});

  // Sync local form data with context
  useEffect(() => {
    setContractFormData(formData);
  }, [formData]);

  // Initialize from context on mount
  useEffect(() => {
    if (contractFormData.name || contractFormData.sector) {
      setFormData(contractFormData);
    }
  }, []);

  useEffect(() => {
    fetchAvailableClauses();
    fetchExpandedConfig();
    if (user) fetchSavedUserClauses();
  }, [user]);

  useEffect(() => {
    if (formData.framework) {
      const filtered = clauses.filter(c => c.frameworks?.includes(formData.framework));
      setPreviewClauses(filtered);
      // Fetch comprehensive framework clauses and auto-select ALL
      axios.get(`${API}/contracts/framework-clauses/${formData.framework}`)
        .then(res => {
          setFrameworkClauses(res.data);
          setExpandedFwCategories({});
          // Auto-select all framework clause IDs + library clause IDs
          const fwClauseIds = [];
          (res.data.categories || []).forEach(cat => {
            (cat.clauses || []).forEach(cl => fwClauseIds.push(cl.id));
          });
          const libraryIds = filtered.map(c => c.id);
          setSelectedClauses([...new Set([...libraryIds, ...fwClauseIds])]);
        })
        .catch(() => {
          setFrameworkClauses(null);
          setSelectedClauses(filtered.map(c => c.id));
        });
    }
  }, [formData.framework, clauses]);

  const handleReset = () => {
    setFormData({
      name: "", sector: "", subsector: "", framework: "", delivery_model: "", 
      risk_description: "", contract_value: "", duration_months: "", 
      employer_name: "", contractor_name: "", project_type: "",
      start_date: "", end_date: ""
    });
    resetContractForm();
    setActiveStep(1);
    setResult(null);
    setSelectedClauses([]);
    setUserDefinedClauses([]);
    setKeyDates([]);
    toast.success("Form reset successfully");
  };

  const fetchExpandedConfig = async () => {
    try {
      const [sectorsRes, typesRes, projectsRes, frameworksRes] = await Promise.all([
        axios.get(`${API}/config/sectors`),
        axios.get(`${API}/config/contract-types`),
        axios.get(`${API}/config/project-types`),
        axios.get(`${API}/config/frameworks`)
      ]);
      setSectors(sectorsRes.data.sectors || {});
      setContractTypes(typesRes.data.contract_types || {});
      setProjectTypes(projectsRes.data.project_types || {});
      setFrameworks(frameworksRes.data.frameworks || []);
    } catch (e) { console.log("Failed to fetch config"); }
  };

  const fetchAvailableClauses = async () => {
    try {
      const res = await axios.get(`${API}/contracts/available-clauses`);
      setAvailableClauses(res.data.library_clauses);
      setClauseCategories(res.data.clause_categories || []);
    } catch (e) { console.log("Failed to fetch clauses"); }
  };

  const fetchSavedUserClauses = async () => {
    try {
      const res = await axios.get(`${API}/user-clauses`);
      setSavedUserClauses(res.data);
    } catch (e) { console.log("Failed to fetch user clauses"); }
  };

  const getSubsectors = () => {
    if (!formData.sector || !sectors[formData.sector]) return [];
    return sectors[formData.sector].subsectors || [];
  };

  const toggleClause = (clauseId) => {
    setSelectedClauses(prev => 
      prev.includes(clauseId) 
        ? prev.filter(id => id !== clauseId) 
        : [...prev, clauseId]
    );
  };

  const selectAllInCategory = (category) => {
    const categoryClauseIds = availableClauses?.[category]?.map(c => c.id) || [];
    setSelectedClauses(prev => [...new Set([...prev, ...categoryClauseIds])]);
  };

  const deselectAllInCategory = (category) => {
    const categoryClauseIds = availableClauses?.[category]?.map(c => c.id) || [];
    setSelectedClauses(prev => prev.filter(id => !categoryClauseIds.includes(id)));
  };

  const addUserDefinedClause = () => {
    if (!newUserClause.title || !newUserClause.text) {
      toast.error("Please fill in title and text");
      return;
    }
    setUserDefinedClauses(prev => [...prev, { ...newUserClause, id: `user_${Date.now()}` }]);
    setNewUserClause({ title: "", text: "", category: "general" });
    setShowAddClause(false);
    toast.success("User-defined clause added");
  };

  const removeUserDefinedClause = (id) => {
    setUserDefinedClauses(prev => prev.filter(c => c.id !== id));
  };

  const addSavedClauseToContract = (clause) => {
    const newClause = {
      id: `saved_${clause.id}`,
      title: clause.title,
      text: clause.text,
      category: clause.category
    };
    if (!userDefinedClauses.find(c => c.title === clause.title)) {
      setUserDefinedClauses(prev => [...prev, newClause]);
      toast.success("Saved clause added");
    } else {
      toast.info("Clause already added");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.sector || !formData.framework || !formData.delivery_model) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (selectedClauses.length === 0 && userDefinedClauses.length === 0) {
      toast.error("Please select at least one clause");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        sector: formData.sector,
        framework: formData.framework,
        delivery_model: formData.delivery_model,
        risk_description: formData.risk_description,
        contract_value: formData.contract_value ? parseFloat(formData.contract_value) : null,
        duration_months: formData.duration_months ? parseInt(formData.duration_months) : null,
        employer_name: formData.employer_name || null,
        contractor_name: formData.contractor_name || null,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        selected_clauses: selectedClauses,
        user_defined_clauses: userDefinedClauses.map(c => ({
          title: c.title,
          text: c.text,
          category: c.category,
          position: "end"
        })),
        include_all_subclauses: includeAllSubclauses,
        key_dates_milestones: keyDates.map(d => ({
          title: d.title,
          date: d.date,
          type: d.type
        }))
      };
      const response = await axios.post(`${API}/contracts/generate-enhanced`, payload, { timeout: 180000 });
      setResult(response.data);
      onContractGenerated();
      toast.success("Contract generated! AI is drafting detailed clause narratives in background...");
      setActiveStep(5);
      
      // Start polling for narrative completion
      if (response.data.contract_id) {
        const pollNarrative = async () => {
          let attempts = 0;
          const maxAttempts = 60; // poll for up to 5 minutes
          const poll = setInterval(async () => {
            attempts++;
            try {
              const statusRes = await axios.get(`${API}/contracts/${response.data.contract_id}/narrative-status`);
              if (statusRes.data.status === 'complete') {
                clearInterval(poll);
                setResult(prev => ({
                  ...prev,
                  filename: statusRes.data.filename,
                  download_url: statusRes.data.download_url,
                  narrative_status: 'complete'
                }));
                toast.success("Detailed AI narratives ready! Download now includes comprehensive contractual language.");
              } else if (statusRes.data.status === 'failed' || attempts >= maxAttempts) {
                clearInterval(poll);
              }
            } catch { clearInterval(poll); }
          }, 5000);
        };
        pollNarrative();
      }
    } catch (error) {
      const msg = getErrorMessage(error, "Failed to generate contract. Please try again.");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { num: 1, label: "Project Details" },
    { num: 2, label: "Select Clauses" },
    { num: 3, label: "User Clauses & Key Dates" },
    { num: 4, label: "Review Contract" },
    { num: 5, label: "Generate" }
  ];

  return (
    <div className="space-y-6" data-testid="generator-view" data-export-region>
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight uppercase font-['Chivo'] mb-2">Contract Generator</h1>
          <p className="text-zinc-500">Create detailed contracts with all clauses, sub-clauses, and user-defined provisions</p>
        </div>
        <div className="flex gap-2 items-center">
          <ExportToolbar title="Contract Generator" pdfFilename="contract-generator" />
          <Button onClick={handleReset} variant="outline" className="border-2 border-zinc-300 hover:border-red-500 hover:bg-red-50 text-zinc-600 hover:text-red-600" data-testid="btn-reset-form">
            <RefreshCw size={16} className="mr-2" /> Reset Form
          </Button>
        </div>
      </div>

      <InfoRibbon
        title="How to Generate a Comprehensive Contract"
        color="blue"
        steps={[
          "Enter project details including parties, value, and duration",
          "Select relevant clauses from the library (organized by category)",
          "Add your own user-defined clauses for project-specific requirements",
          "Review and generate the contract with all sub-clauses included"
        ]}
        tips={[
          "Use the clause categories to ensure comprehensive coverage",
          "Add user-defined clauses for special project conditions",
          "Save frequently used clauses to your library for future use"
        ]}
      />

      {/* Step Indicator */}
      <div className="border border-zinc-200 bg-white brutal-shadow p-4">
        <div className="flex items-center justify-between">
          {steps.map((step, i) => (
            <React.Fragment key={step.num}>
              <button 
                onClick={() => setActiveStep(step.num)} 
                className={`flex items-center gap-2 px-4 py-2 ${activeStep === step.num ? 'bg-black text-white' : activeStep > step.num ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-500'}`}
              >
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${activeStep === step.num ? 'bg-white text-black' : activeStep > step.num ? 'bg-emerald-500 text-white' : 'bg-zinc-300 text-zinc-600'}`}>
                  {activeStep > step.num ? <Check size={14} /> : step.num}
                </span>
                <span className="text-sm font-medium hidden md:block">{step.label}</span>
              </button>
              {i < steps.length - 1 && <div className="flex-1 h-0.5 bg-zinc-200 mx-2" />}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step 1: Project Details */}
      {activeStep === 1 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="border border-zinc-200 bg-white brutal-shadow p-6">
            <h2 className="font-bold font-['Chivo'] uppercase tracking-wide mb-6 pb-4 border-b border-zinc-200">Project Details</h2>
            <div className="space-y-5">
              <div>
                <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2 flex items-center">Project Name * <InfoTip text="Enter the official project name as it will appear on the contract document." /></label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="underline-input w-full text-lg" data-testid="input-project-name" placeholder="Enter project name" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2 flex items-center">Sector * <InfoTip text="Select the industry sector. This determines applicable risks, clauses, and benchmarks for your contract." /></label>
                  <Select value={formData.sector} onValueChange={(v) => setFormData({...formData, sector: v, subsector: ""})}>
                    <SelectTrigger className="border-0 border-b-2 border-zinc-200 rounded-none" data-testid="select-sector"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(sectors).map(([key, value]) => (
                        <SelectItem key={key} value={key}>{value.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2 flex items-center">Sub-sector <InfoTip text="Narrow down the project type within the selected sector for more tailored contract provisions." /></label>
                  <Select value={formData.subsector} onValueChange={(v) => setFormData({...formData, subsector: v})} disabled={!formData.sector}>
                    <SelectTrigger className="border-0 border-b-2 border-zinc-200 rounded-none"><SelectValue placeholder={formData.sector ? "Select sub-sector" : "Select sector first"} /></SelectTrigger>
                    <SelectContent>
                      {getSubsectors().map((sub) => (
                        <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2 flex items-center">Framework * <InfoTip text="The contractual framework (e.g., FIDIC, NEC, JCT) determines the standard clause structure and risk allocation." /></label>
                  <Select value={formData.framework} onValueChange={(v) => setFormData({...formData, framework: v})}>
                    <SelectTrigger className="border-0 border-b-2 border-zinc-200 rounded-none" data-testid="select-framework"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {frameworks.map((fw) => (
                        <SelectItem key={fw.id} value={fw.id}>{fw.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2 flex items-center">Project Type <InfoTip text="The nature of the construction: greenfield (new), brownfield (redevelopment), renovation, expansion, etc." /></label>
                  <Select value={formData.project_type} onValueChange={(v) => setFormData({...formData, project_type: v})}>
                    <SelectTrigger className="border-0 border-b-2 border-zinc-200 rounded-none"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(projectTypes).map(([key, desc]) => (
                        <SelectItem key={key} value={key}>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2 flex items-center">Contract/Delivery Model * <InfoTip text="How the project is delivered: Traditional (DBB), Design & Build, EPC, PPP, Alliance, etc. This affects risk allocation." /></label>
                <Select value={formData.delivery_model} onValueChange={(v) => setFormData({...formData, delivery_model: v})}>
                  <SelectTrigger className="border-0 border-b-2 border-zinc-200 rounded-none" data-testid="select-delivery"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(contractTypes).map(([key, value]) => (
                      <SelectItem key={key} value={key}>{value.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.delivery_model && contractTypes[formData.delivery_model] && (
                  <p className="text-xs text-zinc-500 mt-1">{contractTypes[formData.delivery_model].description}</p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2 flex items-center">Contract Value ($) <InfoTip text="Total contract value in USD. Use StructPro Cost Estimator below for estimation assistance." /></label>
                  <input type="number" value={formData.contract_value} onChange={(e) => setFormData({...formData, contract_value: e.target.value})} className="underline-input w-full" placeholder="e.g., 10000000" data-testid="input-contract-value" />
                  <button type="button" onClick={() => setActiveView("structpro")} className="text-xs text-[#0047AB] hover:underline mt-1.5 flex items-center gap-1 font-medium">
                    <Calculator size={11} /> Use StructPro Cost Estimator
                  </button>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2 flex items-center">Duration (Months) <InfoTip text="Expected project duration from commencement to completion, in months." /></label>
                  <input type="number" value={formData.duration_months} onChange={(e) => setFormData({...formData, duration_months: e.target.value})} className="underline-input w-full" placeholder="e.g., 24" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2 flex items-center">Start Date <InfoTip text="Official contract commencement date. This will appear on the contract document." /></label>
                  <input type="date" value={formData.start_date} onChange={(e) => setFormData({...formData, start_date: e.target.value})} className="underline-input w-full" data-testid="input-start-date" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2 flex items-center">Expected Completion Date <InfoTip text="Target date for project completion. Used in contract terms and risk analysis." /></label>
                  <input type="date" value={formData.end_date} onChange={(e) => setFormData({...formData, end_date: e.target.value})} className="underline-input w-full" data-testid="input-end-date" />
                </div>
              </div>
            </div>
          </div>

          <div className="border border-zinc-200 bg-white brutal-shadow p-6">
            <h2 className="font-bold font-['Chivo'] uppercase tracking-wide mb-6 pb-4 border-b border-zinc-200 flex items-center gap-1">Parties & Risk <InfoTip text="Enter the contracting parties and describe known risks to generate appropriate risk mitigation clauses." /></h2>
            <div className="space-y-5">
              <div>
                <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2">Employer Name</label>
                <input type="text" value={formData.employer_name} onChange={(e) => setFormData({...formData, employer_name: e.target.value})} className="underline-input w-full" placeholder="Name of the Employer/Client" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2">Contractor Name</label>
                <input type="text" value={formData.contractor_name} onChange={(e) => setFormData({...formData, contractor_name: e.target.value})} className="underline-input w-full" placeholder="Name of the Contractor" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2 flex items-center">Risk Description <InfoTip text="Describe known risks, site conditions, geotechnical concerns, regulatory issues, or any constraints the AI should consider when recommending clauses." /></label>
                <textarea value={formData.risk_description} onChange={(e) => setFormData({...formData, risk_description: e.target.value})} className="underline-input w-full min-h-[120px] resize-none" placeholder="Describe known risks, site conditions, constraints..." />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Select Clauses */}
      {activeStep === 2 && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-bold font-['Chivo'] uppercase">Select Contract Clauses</h2>
              <p className="text-sm text-zinc-500">{selectedClauses.length} clauses selected {formData.framework && `for ${formData.framework}`}</p>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={includeAllSubclauses} onChange={(e) => setIncludeAllSubclauses(e.target.checked)} className="w-4 h-4" />
                <span className="text-sm">Include all sub-clauses</span>
              </label>
            </div>
          </div>

          {/* Framework-Specific Standard Clauses */}
          {frameworkClauses && (
            <div className="border-2 border-[#0047AB] bg-white">
              <div className="p-4 bg-[#0047AB] text-white flex items-center justify-between">
                <div>
                  <h3 className="font-bold font-['Chivo'] uppercase text-sm">{frameworkClauses.name} - Standard Clauses</h3>
                  <p className="text-xs text-blue-200 mt-0.5">All clauses auto-selected for your framework. Deselect any you don't need.</p>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className="bg-white text-[#0047AB] border-none">{selectedClauses.filter(id => frameworkClauses.categories?.some(cat => cat.clauses.some(c => c.id === id))).length}/{frameworkClauses.categories?.reduce((s, c) => s + c.clauses.length, 0)} selected</Badge>
                  <button onClick={() => {
                    const allFwIds = [];
                    frameworkClauses.categories?.forEach(cat => cat.clauses.forEach(c => allFwIds.push(c.id)));
                    const currentFwSelected = selectedClauses.filter(id => allFwIds.includes(id));
                    if (currentFwSelected.length === allFwIds.length) {
                      // Deselect all framework clauses
                      setSelectedClauses(prev => prev.filter(id => !allFwIds.includes(id)));
                    } else {
                      // Select all framework clauses
                      setSelectedClauses(prev => [...new Set([...prev, ...allFwIds])]);
                    }
                  }} className="text-xs bg-white/20 hover:bg-white/30 px-3 py-1 rounded" data-testid="fw-toggle-all-btn">
                    {(() => {
                      const allFwIds = [];
                      frameworkClauses.categories?.forEach(cat => cat.clauses.forEach(c => allFwIds.push(c.id)));
                      return selectedClauses.filter(id => allFwIds.includes(id)).length === allFwIds.length ? 'Deselect All' : 'Select All';
                    })()}
                  </button>
                </div>
              </div>
              <div className="divide-y divide-zinc-200 max-h-[500px] overflow-y-auto">
                {frameworkClauses.categories?.map((cat) => (
                  <div key={cat.id}>
                    <button
                      onClick={() => setExpandedFwCategories(prev => ({...prev, [cat.id]: !prev[cat.id]}))}
                      className="w-full flex items-center justify-between p-3 hover:bg-zinc-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <ChevronDown size={14} className={`transition-transform ${expandedFwCategories[cat.id] ? '' : '-rotate-90'}`} />
                        <span className="font-bold text-sm">{cat.name}</span>
                        <Badge variant="outline" className="text-xs">{cat.clauses.length}</Badge>
                        {(() => { const ci = getClauseImpact(null, cat.name); return (
                          <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${ci.color === 'red' ? 'bg-red-100 text-red-700' : ci.color === 'amber' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>{ci.risk}</span>
                        ); })()}
                      </div>
                      <div className="flex gap-1">
                        <button onClick={(e) => { e.stopPropagation(); const ids = cat.clauses.map(c => c.id); setSelectedClauses(prev => [...new Set([...prev, ...ids])]); }} className="text-xs text-[#0047AB] hover:underline px-2">All</button>
                        <button onClick={(e) => { e.stopPropagation(); const ids = cat.clauses.map(c => c.id); setSelectedClauses(prev => prev.filter(id => !ids.includes(id))); }} className="text-xs text-zinc-400 hover:underline px-2">None</button>
                      </div>
                    </button>
                    {expandedFwCategories[cat.id] && (
                      <div className="px-3 pb-3 space-y-1">
                        {cat.clauses.map(clause => {
                          const impact = getClauseImpact(clause.id, cat.name);
                          return (
                          <div key={clause.id} className="group relative">
                            <label className={`flex items-start gap-2 p-2 cursor-pointer hover:bg-zinc-50 ${selectedClauses.includes(clause.id) ? 'bg-blue-50 border border-[#0047AB]' : 'border border-transparent'}`}>
                              <input type="checkbox" checked={selectedClauses.includes(clause.id)} onChange={() => toggleClause(clause.id)} className="mt-0.5" />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-mono text-zinc-400 flex-shrink-0">{clause.num}</span>
                                  <span className="text-sm font-medium">{clause.title}</span>
                                  <span className={`ml-auto flex-shrink-0 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${impact.color === 'red' ? 'bg-red-100 text-red-700' : impact.color === 'amber' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                    {impact.risk} Risk
                                  </span>
                                </div>
                                <p className="text-xs text-zinc-500 mt-0.5">{clause.desc}</p>
                              </div>
                            </label>
                            {/* Clause Impact Preview Tooltip */}
                            <div className="hidden group-hover:block absolute z-50 right-0 top-full mt-1 w-80 bg-white border-2 border-zinc-900 shadow-[4px_4px_0_rgba(0,0,0,0.9)] p-3 pointer-events-none" data-testid={`clause-impact-${clause.id}`}>
                              <div className="text-[10px] font-bold uppercase text-zinc-400 mb-1 tracking-wider">Impact Preview</div>
                              <div className="flex gap-3 mb-2">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-[10px] text-zinc-500 uppercase">Risk:</span>
                                  <span className={`text-xs font-bold ${impact.color === 'red' ? 'text-red-600' : impact.color === 'amber' ? 'text-amber-600' : 'text-emerald-600'}`}>{impact.risk}</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <span className="text-[10px] text-zinc-500 uppercase">Cost:</span>
                                  <span className={`text-xs font-bold ${impact.color === 'red' ? 'text-red-600' : impact.color === 'amber' ? 'text-amber-600' : 'text-emerald-600'}`}>{impact.cost}</span>
                                </div>
                              </div>
                              <p className="text-xs text-zinc-600 leading-relaxed">{impact.summary}</p>
                            </div>
                            {/* Sub-clauses */}
                            {clause.subs && includeAllSubclauses && selectedClauses.includes(clause.id) && (
                              <div className="ml-8 mt-1 space-y-1">
                                {clause.subs.map(sub => (
                                  <div key={sub.num} className="p-2 bg-zinc-50 border-l-2 border-[#0047AB]">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs font-mono text-zinc-400">{sub.num}</span>
                                      <span className="text-xs font-medium">{sub.title}</span>
                                    </div>
                                    {sub.text && <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{sub.text}</p>}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Existing Clause Library by Category */}
          <div>
            <h3 className="font-bold font-['Chivo'] uppercase text-sm mb-3">Additional Library Clauses</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {availableClauses && Object.entries(availableClauses).map(([category, clauseList]) => (
                <div key={category} className="border border-zinc-200 bg-white brutal-shadow">
                  <div className="p-3 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
                    <h3 className="font-bold text-sm uppercase">{category.replace('_', ' ')}</h3>
                    <div className="flex gap-1">
                      <button onClick={() => selectAllInCategory(category)} className="text-xs text-[#0047AB] hover:underline">All</button>
                      <span className="text-zinc-300">|</span>
                      <button onClick={() => deselectAllInCategory(category)} className="text-xs text-zinc-500 hover:underline">None</button>
                    </div>
                  </div>
                  <div className="p-3 max-h-[200px] overflow-y-auto space-y-2">
                  {clauseList.map(clause => (
                    <label key={clause.id} className={`flex items-start gap-2 p-2 cursor-pointer hover:bg-zinc-50 ${selectedClauses.includes(clause.id) ? 'bg-blue-50 border border-[#0047AB]' : 'border border-transparent'}`}>
                      <input type="checkbox" checked={selectedClauses.includes(clause.id)} onChange={() => toggleClause(clause.id)} className="mt-0.5" />
                      <div className="flex-1">
                        <div className="flex items-center gap-1">
                          <span className="text-sm font-medium">{clause.title}</span>
                          <Badge className={`text-xs ${clause.risk_level === 'high' ? 'bg-red-500' : clause.risk_level === 'medium' ? 'bg-amber-500' : 'bg-emerald-500'} text-white border-none`}>{clause.risk_level}</Badge>
                        </div>
                        <p className="text-xs text-zinc-500 mt-1">{clause.subclauses_count} sub-clauses</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
          </div>

          {/* Clause Categories Reference */}
          {clauseCategories.length > 0 && (
            <div className="border-2 border-[#0047AB] bg-blue-50 p-6">
              <h3 className="font-bold font-['Chivo'] uppercase mb-4 text-[#0047AB]">Standard Clause Categories ({clauseCategories.length})</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {clauseCategories.slice(0, 15).map(cat => (
                  <div key={cat.id} className="p-3 bg-white text-center">
                    <p className="font-medium text-sm">{cat.name}</p>
                    <p className="text-xs text-zinc-500">{cat.clauses?.length || 0} clauses</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 3: User-Defined Clauses */}
      {activeStep === 3 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-6">
            <div className="border border-zinc-200 bg-white brutal-shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-bold font-['Chivo'] uppercase">User-Defined Clauses ({userDefinedClauses.length})</h2>
                <Button onClick={() => setShowAddClause(true)} className="bg-black text-white"><Plus size={16} className="mr-2" /> Add Clause</Button>
              </div>
              
              {userDefinedClauses.length === 0 ? (
                <div className="border border-dashed border-zinc-300 p-8 text-center">
                  <FileText className="mx-auto mb-4 text-zinc-400" size={40} />
                  <p className="text-zinc-500">No user-defined clauses added yet</p>
                  <p className="text-xs text-zinc-400 mt-1">Add custom clauses for project-specific requirements</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {userDefinedClauses.map((clause, i) => (
                    <div key={clause.id} className="p-4 border border-zinc-200 bg-zinc-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline">{clause.category}</Badge>
                            <span className="font-medium">{clause.title}</span>
                          </div>
                          <p className="text-sm text-zinc-600">{clause.text.substring(0, 150)}{clause.text.length > 150 ? '...' : ''}</p>
                        </div>
                        <button onClick={() => removeUserDefinedClause(clause.id)} className="text-red-500 hover:text-red-700 p-1"><Trash2 size={16} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add Clause Modal */}
            {showAddClause && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowAddClause(false)}>
                <div className="bg-white border-2 border-black brutal-shadow-xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
                  <div className="p-4 border-b border-zinc-200 flex justify-between items-center">
                    <h3 className="font-bold font-['Chivo'] uppercase">Add User-Defined Clause</h3>
                    <button onClick={() => setShowAddClause(false)}><X size={20} /></button>
                  </div>
                  <div className="p-6 space-y-4">
                    <input type="text" value={newUserClause.title} onChange={(e) => setNewUserClause({...newUserClause, title: e.target.value})} placeholder="Clause Title *" className="underline-input w-full" />
                    <Select value={newUserClause.category} onValueChange={(v) => setNewUserClause({...newUserClause, category: v})}>
                      <SelectTrigger><SelectValue placeholder="Category" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General</SelectItem>
                        <SelectItem value="payment">Payment</SelectItem>
                        <SelectItem value="time">Time</SelectItem>
                        <SelectItem value="variations">Variations</SelectItem>
                        <SelectItem value="risk">Risk</SelectItem>
                        <SelectItem value="dispute">Dispute</SelectItem>
                        <SelectItem value="termination">Termination</SelectItem>
                        <SelectItem value="quality">Quality</SelectItem>
                        <SelectItem value="insurance">Insurance</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <textarea value={newUserClause.text} onChange={(e) => setNewUserClause({...newUserClause, text: e.target.value})} placeholder="Clause Text *" className="underline-input w-full min-h-[150px]" />
                    <Button onClick={addUserDefinedClause} className="w-full bg-black text-white">Add Clause</Button>
                  </div>
                </div>
              </div>
            )}

            {/* Key Dates & Milestones Section */}
            <div className="border border-zinc-200 bg-white brutal-shadow p-6" data-testid="key-dates-section">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-1">
                  <h2 className="font-bold font-['Chivo'] uppercase">Key Dates & Milestones ({keyDates.length})</h2>
                  <InfoTip text="Add important project dates and milestones that will be included in the contract document. These appear in the Key Dates section of the generated contract." />
                </div>
                <Button onClick={() => setShowAddKeyDate(true)} className="bg-[#0047AB] text-white" data-testid="btn-add-key-date"><Plus size={16} className="mr-2" /> Add Date</Button>
              </div>

              {keyDates.length === 0 ? (
                <div className="border border-dashed border-zinc-300 p-6 text-center">
                  <Calendar className="mx-auto mb-3 text-zinc-400" size={36} />
                  <p className="text-zinc-500 text-sm">No key dates or milestones added yet</p>
                  <p className="text-xs text-zinc-400 mt-1">Add contractual deadlines, milestones, or critical dates to include in the contract document</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {keyDates.map((kd, i) => (
                    <div key={i} className="p-3 border border-zinc-200 bg-zinc-50 flex items-center justify-between" data-testid={`key-date-item-${i}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 flex items-center justify-center text-white text-xs font-bold ${kd.type === 'milestone' ? 'bg-[#0047AB]' : kd.type === 'deadline' ? 'bg-[#FF4F00]' : 'bg-emerald-500'}`}>
                          {kd.type === 'milestone' ? <Flag size={14} /> : kd.type === 'deadline' ? <AlertTriangle size={14} /> : <Calendar size={14} />}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{kd.title}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-zinc-500">{kd.date}</span>
                            <Badge variant="outline" className="text-[10px]">{kd.type}</Badge>
                          </div>
                        </div>
                      </div>
                      <button onClick={() => setKeyDates(prev => prev.filter((_, idx) => idx !== i))} className="text-red-500 hover:text-red-700 p-1" data-testid={`btn-remove-key-date-${i}`}><Trash2 size={16} /></button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Key Date Inline Form */}
              {showAddKeyDate && (
                <div className="mt-4 p-4 border-2 border-[#0047AB] bg-blue-50" data-testid="add-key-date-form">
                  <h4 className="font-bold text-sm uppercase mb-3">New Key Date / Milestone</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Title *</label>
                      <input type="text" value={newKeyDate.title} onChange={(e) => setNewKeyDate({...newKeyDate, title: e.target.value})} className="w-full border-2 border-zinc-200 p-2 text-sm" placeholder="e.g., Foundation Complete" data-testid="key-date-title-input" />
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Date *</label>
                      <input type="date" value={newKeyDate.date} onChange={(e) => setNewKeyDate({...newKeyDate, date: e.target.value})} className="w-full border-2 border-zinc-200 p-2 text-sm" data-testid="key-date-date-input" />
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase text-zinc-500 block mb-1 flex items-center">Type <InfoTip text="Milestone: key achievement point. Deadline: contractual due date. Key Date: other important date." /></label>
                      <select value={newKeyDate.type} onChange={(e) => setNewKeyDate({...newKeyDate, type: e.target.value})} className="w-full border-2 border-zinc-200 p-2 text-sm" data-testid="key-date-type-select">
                        <option value="milestone">Milestone</option>
                        <option value="deadline">Deadline</option>
                        <option value="key_date">Key Date</option>
                        <option value="contractual">Contractual</option>
                        <option value="completion">Completion</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button onClick={() => {
                      if (!newKeyDate.title || !newKeyDate.date) { toast.error("Title and date are required"); return; }
                      setKeyDates(prev => [...prev, { ...newKeyDate }]);
                      setNewKeyDate({ title: "", date: "", type: "milestone" });
                      setShowAddKeyDate(false);
                      toast.success("Key date added");
                    }} className="bg-[#0047AB] text-white" data-testid="btn-save-key-date">Add Key Date</Button>
                    <Button onClick={() => setShowAddKeyDate(false)} variant="outline" className="border-2">Cancel</Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-6">
            {/* Saved User Clauses */}
            {savedUserClauses.length > 0 && (
              <div className="border border-zinc-200 bg-white brutal-shadow p-6">
                <h3 className="font-bold font-['Chivo'] uppercase mb-4">Your Saved Clauses ({savedUserClauses.length})</h3>
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {savedUserClauses.map(clause => (
                    <div key={clause.id} className="p-3 border border-zinc-200 hover:border-zinc-400 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{clause.title}</p>
                        <p className="text-xs text-zinc-500">{clause.category}</p>
                      </div>
                      <Button onClick={() => addSavedClauseToContract(clause)} variant="outline" size="sm"><Plus size={14} /></Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Summary */}
            <div className="border-2 border-emerald-500 bg-emerald-50 p-6">
              <h3 className="font-bold font-['Chivo'] uppercase mb-4 text-emerald-700">Contract Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span>Library Clauses:</span><span className="font-bold">{selectedClauses.length}</span></div>
                <div className="flex justify-between"><span>User-Defined Clauses:</span><span className="font-bold">{userDefinedClauses.length}</span></div>
                <div className="flex justify-between"><span>Key Dates/Milestones:</span><span className="font-bold">{keyDates.length}</span></div>
                <div className="flex justify-between border-t border-emerald-300 pt-2 mt-2"><span>Total Clauses:</span><span className="font-bold text-lg">{selectedClauses.length + userDefinedClauses.length}</span></div>
                <div className="flex justify-between"><span>Include Sub-Clauses:</span><span className="font-bold">{includeAllSubclauses ? 'Yes' : 'No'}</span></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 4: Review Contract */}
      {activeStep === 4 && (
        <div className="space-y-6">
          <div className="border-2 border-[#0047AB] bg-blue-50 p-4 flex items-center gap-3">
            <Eye size={20} className="text-[#0047AB]" />
            <div>
              <p className="font-bold text-[#0047AB] text-sm">Review your contract before generating</p>
              <p className="text-xs text-zinc-600">Verify all details, clauses, and provisions below. Go back to make changes.</p>
            </div>
          </div>

          {/* Project Details Review */}
          <div className="border border-zinc-200 bg-white brutal-shadow p-6">
            <h3 className="font-bold font-['Chivo'] uppercase tracking-wide mb-4 pb-3 border-b border-zinc-200">Project Details</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { l: "Project Name", v: formData.name },
                { l: "Sector", v: formData.sector },
                { l: "Sub-sector", v: formData.subsector || "-" },
                { l: "Framework", v: formData.framework },
                { l: "Delivery Model", v: formData.delivery_model },
                { l: "Project Type", v: formData.project_type || "-" },
                { l: "Contract Value", v: formData.contract_value ? `$${parseInt(formData.contract_value).toLocaleString()}` : "-" },
                { l: "Duration", v: formData.duration_months ? `${formData.duration_months} months` : "-" },
                { l: "Start Date", v: formData.start_date || "-" },
                { l: "End Date", v: formData.end_date || "-" },
              ].map((item, i) => (
                <div key={i} className="p-3 bg-zinc-50">
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{item.l}</p>
                  <p className="font-bold text-sm">{item.v || "-"}</p>
                </div>
              ))}
            </div>
            {(formData.employer_name || formData.contractor_name) && (
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="p-3 bg-zinc-50"><p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Employer</p><p className="font-bold text-sm">{formData.employer_name || "-"}</p></div>
                <div className="p-3 bg-zinc-50"><p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Contractor</p><p className="font-bold text-sm">{formData.contractor_name || "-"}</p></div>
              </div>
            )}
            {formData.risk_description && (
              <div className="mt-4 p-3 bg-amber-50 border-l-4 border-amber-500">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Risk Description</p>
                <p className="text-sm">{formData.risk_description}</p>
              </div>
            )}
          </div>

          {/* Standard Framework Clauses */}
          <div className="border border-zinc-200 bg-white brutal-shadow p-6">
            <h3 className="font-bold font-['Chivo'] uppercase tracking-wide mb-4 pb-3 border-b border-zinc-200 flex items-center gap-2">
              <Shield size={18} className="text-[#0047AB]" /> Standard {formData.framework || ""} Clauses
              <Badge className="bg-[#0047AB] text-white border-none ml-2">{previewClauses.length} clauses</Badge>
            </h3>
            <p className="text-xs text-zinc-500 mb-4">These standard clauses from the {formData.framework} framework will be included automatically.</p>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {previewClauses.map((clause, i) => (
                <div key={clause.id} className={`p-3 border border-zinc-200 ${selectedClauses.includes(clause.id) ? 'bg-blue-50 border-[#0047AB]' : 'bg-zinc-50 opacity-60'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-zinc-400">{i + 1}.</span>
                      <span className="font-medium text-sm">{clause.title}</span>
                      <Badge className={`text-xs ${clause.risk_level === 'high' ? 'bg-red-500' : clause.risk_level === 'medium' ? 'bg-amber-500' : 'bg-emerald-500'} text-white border-none`}>{clause.risk_level}</Badge>
                    </div>
                    <span className={`text-xs ${selectedClauses.includes(clause.id) ? 'text-[#0047AB] font-bold' : 'text-zinc-400'}`}>
                      {selectedClauses.includes(clause.id) ? 'INCLUDED' : 'EXCLUDED'}
                    </span>
                  </div>
                  <p className="text-xs text-zinc-500 mt-1 ml-5">{clause.text?.substring(0, 120)}...</p>
                  {clause.subclauses?.length > 0 && <p className="text-xs text-[#0047AB] mt-1 ml-5">{clause.subclauses.length} sub-clauses {includeAllSubclauses ? 'included' : 'not included'}</p>}
                </div>
              ))}
            </div>
          </div>

          {/* Selected Library Clauses (non-standard) */}
          {selectedClauses.filter(id => !previewClauses.find(p => p.id === id)).length > 0 && (
            <div className="border border-zinc-200 bg-white brutal-shadow p-6">
              <h3 className="font-bold font-['Chivo'] uppercase tracking-wide mb-4 pb-3 border-b border-zinc-200 flex items-center gap-2">
                <FileText size={18} className="text-emerald-600" /> Additional Library Clauses
                <Badge className="bg-emerald-500 text-white border-none ml-2">
                  {selectedClauses.filter(id => !previewClauses.find(p => p.id === id)).length}
                </Badge>
              </h3>
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {selectedClauses.filter(id => !previewClauses.find(p => p.id === id)).map((clauseId, i) => {
                  const clauseInfo = availableClauses ? Object.values(availableClauses).flat().find(c => c.id === clauseId) : null;
                  return (
                    <div key={clauseId} className="p-3 bg-emerald-50 border border-emerald-200">
                      <span className="font-medium text-sm">{clauseInfo?.title || clauseId}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* User-Defined Clauses */}
          {userDefinedClauses.length > 0 && (
            <div className="border border-zinc-200 bg-white brutal-shadow p-6">
              <h3 className="font-bold font-['Chivo'] uppercase tracking-wide mb-4 pb-3 border-b border-zinc-200 flex items-center gap-2">
                <Pencil size={18} className="text-[#FF4F00]" /> User-Defined Clauses
                <Badge className="bg-[#FF4F00] text-white border-none ml-2">{userDefinedClauses.length}</Badge>
              </h3>
              <div className="space-y-3">
                {userDefinedClauses.map((clause, i) => (
                  <div key={clause.id} className="p-4 border border-orange-200 bg-orange-50">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline" className="text-xs">{clause.category}</Badge>
                      <span className="font-bold text-sm">{clause.title}</span>
                    </div>
                    <p className="text-sm text-zinc-600 leading-relaxed">{clause.text}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Key Dates & Milestones Review */}
          {keyDates.length > 0 && (
            <div className="border border-zinc-200 bg-white brutal-shadow p-6" data-testid="review-key-dates">
              <h3 className="font-bold font-['Chivo'] uppercase tracking-wide mb-4 pb-3 border-b border-zinc-200 flex items-center gap-2">
                <Calendar size={18} className="text-[#0047AB]" /> Key Dates & Milestones
                <Badge className="bg-[#0047AB] text-white border-none ml-2">{keyDates.length}</Badge>
              </h3>
              <div className="space-y-2">
                {keyDates.map((kd, i) => (
                  <div key={i} className="p-3 border border-zinc-200 bg-blue-50 flex items-center gap-3">
                    <div className={`w-8 h-8 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${kd.type === 'milestone' ? 'bg-[#0047AB]' : kd.type === 'deadline' ? 'bg-[#FF4F00]' : 'bg-emerald-500'}`}>
                      {kd.type === 'milestone' ? <Flag size={14} /> : kd.type === 'deadline' ? <AlertTriangle size={14} /> : <Calendar size={14} />}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">{kd.title}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-zinc-600">{kd.date}</span>
                        <Badge variant="outline" className="text-[10px]">{kd.type}</Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Contract Totals */}
          <div className="border-2 border-black bg-zinc-900 text-white brutal-shadow p-6">
            <div className="grid grid-cols-5 gap-4 text-center">
              <div><p className="text-3xl font-black font-['Chivo']">{selectedClauses.length}</p><p className="text-[10px] uppercase tracking-wider text-zinc-400">Library Clauses</p></div>
              <div><p className="text-3xl font-black font-['Chivo'] text-[#FF4F00]">{userDefinedClauses.length}</p><p className="text-[10px] uppercase tracking-wider text-zinc-400">User Clauses</p></div>
              <div><p className="text-3xl font-black font-['Chivo'] text-[#0047AB]">{keyDates.length}</p><p className="text-[10px] uppercase tracking-wider text-zinc-400">Key Dates</p></div>
              <div><p className="text-3xl font-black font-['Chivo'] text-emerald-300">{includeAllSubclauses ? 'ALL' : 'NONE'}</p><p className="text-[10px] uppercase tracking-wider text-zinc-400">Sub-clauses</p></div>
              <div><p className="text-3xl font-black font-['Chivo'] text-emerald-400">{selectedClauses.length + userDefinedClauses.length}</p><p className="text-[10px] uppercase tracking-wider text-zinc-400">Total Provisions</p></div>
            </div>
          </div>
        </div>
      )}

      {/* Step 5: Generate */}
      {activeStep === 5 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="border border-zinc-200 bg-white brutal-shadow p-6">
            <h2 className="font-bold font-['Chivo'] uppercase mb-4">Review & Generate</h2>
            <div className="space-y-4">
              <div className="p-4 bg-zinc-50">
                <h4 className="font-bold text-sm mb-2">Project Details</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-zinc-500">Name:</span> {formData.name || '-'}</div>
                  <div><span className="text-zinc-500">Sector:</span> {formData.sector || '-'}</div>
                  <div><span className="text-zinc-500">Framework:</span> {formData.framework || '-'}</div>
                  <div><span className="text-zinc-500">Delivery:</span> {formData.delivery_model || '-'}</div>
                  <div><span className="text-zinc-500">Value:</span> {formData.contract_value ? `$${parseInt(formData.contract_value).toLocaleString()}` : '-'}</div>
                  <div><span className="text-zinc-500">Duration:</span> {formData.duration_months ? `${formData.duration_months} months` : '-'}</div>
                </div>
              </div>
              <div className="p-4 bg-blue-50 border border-[#0047AB]">
                <h4 className="font-bold text-sm mb-2">Clauses Summary</h4>
                <div className="text-sm">
                  <p><strong>{selectedClauses.length}</strong> library clauses selected</p>
                  <p><strong>{userDefinedClauses.length}</strong> user-defined clauses added</p>
                  <p className="text-[#0047AB] mt-2">Sub-clauses: {includeAllSubclauses ? 'All included' : 'Not included'}</p>
                </div>
              </div>
              <Button onClick={handleSubmit} disabled={loading} className="btn-brutal w-full bg-black text-white py-4 h-auto text-lg" data-testid="btn-generate">
                {loading ? 'Generating Detailed Contract...' : 'Generate Contract'}
              </Button>
            </div>
          </div>

          <div className="space-y-6">
            {result ? (
              <>
                <div className="border border-zinc-200 bg-white brutal-shadow p-6">
                  <h3 className="font-bold font-['Chivo'] uppercase tracking-wide mb-4 flex items-center gap-2"><BarChart3 size={20} /> Risk Analysis</h3>
                  <div className="mb-6">
                    <div className="flex justify-between items-end mb-2">
                      <span className="text-xs uppercase tracking-wider text-zinc-500">Risk Score</span>
                      <span className={`text-3xl font-black font-['Chivo'] ${result.risk_score > 70 ? 'text-[#FF4F00]' : result.risk_score > 50 ? 'text-amber-500' : 'text-emerald-500'}`}>{result.risk_score}/100</span>
                    </div>
                    <Progress value={result.risk_score} className="h-2" />
                  </div>
                  {result.risk_factors?.length > 0 && (
                    <div className="space-y-2">
                      {result.risk_factors.map((f, i) => (
                        <div key={i} className="p-2 bg-zinc-50 border-l-4 border-[#FF4F00] text-sm">
                          <span className="font-medium">{f.factor}</span>: <span className="text-zinc-600">{f.description}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="border border-zinc-200 bg-white brutal-shadow p-6">
                  <h3 className="font-bold font-['Chivo'] uppercase tracking-wide mb-4">Contract Generated</h3>
                  <div className="p-4 bg-emerald-50 border border-emerald-500 mb-4">
                    <p className="text-emerald-700"><strong>{result.total_clauses}</strong> clauses included ({result.selected_clauses?.length || 0} library + {result.user_defined_clauses_count || 0} user-defined)</p>
                  </div>
                </div>

                <div className="border-2 border-black bg-white brutal-shadow-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <FileText size={32} />
                      <div>
                        <p className="font-bold font-['Chivo']">{result.filename}</p>
                        <p className="text-sm text-zinc-500">
                          {result.narrative_status === 'complete' 
                            ? 'Detailed Contract with AI Narratives Ready' 
                            : result.narrative_status === 'pending'
                            ? 'Contract ready — AI is drafting detailed narratives...'
                            : 'Contract Document Ready'}
                        </p>
                      </div>
                    </div>
                    {result.narrative_status === 'pending' && (
                      <div className="flex items-center gap-2 text-sm text-amber-600">
                        <Loader2 size={16} className="animate-spin" />
                        <span>Enriching...</span>
                      </div>
                    )}
                    {result.narrative_status === 'complete' && (
                      <div className="flex items-center gap-2 text-sm text-emerald-600">
                        <CheckCircle size={16} />
                        <span>AI Narratives Ready</span>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <a href={`${API}${result.download_url}`} className="btn-brutal bg-black text-white px-4 py-2 flex items-center gap-2 text-sm" data-testid="btn-download-docx">
                      <Download size={16} /> DOCX
                    </a>
                    <a href={`${API}/contracts/${result.contract_id}/pdf`} className="btn-brutal bg-[#0047AB] text-white px-4 py-2 flex items-center gap-2 text-sm" data-testid="btn-download-pdf">
                      <FileDown size={16} /> PDF
                    </a>
                  </div>
                </div>
              </>
            ) : (
              <div className="border border-dashed border-zinc-300 bg-zinc-50 p-12 text-center">
                <FileText className="mx-auto mb-4 text-zinc-400" size={48} />
                <p className="text-zinc-500">Click "Generate Contract" to create your detailed contract</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <Button onClick={() => setActiveStep(Math.max(1, activeStep - 1))} disabled={activeStep === 1} variant="outline" className="px-6">
          Previous
        </Button>
        <Button onClick={() => setActiveStep(Math.min(5, activeStep + 1))} disabled={activeStep === 5} className="px-6 bg-black text-white">
          {activeStep === 4 ? 'Proceed to Generate' : 'Next'}
        </Button>
      </div>
    </div>
  );
};

// PM Tools View
const PMToolsView = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("upload");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [selectedContract, setSelectedContract] = useState("");
  const [reviews, setReviews] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [delayPrediction, setDelayPrediction] = useState(null);
  const [showNewReview, setShowNewReview] = useState(false);
  const [newReview, setNewReview] = useState({ reviewer_role: "project_manager", review_type: "standard", priority: "normal", due_date: "", comments: "" });

  useEffect(() => {
    fetchContracts();
  }, []);

  useEffect(() => {
    if (selectedContract) {
      fetchReviews();
      fetchSessions();
      fetchDelayPrediction();
    }
  }, [selectedContract]);

  const fetchContracts = async () => {
    try {
      const res = await axios.get(`${API}/contracts`);
      setContracts(res.data);
    } catch (e) { console.log("Failed to fetch contracts"); }
  };

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API}/contracts/${selectedContract}/reviews`);
      setReviews(res.data);
    } catch (e) { console.log("Failed to fetch reviews"); }
  };

  const fetchSessions = async () => {
    try {
      const res = await axios.get(`${API}/contracts/${selectedContract}/collaboration`);
      setSessions(res.data);
    } catch (e) { console.log("Failed to fetch sessions"); }
  };

  const fetchDelayPrediction = async () => {
    try {
      const res = await axios.get(`${API}/contracts/${selectedContract}/predict-delay`);
      setDelayPrediction(res.data);
    } catch (e) { console.log("Failed to fetch delay prediction"); }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a file");
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/pm/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(response.data);
      toast.success("Schedule analyzed!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to analyze schedule");
    } finally {
      setLoading(false);
    }
  };

  const createReview = async () => {
    if (!user) { toast.error("Please sign in to create a review"); return; }
    try {
      await axios.post(`${API}/contracts/${selectedContract}/reviews`, newReview);
      setShowNewReview(false);
      setNewReview({ reviewer_role: "project_manager", review_type: "standard", priority: "normal", due_date: "", comments: "" });
      fetchReviews();
      toast.success("Review request created");
    } catch (e) { toast.error("Failed to create review"); }
  };

  const updateReviewStatus = async (reviewId, status, rating = null) => {
    try {
      const updates = { status };
      if (rating) updates.rating = rating;
      await axios.put(`${API}/contracts/${selectedContract}/reviews/${reviewId}`, updates);
      fetchReviews();
      toast.success("Review updated");
    } catch (e) { toast.error("Failed to update review"); }
  };

  const finalizeContract = async () => {
    try {
      const res = await axios.post(`${API}/contracts/${selectedContract}/finalize`);
      if (res.data.can_finalize) {
        toast.success("Contract finalized successfully!");
        fetchContracts();
      } else {
        toast.warning(res.data.message);
      }
    } catch (e) { toast.error("Failed to finalize contract"); }
  };

  const tabs = [
    { id: "upload", label: "Schedule Upload", icon: Upload },
    { id: "reviews", label: "Review & Approve", icon: CheckCircle },
    { id: "collaborate", label: "Collaboration", icon: Users },
    { id: "predictive", label: "Predictive Delay", icon: TrendingUp }
  ];

  return (
    <div className="space-y-6" data-testid="pmtools-view" data-export-region>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight uppercase font-['Chivo'] mb-2">PM Tools & Collaboration</h1>
          <p className="text-zinc-500">Import schedules, collaborate on contracts, review and finalize</p>
        </div>
        <ExportToolbar title="PM Tools" pdfFilename="pm-tools" />
      </div>

      <InfoRibbon
        title="How to Use PM Tools"
        color="green"
        steps={[
          "Upload your schedule file (XER from Primavera P6, MPP from MS Project, or XML)",
          "Select a contract to manage reviews and collaboration",
          "Request reviews from Project Managers, Directors, or Legal teams",
          "Track review status and address feedback comments",
          "Finalize contract once all reviews are approved"
        ]}
        tips={[
          "Use Predictive Delay to identify schedule risks early",
          "Add comments to specific clauses during review",
          "Create collaboration sessions for real-time team discussions"
        ]}
      />

      {/* Contract Selector */}
      <div className="border border-zinc-200 bg-white brutal-shadow p-4">
        <label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Select Contract for Review & Collaboration</label>
        <Select value={selectedContract} onValueChange={setSelectedContract}>
          <SelectTrigger className="w-full md:w-96" data-testid="select-contract-pm">
            <SelectValue placeholder="Choose a contract" />
          </SelectTrigger>
          <SelectContent>
            {contracts.map(c => (
              <SelectItem key={c.id} value={c.id}>{c.project_name} ({c.framework})</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Tabs */}
      <div className="border-b border-zinc-200 flex overflow-x-auto">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === tab.id ? 'border-black text-black' : 'border-transparent text-zinc-500 hover:text-zinc-700'}`}>
              <Icon size={16} />{tab.label}
            </button>
          );
        })}
      </div>

      {/* Upload Tab */}
      {activeTab === "upload" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="border border-zinc-200 bg-white brutal-shadow p-6">
            <h2 className="font-bold font-['Chivo'] uppercase tracking-wide mb-6 pb-4 border-b border-zinc-200">Upload Schedule File</h2>
            <div className="space-y-4">
              <div className="border-2 border-dashed border-zinc-300 p-8 text-center">
                <Upload className="mx-auto mb-4 text-zinc-400" size={48} />
                <input type="file" accept=".xml,.mpp,.xer" onChange={(e) => setFile(e.target.files[0])} className="hidden" id="file-upload" />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <span className="text-[#0047AB] hover:underline font-medium">Choose file</span>
                  <span className="text-zinc-500"> or drag and drop</span>
                </label>
                <p className="text-xs text-zinc-400 mt-2">Supports XER (Primavera P6), MPP (MS Project), XML formats</p>
              </div>
              {file && (
                <div className="p-3 bg-zinc-50 flex items-center justify-between">
                  <span className="text-sm font-medium">{file.name}</span>
                  <button onClick={() => setFile(null)} className="text-zinc-400 hover:text-zinc-600"><X size={16} /></button>
                </div>
              )}
              <Button onClick={handleUpload} disabled={loading || !file} className="btn-brutal w-full bg-black text-white py-3 h-auto" data-testid="btn-upload">
                {loading ? 'Analyzing...' : 'Analyze Schedule'}
              </Button>
            </div>
          </div>

          {result && (
            <div className="space-y-6">
              <div className="border border-zinc-200 bg-white brutal-shadow p-6">
                <h3 className="font-bold font-['Chivo'] uppercase tracking-wide mb-4">Schedule Summary</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-zinc-50 text-center">
                    <p className="text-3xl font-black font-['Chivo']">{result.total_tasks}</p>
                    <p className="text-xs uppercase text-zinc-500">Total Tasks</p>
                  </div>
                  <div className="p-4 bg-zinc-50 text-center">
                    <p className="text-3xl font-black font-['Chivo'] text-[#FF4F00]">{result.critical_path_tasks}</p>
                    <p className="text-xs uppercase text-zinc-500">Critical Path</p>
                  </div>
                  <div className="p-4 bg-zinc-50 text-center">
                    <p className="text-3xl font-black font-['Chivo']">{result.project_duration_days}</p>
                    <p className="text-xs uppercase text-zinc-500">Duration (Days)</p>
                  </div>
                  <div className="p-4 bg-zinc-50 text-center">
                    <p className="text-lg font-bold">{result.start_date || 'N/A'}</p>
                    <p className="text-xs uppercase text-zinc-500">Start Date</p>
                  </div>
                </div>
              </div>
              {result.recommendations?.length > 0 && (
                <div className="border border-zinc-200 bg-white brutal-shadow p-6">
                  <h3 className="font-bold font-['Chivo'] uppercase tracking-wide mb-4">Recommendations</h3>
                  <ul className="space-y-2">
                    {result.recommendations.map((r, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="text-emerald-500 mt-0.5 flex-shrink-0" size={16} />
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Reviews Tab */}
      {activeTab === "reviews" && selectedContract && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="font-bold font-['Chivo'] uppercase">Contract Reviews</h3>
            <div className="flex gap-2">
              {user && <Button onClick={() => setShowNewReview(true)} className="bg-black text-white"><Plus size={16} className="mr-2" /> Request Review</Button>}
              {user && reviews.filter(r => r.status === "approved").length === reviews.length && reviews.length > 0 && (
                <Button onClick={finalizeContract} className="bg-emerald-500 text-white"><Check size={16} className="mr-2" /> Finalize Contract</Button>
              )}
            </div>
          </div>

          {/* Review Summary */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bento-item p-4 text-center">
              <p className="text-3xl font-black">{reviews.length}</p>
              <p className="text-xs uppercase text-zinc-500">Total Reviews</p>
            </div>
            <div className="bento-item p-4 text-center">
              <p className="text-3xl font-black text-amber-500">{reviews.filter(r => r.status === "pending").length}</p>
              <p className="text-xs uppercase text-zinc-500">Pending</p>
            </div>
            <div className="bento-item p-4 text-center">
              <p className="text-3xl font-black text-emerald-500">{reviews.filter(r => r.status === "approved").length}</p>
              <p className="text-xs uppercase text-zinc-500">Approved</p>
            </div>
            <div className="bento-item p-4 text-center">
              <p className="text-3xl font-black text-red-500">{reviews.filter(r => r.status === "rejected" || r.status === "changes_requested").length}</p>
              <p className="text-xs uppercase text-zinc-500">Changes Needed</p>
            </div>
          </div>

          {/* Reviews List */}
          {reviews.length === 0 ? (
            <div className="border border-dashed border-zinc-300 p-12 text-center">
              <Users className="mx-auto mb-4 text-zinc-400" size={48} />
              <p className="text-zinc-500">No reviews requested yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {reviews.map(review => (
                <div key={review.id} className={`border ${review.status === 'approved' ? 'border-emerald-500 bg-emerald-50' : review.status === 'rejected' ? 'border-red-500 bg-red-50' : 'border-zinc-200 bg-white'} brutal-shadow p-4`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className="bg-[#0047AB] text-white border-none">{review.reviewer_role.replace('_', ' ')}</Badge>
                        <Badge variant="outline">{review.review_type}</Badge>
                        <Badge className={`${review.priority === 'urgent' ? 'bg-red-500' : review.priority === 'high' ? 'bg-[#FF4F00]' : 'bg-zinc-500'} text-white border-none`}>{review.priority}</Badge>
                        <Badge className={`${review.status === 'approved' ? 'bg-emerald-500' : review.status === 'rejected' ? 'bg-red-500' : review.status === 'changes_requested' ? 'bg-amber-500' : 'bg-zinc-400'} text-white border-none`}>{review.status}</Badge>
                      </div>
                      <p className="font-medium">{review.reviewer_name}</p>
                      {review.comments && <p className="text-sm text-zinc-600 mt-1">{review.comments}</p>}
                      {review.rating && (
                        <div className="flex items-center gap-1 mt-2">
                          {[1,2,3,4,5].map(s => (
                            <Star key={s} size={14} className={s <= review.rating ? 'text-amber-400 fill-amber-400' : 'text-zinc-300'} />
                          ))}
                        </div>
                      )}
                      {review.due_date && <p className="text-xs text-zinc-500 mt-2"><Calendar size={12} className="inline mr-1" />Due: {review.due_date}</p>}
                    </div>
                    {user && review.status === 'pending' && review.reviewer_id === user.id && (
                      <div className="flex gap-2">
                        <Select onValueChange={(rating) => updateReviewStatus(review.id, 'approved', parseInt(rating))}>
                          <SelectTrigger className="w-24"><SelectValue placeholder="Rate" /></SelectTrigger>
                          <SelectContent>
                            {[1,2,3,4,5].map(r => <SelectItem key={r} value={r.toString()}>{r} Star{r > 1 ? 's' : ''}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Button onClick={() => updateReviewStatus(review.id, 'approved')} className="bg-emerald-500 text-white" size="sm"><ThumbsUp size={14} /></Button>
                        <Button onClick={() => updateReviewStatus(review.id, 'changes_requested')} variant="outline" size="sm"><MessageCircle size={14} /></Button>
                        <Button onClick={() => updateReviewStatus(review.id, 'rejected')} className="bg-red-500 text-white" size="sm"><ThumbsDown size={14} /></Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* New Review Modal */}
          {showNewReview && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowNewReview(false)}>
              <div className="bg-white border-2 border-black brutal-shadow-xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
                <div className="p-4 border-b border-zinc-200 flex justify-between items-center">
                  <h3 className="font-bold font-['Chivo'] uppercase">Request Review</h3>
                  <button onClick={() => setShowNewReview(false)}><X size={20} /></button>
                </div>
                <div className="p-6 space-y-4">
                  <Select value={newReview.reviewer_role} onValueChange={(v) => setNewReview({...newReview, reviewer_role: v})}>
                    <SelectTrigger><SelectValue placeholder="Reviewer Role" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="project_manager">Project Manager</SelectItem>
                      <SelectItem value="director">Director</SelectItem>
                      <SelectItem value="legal">Legal</SelectItem>
                      <SelectItem value="technical">Technical</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={newReview.review_type} onValueChange={(v) => setNewReview({...newReview, review_type: v})}>
                    <SelectTrigger><SelectValue placeholder="Review Type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Standard Review</SelectItem>
                      <SelectItem value="final">Final Approval</SelectItem>
                      <SelectItem value="legal">Legal Review</SelectItem>
                      <SelectItem value="technical">Technical Review</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={newReview.priority} onValueChange={(v) => setNewReview({...newReview, priority: v})}>
                    <SelectTrigger><SelectValue placeholder="Priority" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                  <input type="date" value={newReview.due_date} onChange={(e) => setNewReview({...newReview, due_date: e.target.value})} className="underline-input w-full" placeholder="Due Date" />
                  <textarea value={newReview.comments} onChange={(e) => setNewReview({...newReview, comments: e.target.value})} placeholder="Comments/Instructions" className="underline-input w-full min-h-[80px]" />
                  <Button onClick={createReview} className="w-full bg-black text-white">Create Review Request</Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Collaborate Tab */}
      {activeTab === "collaborate" && selectedContract && (
        <div className="space-y-6">
          <div className="border-2 border-[#0047AB] bg-blue-50 p-6">
            <h3 className="font-bold font-['Chivo'] uppercase mb-4 text-[#0047AB] flex items-center gap-2"><Users size={20} /> Team Collaboration</h3>
            <p className="text-sm text-zinc-700 mb-4">Collaborate with your team in real-time. Create sessions, add participants, and discuss contract details.</p>
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div className="bg-white p-4 text-center">
                <p className="text-2xl font-black">{sessions.length}</p>
                <p className="text-xs uppercase text-zinc-500">Active Sessions</p>
              </div>
              <div className="bg-white p-4 text-center">
                <p className="text-2xl font-black">{sessions.reduce((a, s) => a + (s.participants?.length || 0), 0)}</p>
                <p className="text-xs uppercase text-zinc-500">Total Participants</p>
              </div>
              <div className="bg-white p-4 text-center">
                <p className="text-2xl font-black">{reviews.length}</p>
                <p className="text-xs uppercase text-zinc-500">Reviews</p>
              </div>
            </div>
          </div>

          {sessions.length > 0 && (
            <div className="border border-zinc-200 bg-white brutal-shadow p-6">
              <h3 className="font-bold font-['Chivo'] uppercase mb-4">Collaboration Sessions</h3>
              <div className="space-y-3">
                {sessions.map(session => (
                  <div key={session.id} className="p-4 border border-zinc-200 hover:border-zinc-400 transition-colors">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-bold">{session.name}</h4>
                        {session.description && <p className="text-sm text-zinc-600 mt-1">{session.description}</p>}
                        <div className="flex items-center gap-2 mt-2">
                          <Badge className={session.status === 'active' ? 'bg-emerald-500 text-white border-none' : 'bg-zinc-400 text-white border-none'}>{session.status}</Badge>
                          <span className="text-xs text-zinc-500">{session.participants?.length || 0} participants</span>
                        </div>
                      </div>
                      {user && session.status === 'active' && (
                        <Button variant="outline" size="sm">Join Session</Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Predictive Delay Tab */}
      {activeTab === "predictive" && selectedContract && delayPrediction && (
        <div className="space-y-6">
          <div className={`border-2 ${delayPrediction.predicted_delay_days > 30 ? 'border-red-500 bg-red-50' : delayPrediction.predicted_delay_days > 14 ? 'border-amber-500 bg-amber-50' : 'border-emerald-500 bg-emerald-50'} p-6`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Predicted Delay</p>
                <p className="text-5xl font-black font-['Chivo']">{delayPrediction.predicted_delay_days}</p>
                <p className="text-sm text-zinc-600 mt-1">days behind schedule</p>
              </div>
              <div className="text-right">
                <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Confidence</p>
                <p className="text-3xl font-black">{Math.round(delayPrediction.confidence_score * 100)}%</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4">
            <div className="bento-item p-4 text-center">
              <p className="text-2xl font-black">{delayPrediction.current_metrics?.open_risks || 0}</p>
              <p className="text-xs uppercase text-zinc-500">Open Risks</p>
            </div>
            <div className="bento-item p-4 text-center">
              <p className="text-2xl font-black text-red-500">{delayPrediction.current_metrics?.delayed_milestones || 0}</p>
              <p className="text-xs uppercase text-zinc-500">Delayed Milestones</p>
            </div>
            <div className="bento-item p-4 text-center">
              <p className={`text-2xl font-black ${(delayPrediction.current_metrics?.spi || 1) >= 1 ? 'text-emerald-500' : 'text-red-500'}`}>{delayPrediction.current_metrics?.spi?.toFixed(2) || 'N/A'}</p>
              <p className="text-xs uppercase text-zinc-500">SPI</p>
            </div>
            <div className="bento-item p-4 text-center">
              <p className={`text-2xl font-black ${(delayPrediction.current_metrics?.cpi || 1) >= 1 ? 'text-emerald-500' : 'text-red-500'}`}>{delayPrediction.current_metrics?.cpi?.toFixed(2) || 'N/A'}</p>
              <p className="text-xs uppercase text-zinc-500">CPI</p>
            </div>
          </div>

          {delayPrediction.risk_factors?.length > 0 && (
            <div className="border border-zinc-200 bg-white brutal-shadow p-6">
              <h3 className="font-bold font-['Chivo'] uppercase mb-4">Risk Factors Contributing to Delay</h3>
              <div className="space-y-3">
                {delayPrediction.risk_factors.map((factor, i) => (
                  <div key={i} className="p-4 border-l-4 border-[#FF4F00] bg-orange-50">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-bold">{factor.factor}</h4>
                        <p className="text-sm text-zinc-600 mt-1">{factor.description}</p>
                      </div>
                      <Badge className="bg-[#FF4F00] text-white border-none">+{factor.impact_days} days</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {delayPrediction.recommendations?.length > 0 && (
            <div className="border-2 border-[#0047AB] bg-blue-50 p-6">
              <h3 className="font-bold font-['Chivo'] uppercase mb-4 text-[#0047AB]">Recommendations</h3>
              <ul className="space-y-2">
                {delayPrediction.recommendations.map((rec, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Lightbulb className="text-[#0047AB] mt-0.5 flex-shrink-0" size={16} />
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {!selectedContract && activeTab !== "upload" && (
        <div className="border border-dashed border-zinc-300 p-12 text-center">
          <Upload className="mx-auto mb-4 text-zinc-400" size={48} />
          <p className="text-zinc-500">Select a contract to view {activeTab === "reviews" ? "reviews" : activeTab === "collaborate" ? "collaboration" : "delay prediction"}</p>
        </div>
      )}
    </div>
  );
};

// Contract Detail View with Version History and Comments
const ContractDetailView = ({ contractId, onBack }) => {
  const { user } = useAuth();
  const [contract, setContract] = useState(null);
  const [versions, setVersions] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContractData();
  }, [contractId]);

  const fetchContractData = async () => {
    try {
      const [contractRes, versionsRes, commentsRes] = await Promise.all([
        axios.get(`${API}/contracts/${contractId}`),
        axios.get(`${API}/contracts/${contractId}/versions`),
        axios.get(`${API}/contracts/${contractId}/comments`)
      ]);
      setContract(contractRes.data);
      setVersions(versionsRes.data);
      setComments(commentsRes.data);
    } catch (error) {
      toast.error("Failed to load contract");
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !user) return;
    try {
      await axios.post(`${API}/contracts/${contractId}/comments`, { contract_id: contractId, text: newComment });
      setNewComment("");
      fetchContractData();
      toast.success("Comment added");
    } catch (error) {
      toast.error("Failed to add comment");
    }
  };

  const handleApproval = async (action) => {
    try {
      await axios.post(`${API}/contracts/${contractId}/approval`, { contract_id: contractId, action, comment: `${action} by ${user?.name}` });
      fetchContractData();
      toast.success(`Contract ${action}ed`);
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleRollback = async (version) => {
    try {
      await axios.post(`${API}/contracts/${contractId}/rollback/${version}`);
      fetchContractData();
      toast.success(`Rolled back to version ${version}`);
    } catch (error) {
      toast.error("Failed to rollback");
    }
  };

  if (loading) return <div className="p-12 text-center">Loading...</div>;
  if (!contract) return <div className="p-12 text-center">Contract not found</div>;

  return (
    <div className="space-y-8" data-testid="contract-detail-view" data-export-region>
      <div className="flex items-center justify-between">
        <div>
          <button onClick={onBack} className="text-sm text-zinc-500 hover:text-zinc-700 mb-2">&larr; Back to Contracts</button>
          <h1 className="text-3xl font-black tracking-tight uppercase font-['Chivo']">{contract.project_name}</h1>
          <div className="flex items-center gap-3 mt-2">
            <Badge variant="outline">{contract.framework}</Badge>
            <Badge variant="outline">v{contract.version || 1}</Badge>
            <Badge className={`${contract.status === 'approved' ? 'bg-emerald-500' : contract.status === 'rejected' ? 'bg-red-500' : 'bg-amber-500'} text-white border-none`}>{contract.status || 'draft'}</Badge>
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <ExportToolbar title={contract.project_name} pdfFilename={`contract-${contract.project_name}`} />
          <a href={`${API}/contracts/download/${contract.filename}`} className="btn-brutal bg-black text-white px-4 py-2 flex items-center gap-2 text-sm"><Download size={16} /> DOCX</a>
          <a href={`${API}/contracts/${contract.id}/pdf`} className="btn-brutal bg-[#0047AB] text-white px-4 py-2 flex items-center gap-2 text-sm"><FileDown size={16} /> PDF</a>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          <div className="border border-zinc-200 bg-white brutal-shadow p-6">
            <h3 className="font-bold font-['Chivo'] uppercase tracking-wide mb-4">Contract Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div><p className="text-xs text-zinc-500 uppercase">Sector</p><p className="font-medium">{contract.sector}</p></div>
              <div><p className="text-xs text-zinc-500 uppercase">Delivery Model</p><p className="font-medium">{contract.delivery_model}</p></div>
              <div><p className="text-xs text-zinc-500 uppercase">Risk Score</p><p className={`font-bold ${contract.risk_score > 70 ? 'text-[#FF4F00]' : 'text-emerald-500'}`}>{contract.risk_score}/100</p></div>
              <div><p className="text-xs text-zinc-500 uppercase">Contract Value</p><p className="font-medium">{contract.contract_value ? `$${contract.contract_value.toLocaleString()}` : 'TBD'}</p></div>
            </div>
          </div>

          {/* Approval Actions */}
          {user && (
            <div className="border border-zinc-200 bg-white brutal-shadow p-6">
              <h3 className="font-bold font-['Chivo'] uppercase tracking-wide mb-4">Approval Workflow</h3>
              <div className="flex gap-3">
                <Button onClick={() => handleApproval('approve')} className="bg-emerald-500 hover:bg-emerald-600 text-white"><ThumbsUp size={16} className="mr-2" /> Approve</Button>
                <Button onClick={() => handleApproval('reject')} className="bg-red-500 hover:bg-red-600 text-white"><ThumbsDown size={16} className="mr-2" /> Reject</Button>
                <Button onClick={() => handleApproval('request_changes')} variant="outline"><MessageCircle size={16} className="mr-2" /> Request Changes</Button>
              </div>
            </div>
          )}

          {/* Comments */}
          <div className="border border-zinc-200 bg-white brutal-shadow p-6">
            <h3 className="font-bold font-['Chivo'] uppercase tracking-wide mb-4">Comments ({comments.length})</h3>
            <div className="space-y-4 max-h-[300px] overflow-y-auto mb-4">
              {comments.length === 0 ? (
                <p className="text-zinc-500 text-sm">No comments yet</p>
              ) : (
                comments.map((c, i) => (
                  <div key={i} className="p-3 bg-zinc-50 border-l-2 border-zinc-300">
                    <div className="flex justify-between mb-1">
                      <span className="font-medium text-sm">{c.user_name}</span>
                      <span className="text-xs text-zinc-400">{new Date(c.created_at).toLocaleDateString()}</span>
                    </div>
                    <p className="text-sm text-zinc-600">{c.text}</p>
                  </div>
                ))
              )}
            </div>
            {user && (
              <div className="flex gap-2">
                <input type="text" value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Add a comment..." className="flex-1 px-3 py-2 border border-zinc-200 text-sm" />
                <Button onClick={handleAddComment} disabled={!newComment.trim()} className="bg-black text-white">Add</Button>
              </div>
            )}
          </div>
        </div>

        {/* Version History */}
        <div className="border border-zinc-200 bg-white brutal-shadow p-6">
          <h3 className="font-bold font-['Chivo'] uppercase tracking-wide mb-4 flex items-center gap-2"><History size={18} /> Version History</h3>
          <div className="space-y-3">
            {versions.length === 0 ? (
              <p className="text-zinc-500 text-sm">No version history</p>
            ) : (
              versions.map((v, i) => (
                <div key={i} className={`p-3 border ${i === 0 ? 'border-[#0047AB] bg-blue-50' : 'border-zinc-200'}`}>
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-bold text-sm">v{v.version}</span>
                    {i > 0 && user && (
                      <button onClick={() => handleRollback(v.version)} className="text-xs text-[#0047AB] hover:underline flex items-center gap-1">
                        <RotateCcw size={12} /> Rollback
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-zinc-600">{v.changes}</p>
                  <p className="text-xs text-zinc-400 mt-1">{new Date(v.created_at).toLocaleDateString()}</p>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Clause Library with Info Popups
const ClauseLibraryView = ({ clauses }) => {
  const [filter, setFilter] = useState("all");
  const [selectedClause, setSelectedClause] = useState(null);
  const [expandedFramework, setExpandedFramework] = useState(null);
  const [availableClauseData, setAvailableClauseData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/contracts/available-clauses`).then(r => setAvailableClauseData(r.data)).catch(() => {});
  }, []);

  const filteredClauses = filter === "all" ? clauses : clauses.filter(c => c.frameworks.includes(filter));
  const allFrameworks = ["FIDIC", "NEC", "JCT", "AIA", "ICC", "GCC", "AS4000"];

  const handleFrameworkSelect = (fw) => {
    if (expandedFramework === fw) { setExpandedFramework(null); setFilter("all"); }
    else { setExpandedFramework(fw); setFilter(fw); }
  };

  // Get all clauses for the expanded framework from the library
  const getFrameworkClauses = () => {
    if (!expandedFramework || !availableClauseData?.library_clauses) return {};
    const result = {};
    Object.entries(availableClauseData.library_clauses).forEach(([cat, list]) => {
      const filtered = list.filter(c => c.frameworks?.includes(expandedFramework));
      if (filtered.length > 0) result[cat] = filtered;
    });
    return result;
  };

  return (
    <div className="space-y-6" data-testid="clauses-view" data-export-region>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight uppercase font-['Chivo'] mb-2">Clause Library</h1>
          <p className="text-zinc-500">Browse clauses by framework with full details and subclauses</p>
        </div>
      </div>

      {/* Framework Selection Bar */}
      <div className="border-2 border-black brutal-shadow bg-white p-4">
        <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">Select Framework to Expand All Related Clauses</p>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => { setFilter("all"); setExpandedFramework(null); }} className={`px-5 py-2.5 text-xs uppercase tracking-wider font-bold transition-all border-2 ${filter === "all" && !expandedFramework ? 'bg-black text-white border-black' : 'bg-white text-zinc-500 border-zinc-200 hover:border-zinc-400'}`} data-testid="filter-all">All</button>
          {allFrameworks.map((fw) => (
            <button key={fw} onClick={() => handleFrameworkSelect(fw)} className={`px-5 py-2.5 text-xs uppercase tracking-wider font-bold transition-all border-2 ${expandedFramework === fw ? 'bg-[#0047AB] text-white border-[#0047AB]' : 'bg-white text-zinc-600 border-zinc-200 hover:border-[#0047AB] hover:text-[#0047AB]'}`} data-testid={`filter-${fw}`}>{fw}</button>
          ))}
        </div>
      </div>

      {/* Expanded Framework View */}
      {expandedFramework && (() => {
        const fwClauses = getFrameworkClauses();
        return (
          <div className="space-y-4">
            <div className="border-2 border-[#0047AB] bg-blue-50 p-4 flex items-center justify-between">
              <div>
                <h3 className="font-bold font-['Chivo'] text-[#0047AB]">{expandedFramework} Framework - All Related Clauses</h3>
                <p className="text-xs text-zinc-600">{Object.values(fwClauses).flat().length} clauses across {Object.keys(fwClauses).length} categories</p>
              </div>
              <Button onClick={() => { setExpandedFramework(null); setFilter("all"); }} variant="outline" className="border-[#0047AB] text-[#0047AB]">Close</Button>
            </div>
            {Object.entries(fwClauses).map(([category, clauseList]) => (
              <div key={category} className="border border-zinc-200 bg-white brutal-shadow">
                <div className="p-4 bg-zinc-50 border-b border-zinc-200">
                  <h4 className="font-bold uppercase text-sm tracking-wider">{category.replace(/_/g, ' ')}</h4>
                  <p className="text-xs text-zinc-500">{clauseList.length} clauses</p>
                </div>
                <div className="divide-y divide-zinc-100">
                  {clauseList.map((clause) => {
                    const fullClause = clauses.find(c => c.id === clause.id);
                    return (
                      <div key={clause.id} className="p-4 hover:bg-zinc-50 cursor-pointer transition-colors" onClick={() => setSelectedClause(fullClause || clause)}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-sm">{clause.title}</span>
                            <Badge className={`text-xs ${clause.risk_level === 'high' ? 'bg-red-500' : clause.risk_level === 'medium' ? 'bg-amber-500' : 'bg-emerald-500'} text-white border-none`}>{clause.risk_level}</Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-zinc-400 font-mono">{clause.subclauses_count} sub-clauses</span>
                            <ChevronRight size={14} className="text-zinc-400" />
                          </div>
                        </div>
                        <p className="text-xs text-zinc-500">{clause.description}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Grid of clauses (when no framework expanded) */}
      {!expandedFramework && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredClauses.map((clause) => (
            <div key={clause.id} className="bento-item p-6 cursor-pointer" onClick={() => setSelectedClause(clause)}>
              <div className="flex items-start justify-between mb-3">
                <Badge className={`text-white border-none ${clause.risk_level === 'high' ? 'bg-[#FF4F00]' : clause.risk_level === 'medium' ? 'bg-amber-500' : 'bg-emerald-500'}`}>{clause.risk_level}</Badge>
                <div className="flex items-center gap-2">
                  <ClauseInfoPopup clause={clause} />
                  <span className="text-xs font-mono text-zinc-400">{clause.frameworks.join('/')}</span>
                </div>
              </div>
              <h3 className="font-bold font-['Chivo'] mb-2">{clause.title}</h3>
              <p className="text-sm text-zinc-500 line-clamp-2">{clause.text}</p>
              {clause.subclauses && (
                <p className="text-xs text-[#0047AB] mt-3">{clause.subclauses.length} subclauses</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Clause Detail Modal */}
      {selectedClause && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedClause(null)}>
          <div className="bg-white border-2 border-black brutal-shadow-xl max-w-3xl w-full max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-zinc-200 flex items-start justify-between sticky top-0 bg-white">
              <div>
                <Badge className={`mb-2 text-white border-none ${selectedClause.risk_level === 'high' ? 'bg-[#FF4F00]' : selectedClause.risk_level === 'medium' ? 'bg-amber-500' : 'bg-emerald-500'}`}>{selectedClause.risk_level} risk</Badge>
                <h2 className="text-2xl font-black font-['Chivo']">{selectedClause.title}</h2>
              </div>
              <button onClick={() => setSelectedClause(null)} className="p-2 hover:bg-zinc-100"><X size={20} /></button>
            </div>
            <div className="p-6">
              {/* Info Popup Content */}
              {selectedClause.info_popup && (
                <div className="mb-6 p-4 bg-blue-50 border border-[#0047AB]">
                  <h4 className="font-bold text-[#0047AB] mb-2">{selectedClause.info_popup.title}</h4>
                  <p className="text-sm text-zinc-600 mb-3">{selectedClause.info_popup.description}</p>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    {selectedClause.info_popup.key_points?.map((point, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs">
                        <CheckCircle size={12} className="text-emerald-500 mt-0.5" />
                        <span>{point}</span>
                      </div>
                    ))}
                  </div>
                  {selectedClause.info_popup.risk_warning && (
                    <div className="p-2 bg-amber-50 border-l-2 border-amber-500 text-xs">
                      <AlertTriangle size={12} className="inline mr-1 text-amber-500" />{selectedClause.info_popup.risk_warning}
                    </div>
                  )}
                </div>
              )}

              <p className="text-zinc-600 leading-relaxed mb-6">{selectedClause.text}</p>

              {/* Subclauses */}
              {selectedClause.subclauses && selectedClause.subclauses.length > 0 && (
                <div className="border-t border-zinc-200 pt-6">
                  <h3 className="font-bold font-['Chivo'] uppercase tracking-wide mb-4">Subclauses ({selectedClause.subclauses.length})</h3>
                  <div className="space-y-4">
                    {selectedClause.subclauses.map((sub, i) => (
                      <div key={i} className="p-4 bg-zinc-50 border-l-4 border-[#0047AB]">
                        <h4 className="font-bold text-sm mb-2">{sub.number} {sub.title}</h4>
                        <p className="text-sm text-zinc-600">{sub.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-zinc-200">
                <div className="p-4 bg-zinc-50">
                  <p className="text-xs uppercase text-zinc-500 mb-1">Category</p>
                  <p className="font-medium">{selectedClause.category.replace(/_/g, ' ')}</p>
                </div>
                <div className="p-4 bg-zinc-50">
                  <p className="text-xs uppercase text-zinc-500 mb-1">Frameworks</p>
                  <div className="flex gap-2">{selectedClause.frameworks.map((fw) => <Badge key={fw} variant="outline">{fw}</Badge>)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Delay Prediction, Claim Prevention, Compare, Copilot, Contracts views (simplified for space)
const DelayPredictionView = () => {
  const [formData, setFormData] = useState({ project_name: "", sector: "", framework: "", delivery_model: "", contract_value: "", duration_months: "", risk_description: "", schedule_buffer_percent: "10", complexity_level: "medium" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.project_name || !formData.sector || !formData.framework || !formData.delivery_model) { toast.error("Fill required fields"); return; }
    setLoading(true);
    try {
      const payload = { ...formData, contract_value: formData.contract_value ? parseFloat(formData.contract_value) : null, duration_months: formData.duration_months ? parseInt(formData.duration_months) : null, schedule_buffer_percent: parseFloat(formData.schedule_buffer_percent) };
      const response = await axios.post(`${API}/ai-engine/delay-prediction`, payload);
      setResult(response.data);
      toast.success("Delay analysis complete");
    } catch (error) { toast.error(error.response?.data?.detail || "Failed"); } finally { setLoading(false); }
  };

  const getSeverityColor = (s) => s === 'critical' ? 'border-red-500 bg-red-50' : s === 'high' ? 'border-orange-500 bg-orange-50' : s === 'medium' ? 'border-amber-500 bg-amber-50' : 'border-emerald-500 bg-emerald-50';
  const getSeverityBadge = (s) => s === 'critical' ? 'bg-red-100 text-red-700' : s === 'high' ? 'bg-orange-100 text-orange-700' : s === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700';

  return (
    <div className="space-y-6" data-testid="delay-view" data-export-region>
      <div className="flex items-center justify-between"><div><h1 className="text-3xl font-black tracking-tight uppercase font-['Chivo'] mb-2">Delay Prediction</h1>
        <p className="text-zinc-500">AI-powered categorical delay prediction with mitigation strategies and commercial impact</p></div>
        <ExportToolbar title="Delay Prediction" pdfFilename="delay-prediction" /></div>
      
      <div className="border-2 border-black brutal-shadow bg-white p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Project Name *</label>
              <input type="text" value={formData.project_name} onChange={(e) => setFormData({...formData, project_name: e.target.value})} className="w-full border-2 border-zinc-200 p-2 text-sm focus:border-black" placeholder="Highway Bridge Project" data-testid="delay-project-name" /></div>
            <div><label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Sector *</label>
              <Select value={formData.sector} onValueChange={(v) => setFormData({...formData, sector: v})}><SelectTrigger className="border-2 border-zinc-200"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent><SelectItem value="Rail">Rail</SelectItem><SelectItem value="Energy">Energy</SelectItem><SelectItem value="Infrastructure">Infrastructure</SelectItem><SelectItem value="Buildings">Buildings</SelectItem><SelectItem value="Water">Water</SelectItem></SelectContent></Select></div>
            <div><label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Framework *</label>
              <Select value={formData.framework} onValueChange={(v) => setFormData({...formData, framework: v})}><SelectTrigger className="border-2 border-zinc-200"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent><SelectItem value="FIDIC">FIDIC</SelectItem><SelectItem value="NEC">NEC</SelectItem><SelectItem value="JCT">JCT</SelectItem><SelectItem value="ICC">ICC</SelectItem></SelectContent></Select></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Delivery Model *</label>
              <Select value={formData.delivery_model} onValueChange={(v) => setFormData({...formData, delivery_model: v})}><SelectTrigger className="border-2 border-zinc-200"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent><SelectItem value="EPC">EPC</SelectItem><SelectItem value="Design Build">Design Build</SelectItem><SelectItem value="PMC">PMC</SelectItem><SelectItem value="Traditional">Traditional</SelectItem></SelectContent></Select></div>
            <div><label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Contract Value ($)</label>
              <input type="number" value={formData.contract_value} onChange={(e) => setFormData({...formData, contract_value: e.target.value})} className="w-full border-2 border-zinc-200 p-2 text-sm" placeholder="50000000" /></div>
            <div><label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Duration (months)</label>
              <input type="number" value={formData.duration_months} onChange={(e) => setFormData({...formData, duration_months: e.target.value})} className="w-full border-2 border-zinc-200 p-2 text-sm" placeholder="24" /></div>
          </div>
          <div><label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Risk Description</label>
            <textarea value={formData.risk_description} onChange={(e) => setFormData({...formData, risk_description: e.target.value})} className="w-full border-2 border-zinc-200 p-3 text-sm h-20" placeholder="Describe key project risks..." /></div>
          <Button type="submit" disabled={loading} className="btn-brutal bg-black text-white py-3 h-auto px-8" data-testid="btn-predict-delay">
            {loading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Analyzing...</> : 'Run Delay Prediction'}
          </Button>
        </form>
      </div>

      {result && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="border-2 border-black bg-white p-4 text-center brutal-shadow" data-testid="delay-probability">
              <p className="text-xs font-bold uppercase text-zinc-500">Delay Probability</p>
              <p className={`text-3xl font-black ${(result.delay_probability||0) > 0.6 ? 'text-red-600' : (result.delay_probability||0) > 0.3 ? 'text-amber-600' : 'text-emerald-600'}`}>{Math.round((result.delay_probability||0) * 100)}%</p>
            </div>
            <div className="border-2 border-black bg-white p-4 text-center brutal-shadow"><p className="text-xs font-bold uppercase text-zinc-500">Est. Delay Days</p><p className="text-3xl font-black text-[#0047AB]">{result.estimated_delay_days || 0}</p></div>
            <div className="border-2 border-black bg-white p-4 text-center brutal-shadow"><p className="text-xs font-bold uppercase text-zinc-500">Confidence</p><p className="text-3xl font-black">{Math.round((result.confidence_score||0) * 100)}%</p></div>
            <div className="border-2 border-black bg-white p-4 text-center brutal-shadow"><p className="text-xs font-bold uppercase text-zinc-500">Buffer Needed</p><p className="text-3xl font-black text-[#FF4F00]">{result.recommended_buffer_days || 0}d</p></div>
          </div>

          {result.monte_carlo_simulation && (
            <div className="border-2 border-[#0047AB] bg-blue-50 p-5">
              <h3 className="text-xs font-bold uppercase text-[#0047AB] mb-3">Monte Carlo Simulation</h3>
              <div className="grid grid-cols-5 gap-3 text-center">
                <div><p className="text-xs text-zinc-500">Best Case</p><p className="font-bold text-emerald-600 text-lg">{result.monte_carlo_simulation.best_case}d</p></div>
                <div><p className="text-xs text-zinc-500">P50</p><p className="font-bold text-lg">{result.monte_carlo_simulation.p50_delay}d</p></div>
                <div><p className="text-xs text-zinc-500">P75</p><p className="font-bold text-amber-600 text-lg">{result.monte_carlo_simulation.p75_delay}d</p></div>
                <div><p className="text-xs text-zinc-500">P90</p><p className="font-bold text-orange-600 text-lg">{result.monte_carlo_simulation.p90_delay}d</p></div>
                <div><p className="text-xs text-zinc-500">Worst Case</p><p className="font-bold text-red-600 text-lg">{result.monte_carlo_simulation.worst_case}d</p></div>
              </div>
            </div>
          )}

          {result.delay_categories?.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-500">Delay Categories & Mitigation Strategies</h3>
              {result.delay_categories.map((cat, i) => (
                <div key={i} className={`border-l-4 p-5 bg-white border border-zinc-200 ${getSeverityColor(cat.severity)}`} data-testid={`delay-cat-${i}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-base font-bold capitalize">{(i+1)}. {cat.category?.replace(/_/g, ' ')}</span>
                      <Badge className={`${getSeverityBadge(cat.severity)} border-none text-xs`}>{cat.severity}</Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="font-mono font-bold">{cat.impact_days}d impact</span>
                      <span className="text-zinc-500">P: {((cat.probability||0)*100).toFixed(0)}%</span>
                    </div>
                  </div>
                  <p className="text-sm text-zinc-700 mb-3">{cat.description}</p>
                  
                  {cat.mitigation_strategies?.length > 0 && (
                    <div className="mt-3 p-3 bg-zinc-50 border border-zinc-200">
                      <p className="text-xs font-bold uppercase text-emerald-700 mb-2">Mitigation Strategies</p>
                      <div className="space-y-2">
                        {cat.mitigation_strategies.map((m, j) => (
                          <div key={j} className="flex items-start gap-2 text-sm">
                            <CheckCircle size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <span className="font-medium">{m.strategy || m}</span>
                              {m.cost_impact && <span className="text-xs text-zinc-500 ml-2">| Cost: {m.cost_impact}</span>}
                              {m.effectiveness && <Badge variant="outline" className="text-xs ml-2">{m.effectiveness}</Badge>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {formData.contract_value && cat.impact_days > 0 && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200">
                      <p className="text-xs font-bold uppercase text-red-700 mb-1">Predictive Commercial Impact</p>
                      <p className="text-sm font-bold text-red-700">
                        Est. cost: ${Math.round((parseFloat(formData.contract_value) / (parseInt(formData.duration_months || 24) * 30)) * cat.impact_days).toLocaleString()}
                        <span className="font-normal text-zinc-600 ml-2">({cat.impact_days} days x daily burn rate)</span>
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {result.overall_assessment && (
            <div className="border-2 border-zinc-800 bg-zinc-900 text-white p-5">
              <h3 className="text-xs font-bold uppercase text-zinc-400 mb-2">Overall Assessment</h3>
              <p className="text-sm leading-relaxed">{result.overall_assessment}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ClaimPreventionView = ({ clauses }) => {
  const [formData, setFormData] = useState({ project_name: "", sector: "", framework: "", delivery_model: "", risk_description: "", contract_value: "", duration_months: "", payment_history: "on_time", variation_frequency: "low", relationship_quality: "good" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.project_name) { toast.error("Enter project name"); return; }
    setLoading(true);
    try {
      const payload = { ...formData, contract_value: formData.contract_value ? parseFloat(formData.contract_value) : null, duration_months: formData.duration_months ? parseInt(formData.duration_months) : null, existing_issues: [] };
      const response = await axios.post(`${API}/ai-engine/claim-prevention`, payload);
      setResult(response.data);
      toast.success("Claim analysis complete");
    } catch (error) { toast.error(error.response?.data?.detail || "Failed"); } finally { setLoading(false); }
  };

  const getSeverityColor = (s) => s === 'critical' ? 'border-red-500 bg-red-50' : s === 'high' ? 'border-orange-500 bg-orange-50' : s === 'medium' ? 'border-amber-500 bg-amber-50' : 'border-emerald-500 bg-emerald-50';
  const getSeverityBadge = (s) => s === 'critical' ? 'bg-red-100 text-red-700' : s === 'high' ? 'bg-orange-100 text-orange-700' : s === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700';

  return (
    <div className="space-y-6" data-testid="claims-view" data-export-region>
      <div className="flex items-center justify-between"><div><h1 className="text-3xl font-black tracking-tight uppercase font-['Chivo'] mb-2">Claim Prevention</h1>
        <p className="text-zinc-500">AI-powered categorical claim analysis with mitigation strategies and commercial impact</p></div>
        <ExportToolbar title="Claim Prevention" pdfFilename="claim-prevention" /></div>
      
      <div className="border-2 border-black brutal-shadow bg-white p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Project Name *</label>
              <input type="text" value={formData.project_name} onChange={(e) => setFormData({...formData, project_name: e.target.value})} className="w-full border-2 border-zinc-200 p-2 text-sm" placeholder="Project Name" data-testid="claim-project-name" /></div>
            <div><label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Sector</label>
              <Select value={formData.sector} onValueChange={(v) => setFormData({...formData, sector: v})}><SelectTrigger className="border-2 border-zinc-200"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent><SelectItem value="Rail">Rail</SelectItem><SelectItem value="Energy">Energy</SelectItem><SelectItem value="Infrastructure">Infrastructure</SelectItem><SelectItem value="Buildings">Buildings</SelectItem></SelectContent></Select></div>
            <div><label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Framework</label>
              <Select value={formData.framework} onValueChange={(v) => setFormData({...formData, framework: v})}><SelectTrigger className="border-2 border-zinc-200"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent><SelectItem value="FIDIC">FIDIC</SelectItem><SelectItem value="NEC">NEC</SelectItem><SelectItem value="JCT">JCT</SelectItem></SelectContent></Select></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Delivery Model</label>
              <Select value={formData.delivery_model} onValueChange={(v) => setFormData({...formData, delivery_model: v})}><SelectTrigger className="border-2 border-zinc-200"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent><SelectItem value="EPC">EPC</SelectItem><SelectItem value="Design Build">Design Build</SelectItem><SelectItem value="PMC">PMC</SelectItem></SelectContent></Select></div>
            <div><label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Contract Value ($)</label>
              <input type="number" value={formData.contract_value} onChange={(e) => setFormData({...formData, contract_value: e.target.value})} className="w-full border-2 border-zinc-200 p-2 text-sm" placeholder="50000000" /></div>
            <div><label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Duration (months)</label>
              <input type="number" value={formData.duration_months} onChange={(e) => setFormData({...formData, duration_months: e.target.value})} className="w-full border-2 border-zinc-200 p-2 text-sm" placeholder="24" /></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Payment History</label>
              <Select value={formData.payment_history} onValueChange={(v) => setFormData({...formData, payment_history: v})}><SelectTrigger className="border-2 border-zinc-200"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="on_time">On Time</SelectItem><SelectItem value="occasional_delays">Occasional Delays</SelectItem><SelectItem value="frequent_delays">Frequent Delays</SelectItem></SelectContent></Select></div>
            <div><label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Variation Frequency</label>
              <Select value={formData.variation_frequency} onValueChange={(v) => setFormData({...formData, variation_frequency: v})}><SelectTrigger className="border-2 border-zinc-200"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="moderate">Moderate</SelectItem><SelectItem value="high">High</SelectItem></SelectContent></Select></div>
            <div><label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Relationship Quality</label>
              <Select value={formData.relationship_quality} onValueChange={(v) => setFormData({...formData, relationship_quality: v})}><SelectTrigger className="border-2 border-zinc-200"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="excellent">Excellent</SelectItem><SelectItem value="good">Good</SelectItem><SelectItem value="strained">Strained</SelectItem><SelectItem value="adversarial">Adversarial</SelectItem></SelectContent></Select></div>
          </div>
          <div><label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Risk Description</label>
            <textarea value={formData.risk_description} onChange={(e) => setFormData({...formData, risk_description: e.target.value})} className="w-full border-2 border-zinc-200 p-3 text-sm h-20" placeholder="Describe key risks..." /></div>
          <Button type="submit" disabled={loading} className="btn-brutal bg-black text-white py-3 h-auto px-8" data-testid="btn-prevent-claims">
            {loading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Analyzing...</> : 'Run Claim Analysis'}
          </Button>
        </form>
      </div>

      {result && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className={`border-2 p-5 text-center ${result.overall_claim_risk === 'critical' ? 'border-red-500 bg-red-50' : result.overall_claim_risk === 'high' ? 'border-orange-500 bg-orange-50' : result.overall_claim_risk === 'medium' ? 'border-amber-500 bg-amber-50' : 'border-emerald-500 bg-emerald-50'}`} data-testid="claim-risk-overall">
              <p className="text-xs font-bold uppercase text-zinc-500">Overall Risk</p>
              <p className="text-3xl font-black uppercase">{result.overall_claim_risk}</p>
            </div>
            <div className="border-2 border-black bg-white p-5 text-center brutal-shadow"><p className="text-xs font-bold uppercase text-zinc-500">Risk Score</p><p className="text-3xl font-black">{result.claim_risk_score || 0}/100</p></div>
            <div className="border-2 border-black bg-white p-5 text-center brutal-shadow"><p className="text-xs font-bold uppercase text-zinc-500">Documentation</p><p className="text-3xl font-black">{result.dispute_resolution_readiness?.documentation_score || 0}%</p></div>
          </div>

          {result.executive_summary && (
            <div className="border-2 border-zinc-800 bg-zinc-900 text-white p-5">
              <h3 className="text-xs font-bold uppercase text-zinc-400 mb-2">Executive Summary</h3>
              <p className="text-sm leading-relaxed">{result.executive_summary}</p>
            </div>
          )}

          {result.claim_categories?.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-500">Claim Categories & Prevention Strategies</h3>
              {result.claim_categories.map((cat, i) => (
                <div key={i} className={`border-l-4 p-5 bg-white border border-zinc-200 ${getSeverityColor(cat.risk_level)}`} data-testid={`claim-cat-${i}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-base font-bold capitalize">{(i+1)}. {cat.category?.replace(/_/g, ' ')}</span>
                      <Badge className={`${getSeverityBadge(cat.risk_level)} border-none text-xs`}>{cat.risk_level}</Badge>
                    </div>
                    {cat.estimated_value_impact && <span className="text-sm font-bold text-red-600">{cat.estimated_value_impact}</span>}
                  </div>
                  <p className="text-sm text-zinc-700 mb-2">{cat.description}</p>
                  {cat.contractual_basis && <p className="text-xs text-zinc-500 italic mb-3">Contractual Basis: {cat.contractual_basis}</p>}
                  
                  {cat.preventive_measures?.length > 0 && (
                    <div className="mt-3 p-3 bg-zinc-50 border border-zinc-200">
                      <p className="text-xs font-bold uppercase text-emerald-700 mb-2">Preventive Measures</p>
                      <div className="space-y-2">
                        {cat.preventive_measures.map((m, j) => (
                          <div key={j} className="flex items-start gap-2 text-sm">
                            <CheckCircle size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <span className="font-medium">{m.measure || m}</span>
                              {m.priority && <Badge variant="outline" className="text-xs ml-2">{m.priority}</Badge>}
                              {m.responsible_party && <span className="text-xs text-zinc-500 ml-2">({m.responsible_party})</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {formData.contract_value && cat.estimated_value_impact && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200">
                      <p className="text-xs font-bold uppercase text-red-700 mb-1">Predictive Commercial Impact</p>
                      <p className="text-sm text-red-700">{cat.estimated_value_impact}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {result.missing_protections?.length > 0 && (
            <div className="border-2 border-[#FF4F00] bg-orange-50 p-5">
              <h3 className="text-xs font-bold uppercase text-[#FF4F00] mb-3">Missing Protections</h3>
              <div className="space-y-2">
                {result.missing_protections.map((mp, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm p-2 bg-white border border-orange-200">
                    <AlertTriangle size={14} className="text-[#FF4F00] mt-0.5 flex-shrink-0" />
                    <div><p className="font-medium">{mp.protection}</p><p className="text-xs text-zinc-500">{mp.risk_if_missing}</p>{mp.recommended_clause && <p className="text-xs text-[#0047AB] mt-1">Recommended: {mp.recommended_clause}</p>}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ContractComparisonView = ({ contracts }) => {
  const [contract1, setContract1] = useState("");
  const [contract2, setContract2] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [clauseFilter, setClauseFilter] = useState("all");

  const handleCompare = async () => {
    if (!contract1 || !contract2) { toast.error("Select two contracts"); return; }
    if (contract1 === contract2) { toast.error("Select two different contracts"); return; }
    setLoading(true);
    setResult(null);
    try {
      const response = await axios.post(`${API}/contracts/compare`, { contract_id_1: contract1, contract_id_2: contract2 }, { timeout: 90000 });
      setResult(response.data);
      toast.success("Comparison complete!");
    } catch (error) { toast.error(error.response?.data?.detail || "Comparison failed"); } finally { setLoading(false); }
  };

  const getRiskColor = (level) => level === 'high' || level === 'critical' ? 'text-red-600 bg-red-50' : level === 'medium' ? 'text-amber-600 bg-amber-50' : level === 'low' ? 'text-emerald-600 bg-emerald-50' : 'text-zinc-400 bg-zinc-50';
  const getRiskBorder = (level) => level === 'high' || level === 'critical' ? 'border-red-300' : level === 'medium' ? 'border-amber-300' : level === 'low' ? 'border-emerald-300' : 'border-zinc-200';

  const filteredClauses = result?.clause_comparison?.filter(c => {
    if (clauseFilter === 'all') return true;
    if (clauseFilter === 'common') return c.status === 'both';
    if (clauseFilter === 'only1') return c.status === 'only_contract_1';
    if (clauseFilter === 'only2') return c.status === 'only_contract_2';
    if (clauseFilter === 'different') return c.delta !== 'equal';
    return true;
  }) || [];

  const c1 = result?.contract_1;
  const c2 = result?.contract_2;
  const ai = result?.ai_analysis;
  const summary = result?.summary;

  return (
    <div className="space-y-6" data-testid="compare-view" data-export-region>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight uppercase font-['Chivo'] mb-2">Contract Comparison</h1>
          <p className="text-zinc-500">Side-by-side clause analysis with AI-powered risk delta assessment</p>
        </div>
        <ExportToolbar title="Contract Comparison" pdfFilename="contract-comparison" />
      </div>

      {/* Contract Selectors */}
      <div className="border-2 border-black brutal-shadow bg-white p-6">
        <div className="grid grid-cols-1 md:grid-cols-7 gap-4 items-end">
          <div className="md:col-span-3">
            <label className="text-xs font-bold uppercase text-[#0047AB] block mb-2">Contract A</label>
            <select value={contract1} onChange={e => setContract1(e.target.value)} className="w-full border-2 border-[#0047AB] p-3 text-sm focus:outline-none" data-testid="compare-contract-1">
              <option value="">Select first contract...</option>
              {contracts.map((c) => <option key={c.id} value={c.id}>{c.project_name || c.name} ({c.framework})</option>)}
            </select>
          </div>
          <div className="flex items-center justify-center">
            <div className="w-10 h-10 border-2 border-zinc-300 flex items-center justify-center font-black text-zinc-400">VS</div>
          </div>
          <div className="md:col-span-3">
            <label className="text-xs font-bold uppercase text-[#FF4F00] block mb-2">Contract B</label>
            <select value={contract2} onChange={e => setContract2(e.target.value)} className="w-full border-2 border-[#FF4F00] p-3 text-sm focus:outline-none" data-testid="compare-contract-2">
              <option value="">Select second contract...</option>
              {contracts.map((c) => <option key={c.id} value={c.id}>{c.project_name || c.name} ({c.framework})</option>)}
            </select>
          </div>
        </div>
        <div className="mt-4 flex justify-center">
          <Button onClick={handleCompare} disabled={loading || !contract1 || !contract2} className="bg-black text-white px-12 py-3 h-auto text-sm font-bold uppercase tracking-widest" data-testid="btn-compare">
            {loading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Analyzing Contracts...</> : <><GitCompare size={16} className="mr-2" /> Compare & Analyze</>}
          </Button>
        </div>
      </div>

      {result && (
        <>
          {/* Header Cards - Side by Side */}
          <div className="grid grid-cols-2 gap-4">
            <div className="border-2 border-[#0047AB] bg-blue-50 p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-[#0047AB] text-white flex items-center justify-center font-black text-sm">A</div>
                <div>
                  <h3 className="font-bold font-['Chivo'] text-lg leading-tight">{c1?.project_name}</h3>
                  <p className="text-xs text-zinc-500">{c1?.framework} | {c1?.sector} | {c1?.delivery_model}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="p-2 bg-white border border-zinc-200"><p className="text-xs text-zinc-500">Risk</p><p className="text-xl font-black">{c1?.risk_score || '-'}</p></div>
                <div className="p-2 bg-white border border-zinc-200"><p className="text-xs text-zinc-500">Clauses</p><p className="text-xl font-black">{c1?.clause_count || 0}</p></div>
                <div className="p-2 bg-white border border-zinc-200"><p className="text-xs text-zinc-500">Value</p><p className="text-xl font-black">{c1?.contract_value ? `$${(c1.contract_value/1e6).toFixed(0)}M` : '-'}</p></div>
              </div>
            </div>
            <div className="border-2 border-[#FF4F00] bg-orange-50 p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-[#FF4F00] text-white flex items-center justify-center font-black text-sm">B</div>
                <div>
                  <h3 className="font-bold font-['Chivo'] text-lg leading-tight">{c2?.project_name}</h3>
                  <p className="text-xs text-zinc-500">{c2?.framework} | {c2?.sector} | {c2?.delivery_model}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="p-2 bg-white border border-zinc-200"><p className="text-xs text-zinc-500">Risk</p><p className="text-xl font-black">{c2?.risk_score || '-'}</p></div>
                <div className="p-2 bg-white border border-zinc-200"><p className="text-xs text-zinc-500">Clauses</p><p className="text-xl font-black">{c2?.clause_count || 0}</p></div>
                <div className="p-2 bg-white border border-zinc-200"><p className="text-xs text-zinc-500">Value</p><p className="text-xl font-black">{c2?.contract_value ? `$${(c2.contract_value/1e6).toFixed(0)}M` : '-'}</p></div>
              </div>
            </div>
          </div>

          {/* Comparison Summary Bar */}
          <div className="grid grid-cols-4 gap-3">
            <div className="border-2 border-zinc-200 bg-white p-4 text-center"><p className="text-xs font-bold uppercase text-zinc-500">Total Compared</p><p className="text-3xl font-black">{summary?.total_clauses_compared || 0}</p></div>
            <div className="border-2 border-emerald-200 bg-emerald-50 p-4 text-center"><p className="text-xs font-bold uppercase text-emerald-700">Common</p><p className="text-3xl font-black text-emerald-600">{summary?.common_clauses || 0}</p></div>
            <div className="border-2 border-[#0047AB]/30 bg-blue-50 p-4 text-center"><p className="text-xs font-bold uppercase text-[#0047AB]">Only in A</p><p className="text-3xl font-black text-[#0047AB]">{summary?.only_in_1 || 0}</p></div>
            <div className="border-2 border-[#FF4F00]/30 bg-orange-50 p-4 text-center"><p className="text-xs font-bold uppercase text-[#FF4F00]">Only in B</p><p className="text-3xl font-black text-[#FF4F00]">{summary?.only_in_2 || 0}</p></div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-zinc-100 p-1">
              <TabsTrigger value="overview"><BarChart3 size={14} className="mr-1" /> Risk Delta</TabsTrigger>
              <TabsTrigger value="clauses"><FileText size={14} className="mr-1" /> Clause Comparison</TabsTrigger>
              <TabsTrigger value="insights"><Lightbulb size={14} className="mr-1" /> AI Insights</TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              <div className="space-y-6">
                {/* AI Risk Delta */}
                {ai?.risk_delta && (
                  <div className={`border-2 p-6 ${ai.risk_delta.overall === 'A_riskier' ? 'border-[#0047AB] bg-blue-50' : ai.risk_delta.overall === 'B_riskier' ? 'border-[#FF4F00] bg-orange-50' : 'border-emerald-500 bg-emerald-50'}`}>
                    <h3 className="font-bold font-['Chivo'] uppercase mb-3">Risk Delta Assessment</h3>
                    <div className="flex items-center gap-8 mb-4">
                      <div className="text-center">
                        <p className="text-xs uppercase text-[#0047AB] font-bold">Contract A</p>
                        <p className="text-5xl font-black text-[#0047AB]">{ai.risk_delta.score_a}</p>
                      </div>
                      <div className="flex-1 h-4 bg-zinc-200 relative">
                        <div className="absolute inset-y-0 left-0 bg-[#0047AB]" style={{ width: `${ai.risk_delta.score_a}%` }} />
                        <div className="absolute inset-y-0 right-0 bg-[#FF4F00]" style={{ width: `${ai.risk_delta.score_b}%` }} />
                      </div>
                      <div className="text-center">
                        <p className="text-xs uppercase text-[#FF4F00] font-bold">Contract B</p>
                        <p className="text-5xl font-black text-[#FF4F00]">{ai.risk_delta.score_b}</p>
                      </div>
                    </div>
                    <p className="text-sm">{ai.risk_delta.explanation}</p>
                  </div>
                )}

                {ai?.executive_summary && (
                  <div className="border-2 border-zinc-800 bg-zinc-900 text-white p-6">
                    <h3 className="text-xs font-bold uppercase text-zinc-400 mb-2">Executive Summary</h3>
                    <p className="text-sm leading-relaxed">{ai.executive_summary}</p>
                  </div>
                )}

                {/* Key Differences */}
                {ai?.key_differences?.length > 0 && (
                  <div className="border border-zinc-200 bg-white brutal-shadow p-6">
                    <h3 className="font-bold font-['Chivo'] uppercase mb-4">Key Differences</h3>
                    <div className="space-y-3">
                      {ai.key_differences.map((diff, i) => (
                        <div key={i} className={`p-4 border-l-4 ${diff.advantage === 'A' ? 'border-[#0047AB] bg-blue-50' : diff.advantage === 'B' ? 'border-[#FF4F00] bg-orange-50' : 'border-zinc-300 bg-zinc-50'}`} data-testid={`diff-${i}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-bold text-sm">{diff.area}</span>
                            <div className="flex items-center gap-2">
                              <Badge className={`border-none text-xs ${diff.impact === 'high' ? 'bg-red-500 text-white' : diff.impact === 'medium' ? 'bg-amber-500 text-white' : 'bg-zinc-200'}`}>{diff.impact}</Badge>
                              <Badge variant="outline" className="text-xs">{diff.advantage === 'A' ? 'A Better' : diff.advantage === 'B' ? 'B Better' : 'Neutral'}</Badge>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm mb-2">
                            <div><span className="text-[#0047AB] font-bold text-xs">A:</span> {diff.contract_a}</div>
                            <div><span className="text-[#FF4F00] font-bold text-xs">B:</span> {diff.contract_b}</div>
                          </div>
                          {diff.recommendation && <p className="text-xs text-zinc-600 mt-2 p-2 bg-white border border-zinc-200"><strong>Recommendation:</strong> {diff.recommendation}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="clauses">
              <div className="space-y-4">
                {/* Clause Filter */}
                <div className="flex gap-2 flex-wrap">
                  {[
                    { id: 'all', label: `All (${result?.clause_comparison?.length || 0})` },
                    { id: 'common', label: `Common (${summary?.common_clauses || 0})` },
                    { id: 'only1', label: `Only A (${summary?.only_in_1 || 0})` },
                    { id: 'only2', label: `Only B (${summary?.only_in_2 || 0})` },
                    { id: 'different', label: 'Risk Differs' },
                  ].map(f => (
                    <button key={f.id} onClick={() => setClauseFilter(f.id)} className={`px-4 py-2 text-xs uppercase tracking-wider font-bold border-2 transition-all ${clauseFilter === f.id ? 'bg-black text-white border-black' : 'bg-white text-zinc-500 border-zinc-200 hover:border-zinc-400'}`} data-testid={`clause-filter-${f.id}`}>{f.label}</button>
                  ))}
                </div>

                {/* Clause Table */}
                <div className="border border-zinc-200 bg-white brutal-shadow overflow-hidden">
                  <div className="grid grid-cols-12 gap-0 bg-zinc-900 text-white text-xs font-bold uppercase tracking-wider p-3">
                    <div className="col-span-4">Clause</div>
                    <div className="col-span-2 text-center">In A</div>
                    <div className="col-span-2 text-center">Risk A</div>
                    <div className="col-span-2 text-center">In B</div>
                    <div className="col-span-2 text-center">Risk B</div>
                  </div>
                  <ScrollArea className="max-h-[500px]">
                    {filteredClauses.map((clause, i) => (
                      <div key={i} className={`grid grid-cols-12 gap-0 p-3 text-sm border-b border-zinc-100 hover:bg-zinc-50 ${clause.status !== 'both' ? 'bg-amber-50/30' : ''}`} data-testid={`clause-row-${i}`}>
                        <div className="col-span-4 font-medium pr-2">{clause.title}</div>
                        <div className="col-span-2 text-center">
                          {clause.in_contract_1 ? <CheckCircle size={16} className="inline text-[#0047AB]" /> : <X size={16} className="inline text-zinc-300" />}
                        </div>
                        <div className="col-span-2 text-center">
                          <span className={`inline-block px-2 py-0.5 text-xs font-bold uppercase ${getRiskColor(clause.risk_level_1)}`}>{clause.risk_level_1}</span>
                        </div>
                        <div className="col-span-2 text-center">
                          {clause.in_contract_2 ? <CheckCircle size={16} className="inline text-[#FF4F00]" /> : <X size={16} className="inline text-zinc-300" />}
                        </div>
                        <div className="col-span-2 text-center">
                          <span className={`inline-block px-2 py-0.5 text-xs font-bold uppercase ${getRiskColor(clause.risk_level_2)}`}>{clause.risk_level_2}</span>
                        </div>
                      </div>
                    ))}
                  </ScrollArea>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="insights">
              <div className="space-y-6">
                {/* Missing Protections */}
                {ai?.missing_protections && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="border-2 border-[#0047AB] bg-white p-5">
                      <h3 className="font-bold text-sm uppercase text-[#0047AB] mb-3 flex items-center gap-2"><AlertTriangle size={16} /> Missing in Contract A</h3>
                      {ai.missing_protections.contract_a?.length > 0 ? (
                        <div className="space-y-2">
                          {ai.missing_protections.contract_a.map((mp, i) => (
                            <div key={i} className={`p-3 border ${mp.urgency === 'critical' ? 'border-red-300 bg-red-50' : mp.urgency === 'high' ? 'border-orange-300 bg-orange-50' : 'border-zinc-200 bg-zinc-50'}`}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-bold text-sm">{mp.clause}</span>
                                <Badge className={`text-xs border-none ${mp.urgency === 'critical' ? 'bg-red-500 text-white' : mp.urgency === 'high' ? 'bg-orange-500 text-white' : 'bg-zinc-200'}`}>{mp.urgency}</Badge>
                              </div>
                              <p className="text-xs text-zinc-600">{mp.risk}</p>
                            </div>
                          ))}
                        </div>
                      ) : <p className="text-sm text-zinc-400">No missing protections identified</p>}
                    </div>
                    <div className="border-2 border-[#FF4F00] bg-white p-5">
                      <h3 className="font-bold text-sm uppercase text-[#FF4F00] mb-3 flex items-center gap-2"><AlertTriangle size={16} /> Missing in Contract B</h3>
                      {ai.missing_protections.contract_b?.length > 0 ? (
                        <div className="space-y-2">
                          {ai.missing_protections.contract_b.map((mp, i) => (
                            <div key={i} className={`p-3 border ${mp.urgency === 'critical' ? 'border-red-300 bg-red-50' : mp.urgency === 'high' ? 'border-orange-300 bg-orange-50' : 'border-zinc-200 bg-zinc-50'}`}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-bold text-sm">{mp.clause}</span>
                                <Badge className={`text-xs border-none ${mp.urgency === 'critical' ? 'bg-red-500 text-white' : mp.urgency === 'high' ? 'bg-orange-500 text-white' : 'bg-zinc-200'}`}>{mp.urgency}</Badge>
                              </div>
                              <p className="text-xs text-zinc-600">{mp.risk}</p>
                            </div>
                          ))}
                        </div>
                      ) : <p className="text-sm text-zinc-400">No missing protections identified</p>}
                    </div>
                  </div>
                )}

                {/* Negotiation Insights */}
                {ai?.negotiation_insights?.length > 0 && (
                  <div className="border border-zinc-200 bg-white brutal-shadow p-6">
                    <h3 className="font-bold font-['Chivo'] uppercase mb-4 flex items-center gap-2"><Target size={18} /> Negotiation Insights</h3>
                    <div className="space-y-3">
                      {ai.negotiation_insights.map((ni, i) => (
                        <div key={i} className="p-4 border border-zinc-200 hover:border-zinc-400 transition-colors" data-testid={`negotiation-insight-${i}`}>
                          <div className="flex items-center gap-3 mb-2">
                            <Badge className={`border-none text-xs ${ni.leverage === 'A' ? 'bg-[#0047AB] text-white' : 'bg-[#FF4F00] text-white'}`}>Leverage: {ni.leverage}</Badge>
                            <span className="font-bold text-sm">{ni.point}</span>
                          </div>
                          <p className="text-sm text-zinc-600">{ni.strategy}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Overall Recommendation */}
                {ai?.recommendation && (
                  <div className="border-2 border-zinc-800 bg-zinc-900 text-white p-6">
                    <h3 className="text-xs font-bold uppercase text-zinc-400 mb-3">Overall Recommendation</h3>
                    <p className="text-sm leading-relaxed">{ai.recommendation}</p>
                  </div>
                )}

                {!ai && (
                  <div className="text-center py-12 text-zinc-400 border border-dashed border-zinc-300 bg-zinc-50">
                    <Lightbulb size={48} className="mx-auto mb-4 opacity-30" />
                    <p>AI analysis not available for this comparison.</p>
                    <p className="text-sm mt-2">The clause-by-clause comparison is still available in the Clauses tab.</p>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </>
      )}

      {!result && !loading && (
        <div className="text-center py-16 text-zinc-400 border-2 border-dashed border-zinc-300 bg-zinc-50">
          <GitCompare size={64} className="mx-auto mb-6 opacity-20" />
          <h3 className="font-bold text-lg text-zinc-600 mb-2">Select Two Contracts to Compare</h3>
          <p className="text-sm max-w-md mx-auto">Choose two contracts above and click "Compare & Analyze" to get a side-by-side clause comparison with AI-powered risk delta analysis, missing protections, and negotiation insights.</p>
        </div>
      )}
    </div>
  );
};

// Risk Analysis View
const RiskAnalysisView = ({ contracts }) => {
  const { user } = useAuth();
  const { riskAnalysisData, setRiskAnalysisData, resetRiskAnalysis } = useFormData();
  const [selectedContract, setSelectedContract] = useState(riskAnalysisData.selectedContract || "");
  const [analysis, setAnalysis] = useState(riskAnalysisData.analysis || null);
  const [loading, setLoading] = useState(false);
  const [pinchPoints, setPinchPoints] = useState([]);

  // Sync with context
  useEffect(() => {
    setRiskAnalysisData({ selectedContract, analysis });
  }, [selectedContract, analysis]);

  useEffect(() => {
    fetchPinchPoints();
  }, []);

  const fetchPinchPoints = async () => {
    try {
      const res = await axios.get(`${API}/risk/pinch-points`);
      setPinchPoints(res.data.pinch_points || []);
    } catch (e) { console.log("Failed to fetch pinch points"); }
  };

  const handleReset = () => {
    setSelectedContract("");
    setAnalysis(null);
    resetRiskAnalysis();
    toast.success("Risk analysis reset");
  };

  const runAnalysis = async () => {
    if (!selectedContract) return toast.error("Select a contract first");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/risk/detailed-analyze?contract_id=${selectedContract}&include_clause_mapping=true`);
      setAnalysis(res.data);
      toast.success("Analysis complete");
    } catch (e) { 
      toast.error(e.response?.data?.detail || "Analysis failed"); 
    } finally { 
      setLoading(false); 
    }
  };

  const getRiskColor = (rating) => {
    switch(rating?.toLowerCase()) {
      case 'critical': return 'bg-red-600 text-white';
      case 'high': return 'bg-red-500 text-white';
      case 'medium': return 'bg-amber-500 text-white';
      case 'low': return 'bg-emerald-500 text-white';
      default: return 'bg-zinc-200';
    }
  };

  return (
    <div className="space-y-6" data-testid="risk-analysis-view" data-export-region>
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight uppercase font-['Chivo']">Risk Analysis</h1>
          <p className="text-zinc-500 mt-1">Comprehensive contract risk assessment with pinch point detection</p>
        </div>
        <div className="flex gap-2 items-center">
          <ExportToolbar title="Risk Analysis" pdfFilename="risk-analysis" />
          <Button onClick={handleReset} variant="outline" className="border-2 border-zinc-300 hover:border-red-500 hover:bg-red-50 text-zinc-600 hover:text-red-600" data-testid="btn-reset-risk">
            <RefreshCw size={16} className="mr-2" /> Reset
          </Button>
        </div>
      </div>

      <div className="flex gap-4 items-end">
        <div className="flex-1 max-w-md">
          <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2">Select Contract</label>
          <Select value={selectedContract} onValueChange={setSelectedContract}>
            <SelectTrigger className="border-2 border-zinc-200"><SelectValue placeholder="Choose contract to analyze" /></SelectTrigger>
            <SelectContent>
              {contracts.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.project_name} ({c.framework})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={runAnalysis} disabled={loading || !selectedContract} className="bg-[#0047AB] text-white">
          {loading ? <><Loader2 className="animate-spin mr-2" size={16} /> Analyzing...</> : <><Shield className="mr-2" size={16} /> Run Analysis</>}
        </Button>
      </div>

      {/* Pinch Points Reference */}
      <Collapsible>
        <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium text-zinc-600 hover:text-zinc-900">
          <AlertOctagon size={16} /> View Common Pinch Points ({pinchPoints.length}) <ChevronDown size={14} />
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {pinchPoints.map((pp) => (
              <div key={pp.id} className="border border-zinc-200 bg-white p-4">
                <div className="flex items-start justify-between mb-2">
                  <span className="font-medium text-sm">{pp.name}</span>
                  <Badge className={getRiskColor(pp.severity)}>{pp.severity}</Badge>
                </div>
                <p className="text-xs text-zinc-500 mb-2">{pp.description}</p>
                <p className="text-xs text-emerald-600"><Lightbulb size={12} className="inline mr-1" />{pp.opportunity}</p>
              </div>
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {analysis && (
        <div className="space-y-6">
          {/* Executive Summary */}
          <div className="border-2 border-black brutal-shadow bg-white p-6">
            <h2 className="font-bold font-['Chivo'] uppercase mb-4 flex items-center gap-2">
              <Target size={20} /> Executive Summary
            </h2>
            <div className="flex items-center gap-4 mb-4">
              <Badge className={`${getRiskColor(analysis.executive_summary?.overall_risk_rating)} text-lg px-4 py-2`}>
                {analysis.executive_summary?.overall_risk_rating?.toUpperCase()} RISK
              </Badge>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Key Concerns</p>
                <ul className="space-y-1">
                  {analysis.executive_summary?.key_concerns?.map((c, i) => (
                    <li key={i} className="text-sm flex items-start gap-2"><AlertTriangle size={14} className="text-amber-500 mt-0.5" />{c}</li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Immediate Actions</p>
                <ul className="space-y-1">
                  {analysis.executive_summary?.immediate_actions?.map((a, i) => (
                    <li key={i} className="text-sm flex items-start gap-2"><CheckCircle size={14} className="text-emerald-500 mt-0.5" />{a}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Risk Matrix */}
          <div className="border border-zinc-200 bg-white brutal-shadow p-6">
            <h2 className="font-bold font-['Chivo'] uppercase mb-4">Risk Matrix</h2>
            <div className="grid grid-cols-5 gap-4">
              {Object.entries(analysis.risk_matrix || {}).map(([key, value]) => (
                <div key={key} className="text-center">
                  <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${getRiskColor(value?.rating)} text-2xl font-black mb-2`}>
                    {value?.score}
                  </div>
                  <p className="text-xs uppercase font-medium">{key}</p>
                  <p className="text-xs text-zinc-500 mt-1">{value?.rating}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Pinch Points Detected */}
          {analysis.pinch_points_detected?.length > 0 && (
            <div className="border border-zinc-200 bg-white brutal-shadow p-6">
              <h2 className="font-bold font-['Chivo'] uppercase mb-4 flex items-center gap-2">
                <AlertOctagon size={20} className="text-red-500" /> Pinch Points Detected ({analysis.pinch_points_detected.length})
              </h2>
              <div className="space-y-4">
                {analysis.pinch_points_detected.map((pp, i) => (
                  <div key={i} className="border-l-4 border-red-500 pl-4 py-2">
                    <div className="flex items-start justify-between">
                      <h3 className="font-medium">{pp.pinch_point}</h3>
                      <Badge className={getRiskColor(pp.severity)}>{pp.severity}</Badge>
                    </div>
                    <p className="text-sm text-zinc-600 mt-1">{pp.specific_concern}</p>
                    {pp.financial_exposure && <p className="text-sm text-red-600 mt-1"><DollarSign size={14} className="inline" /> {pp.financial_exposure}</p>}
                    <div className="mt-2 p-2 bg-emerald-50 text-sm">
                      <p className="font-medium text-emerald-700">Negotiation Strategy:</p>
                      <p className="text-emerald-600">{pp.negotiation_strategy}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Clause-by-Clause Analysis */}
          {analysis.clause_by_clause_analysis?.length > 0 && (
            <div className="border border-zinc-200 bg-white brutal-shadow p-6">
              <h2 className="font-bold font-['Chivo'] uppercase mb-4">Clause-by-Clause Analysis</h2>
              <div className="space-y-3">
                {analysis.clause_by_clause_analysis.slice(0, 10).map((clause, i) => (
                  <Collapsible key={i}>
                    <CollapsibleTrigger className="w-full text-left p-3 border border-zinc-200 hover:bg-zinc-50 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Badge className={getRiskColor(clause.risk_level)}>{clause.risk_level}</Badge>
                        <span className="font-medium">{clause.clause_title}</span>
                        <span className="text-xs text-zinc-500">{clause.clause_reference}</span>
                      </div>
                      <ChevronDown size={16} />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="p-4 border border-t-0 border-zinc-200 bg-zinc-50">
                      {clause.issues_identified?.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs font-bold uppercase text-red-600 mb-1">Issues Identified:</p>
                          <ul className="text-sm space-y-1">{clause.issues_identified.map((issue, j) => <li key={j}>• {issue}</li>)}</ul>
                        </div>
                      )}
                      {clause.recommended_changes?.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs font-bold uppercase text-emerald-600 mb-1">Recommended Changes:</p>
                          <ul className="text-sm space-y-1">{clause.recommended_changes.map((rec, j) => <li key={j}>• {rec}</li>)}</ul>
                        </div>
                      )}
                      {clause.negotiation_points?.length > 0 && (
                        <div>
                          <p className="text-xs font-bold uppercase text-blue-600 mb-1">Negotiation Points:</p>
                          <ul className="text-sm space-y-1">{clause.negotiation_points.map((np, j) => <li key={j}>• {np}</li>)}</ul>
                        </div>
                      )}
                    </CollapsibleContent>
                  </Collapsible>
                ))}
              </div>
            </div>
          )}

          {/* Detailed Reasoning */}
          {analysis.detailed_reasoning && (
            <div className="border border-zinc-200 bg-white brutal-shadow p-6">
              <h2 className="font-bold font-['Chivo'] uppercase mb-4">Detailed Analysis</h2>
              <p className="text-sm text-zinc-700 whitespace-pre-wrap">{analysis.detailed_reasoning}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Change Management View
const ChangeManagementView = ({ contracts }) => {
  const { user } = useAuth();
  const { changeManagementData, setChangeManagementData, resetChangeManagement } = useFormData();
  const [selectedContract, setSelectedContract] = useState(changeManagementData.selectedContract || "");
  const [changes, setChanges] = useState(changeManagementData.changes || []);
  const [summary, setSummary] = useState(changeManagementData.summary || null);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedChange, setSelectedChange] = useState(null);
  const [newChange, setNewChange] = useState({
    title: "", description: "", change_type: "scope_change", priority: "medium",
    requested_by: "", estimated_cost_impact: "", estimated_time_impact_days: "", justification: ""
  });

  // Sync with context
  useEffect(() => {
    setChangeManagementData({ selectedContract, changes, summary });
  }, [selectedContract, changes, summary]);

  useEffect(() => {
    if (selectedContract) fetchChanges();
  }, [selectedContract]);

  const handleReset = () => {
    setSelectedContract("");
    setChanges([]);
    setSummary(null);
    setSelectedChange(null);
    resetChangeManagement();
    toast.success("Change management reset");
  };

  const fetchChanges = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/contracts/${selectedContract}/changes`);
      setChanges(res.data.changes || []);
      setSummary(res.data.summary);
    } catch (e) { toast.error("Failed to fetch changes"); }
    finally { setLoading(false); }
  };

  const createChange = async () => {
    if (!newChange.title || !newChange.description) return toast.error("Title and description required");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contracts/${selectedContract}/changes`, {
        ...newChange,
        contract_id: selectedContract,
        estimated_cost_impact: parseFloat(newChange.estimated_cost_impact) || null,
        estimated_time_impact_days: parseInt(newChange.estimated_time_impact_days) || null
      });
      toast.success(`Change Order ${res.data.change_number} created`);
      setShowCreate(false);
      setNewChange({ title: "", description: "", change_type: "scope_change", priority: "medium", requested_by: "", estimated_cost_impact: "", estimated_time_impact_days: "", justification: "" });
      fetchChanges();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create"); }
    finally { setLoading(false); }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'approved': return 'bg-emerald-500 text-white';
      case 'rejected': return 'bg-red-500 text-white';
      case 'submitted': return 'bg-blue-500 text-white';
      case 'under_review': return 'bg-amber-500 text-white';
      case 'implemented': return 'bg-purple-500 text-white';
      default: return 'bg-zinc-300';
    }
  };

  return (
    <div className="space-y-6" data-testid="change-management-view" data-export-region>
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight uppercase font-['Chivo']">Change Management</h1>
          <p className="text-zinc-500 mt-1">Track variations, scope changes, and contract modifications</p>
        </div>
        <div className="flex gap-2 items-center">
          <ExportToolbar title="Change Management" pdfFilename="change-management" csvData={changes} csvFilename="changes" />
          <Button onClick={handleReset} variant="outline" className="border-2 border-zinc-300 hover:border-red-500 hover:bg-red-50 text-zinc-600 hover:text-red-600" data-testid="btn-reset-changes">
            <RefreshCw size={16} className="mr-2" /> Reset
          </Button>
        </div>
      </div>

      <div className="flex gap-4 items-end">
        <div className="flex-1 max-w-md">
          <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2">Select Contract</label>
          <Select value={selectedContract} onValueChange={setSelectedContract}>
            <SelectTrigger className="border-2 border-zinc-200"><SelectValue placeholder="Choose contract" /></SelectTrigger>
            <SelectContent>
              {contracts.map((c) => (<SelectItem key={c.id} value={c.id}>{c.project_name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        {selectedContract && (
          <Button onClick={() => setShowCreate(true)} className="bg-black text-white">
            <Plus size={16} className="mr-2" /> New Change Order
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="border border-zinc-200 bg-white p-4 text-center">
            <p className="text-3xl font-black font-['Chivo']">{summary.total_changes}</p>
            <p className="text-xs uppercase text-zinc-500">Total Changes</p>
          </div>
          <div className="border border-zinc-200 bg-white p-4 text-center">
            <p className="text-3xl font-black font-['Chivo'] text-emerald-500">{summary.by_status?.approved || 0}</p>
            <p className="text-xs uppercase text-zinc-500">Approved</p>
          </div>
          <div className="border border-zinc-200 bg-white p-4 text-center">
            <p className="text-3xl font-black font-['Chivo'] text-amber-500">{summary.by_status?.under_review || 0}</p>
            <p className="text-xs uppercase text-zinc-500">Under Review</p>
          </div>
          <div className="border border-zinc-200 bg-white p-4 text-center">
            <p className="text-3xl font-black font-['Chivo'] text-red-500">${(summary.total_estimated_cost_impact || 0).toLocaleString()}</p>
            <p className="text-xs uppercase text-zinc-500">Total Cost Impact</p>
          </div>
        </div>
      )}

      {/* Changes List */}
      {changes.length > 0 && (
        <div className="border border-zinc-200 bg-white brutal-shadow">
          <table className="brutal-table">
            <thead>
              <tr><th>Change #</th><th>Title</th><th>Type</th><th>Priority</th><th>Status</th><th>Cost Impact</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {changes.map((change) => (
                <tr key={change.id}>
                  <td className="font-mono text-sm">{change.change_number}</td>
                  <td className="font-medium">{change.title}</td>
                  <td><Badge variant="outline">{change.change_type?.replace(/_/g, ' ')}</Badge></td>
                  <td><Badge className={change.priority === 'critical' ? 'bg-red-100 text-red-700' : change.priority === 'high' ? 'bg-amber-100 text-amber-700' : 'bg-zinc-100'}>{change.priority}</Badge></td>
                  <td><Badge className={getStatusColor(change.status)}>{change.status}</Badge></td>
                  <td>${(change.estimated_cost_impact || 0).toLocaleString()}</td>
                  <td>
                    <button onClick={() => setSelectedChange(change)} className="text-[#0047AB] hover:underline text-sm flex items-center gap-1">
                      <Eye size={14} /> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Change Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-white border-2 border-black brutal-shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-zinc-200 flex justify-between items-center">
              <h2 className="text-xl font-black font-['Chivo'] uppercase">New Change Order</h2>
              <button onClick={() => setShowCreate(false)} className="p-2 hover:bg-zinc-100"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2">Title *</label>
                <input type="text" value={newChange.title} onChange={(e) => setNewChange({...newChange, title: e.target.value})} className="underline-input w-full" placeholder="Brief title for this change" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2">Change Type *</label>
                  <Select value={newChange.change_type} onValueChange={(v) => setNewChange({...newChange, change_type: v})}>
                    <SelectTrigger className="border-0 border-b-2 border-zinc-200 rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="scope_change">Scope Change</SelectItem>
                      <SelectItem value="time_extension">Time Extension</SelectItem>
                      <SelectItem value="cost_variation">Cost Variation</SelectItem>
                      <SelectItem value="design_change">Design Change</SelectItem>
                      <SelectItem value="site_instruction">Site Instruction</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2">Priority</label>
                  <Select value={newChange.priority} onValueChange={(v) => setNewChange({...newChange, priority: v})}>
                    <SelectTrigger className="border-0 border-b-2 border-zinc-200 rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2">Description *</label>
                <textarea value={newChange.description} onChange={(e) => setNewChange({...newChange, description: e.target.value})} className="underline-input w-full h-24" placeholder="Detailed description of the change" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2">Justification *</label>
                <textarea value={newChange.justification} onChange={(e) => setNewChange({...newChange, justification: e.target.value})} className="underline-input w-full h-20" placeholder="Why is this change needed?" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2">Est. Cost Impact ($)</label>
                  <input type="number" value={newChange.estimated_cost_impact} onChange={(e) => setNewChange({...newChange, estimated_cost_impact: e.target.value})} className="underline-input w-full" placeholder="0" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2">Est. Time Impact (days)</label>
                  <input type="number" value={newChange.estimated_time_impact_days} onChange={(e) => setNewChange({...newChange, estimated_time_impact_days: e.target.value})} className="underline-input w-full" placeholder="0" />
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2">Requested By</label>
                <input type="text" value={newChange.requested_by} onChange={(e) => setNewChange({...newChange, requested_by: e.target.value})} className="underline-input w-full" placeholder="Name or role" />
              </div>
              <Button onClick={createChange} disabled={loading} className="w-full bg-black text-white py-3 h-auto">
                {loading ? 'Creating...' : 'Create Change Order with AI Analysis'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Change Detail Modal */}
      {selectedChange && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedChange(null)}>
          <div className="bg-white border-2 border-black brutal-shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-zinc-200 flex justify-between items-center">
              <div>
                <span className="text-xs text-zinc-500">{selectedChange.change_number}</span>
                <h2 className="text-xl font-black font-['Chivo']">{selectedChange.title}</h2>
              </div>
              <button onClick={() => setSelectedChange(null)} className="p-2 hover:bg-zinc-100"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-6">
              <div className="flex gap-4">
                <Badge className={getStatusColor(selectedChange.status)}>{selectedChange.status}</Badge>
                <Badge variant="outline">{selectedChange.change_type?.replace(/_/g, ' ')}</Badge>
                <Badge className={selectedChange.priority === 'critical' ? 'bg-red-100 text-red-700' : selectedChange.priority === 'high' ? 'bg-amber-100 text-amber-700' : 'bg-zinc-100'}>{selectedChange.priority}</Badge>
              </div>
              
              <div>
                <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Description</p>
                <p className="text-sm">{selectedChange.description}</p>
              </div>

              <div>
                <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Justification</p>
                <p className="text-sm">{selectedChange.justification}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-zinc-50">
                  <p className="text-xs uppercase text-zinc-500">Estimated Cost Impact</p>
                  <p className="text-2xl font-black">${(selectedChange.estimated_cost_impact || 0).toLocaleString()}</p>
                </div>
                <div className="p-4 bg-zinc-50">
                  <p className="text-xs uppercase text-zinc-500">Estimated Time Impact</p>
                  <p className="text-2xl font-black">{selectedChange.estimated_time_impact_days || 0} days</p>
                </div>
              </div>

              {/* AI Analysis */}
              {selectedChange.ai_analysis && (
                <div className="border border-zinc-200 p-4">
                  <h3 className="font-bold mb-3 flex items-center gap-2"><Bot size={18} /> AI Analysis</h3>
                  
                  {selectedChange.ai_analysis.risk_assessment && (
                    <div className="mb-4">
                      <p className="text-xs uppercase text-zinc-500 mb-2">Risk Assessment</p>
                      <Badge className={selectedChange.ai_analysis.risk_assessment.overall_risk === 'high' ? 'bg-red-500 text-white' : selectedChange.ai_analysis.risk_assessment.overall_risk === 'medium' ? 'bg-amber-500 text-white' : 'bg-emerald-500 text-white'}>
                        {selectedChange.ai_analysis.risk_assessment.overall_risk?.toUpperCase()} RISK
                      </Badge>
                      <div className="mt-2 text-sm space-y-1">
                        <p><strong>Cost Risk:</strong> {selectedChange.ai_analysis.risk_assessment.cost_risk}</p>
                        <p><strong>Schedule Risk:</strong> {selectedChange.ai_analysis.risk_assessment.schedule_risk}</p>
                      </div>
                    </div>
                  )}

                  {selectedChange.ai_analysis.approval_recommendation && (
                    <div className="p-3 bg-blue-50">
                      <p className="text-xs uppercase text-blue-600 mb-1">Recommendation</p>
                      <p className="text-sm font-medium">{selectedChange.ai_analysis.approval_recommendation}</p>
                    </div>
                  )}

                  {selectedChange.ai_analysis.mitigation_measures?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs uppercase text-zinc-500 mb-2">Mitigation Measures</p>
                      <ul className="text-sm space-y-1">
                        {selectedChange.ai_analysis.mitigation_measures.map((m, i) => (
                          <li key={i} className="flex items-start gap-2"><CheckCircle size={14} className="text-emerald-500 mt-0.5" />{m}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Timeline View
const TimelineView = ({ contracts }) => {
  const { user } = useAuth();
  const { timelineData, setTimelineData, resetTimelines } = useFormData();
  const [selectedContract, setSelectedContract] = useState(timelineData.selectedContract || "");
  const [contractTimeline, setContractTimeline] = useState(timelineData.contractTimeline || null);
  const [worksTimeline, setWorksTimeline] = useState(timelineData.worksTimeline || null);
  const [activeTab, setActiveTab] = useState("contract");
  const [loading, setLoading] = useState(false);

  // Sync with context
  useEffect(() => {
    setTimelineData({ selectedContract, contractTimeline, worksTimeline });
  }, [selectedContract, contractTimeline, worksTimeline]);

  const handleReset = () => {
    setSelectedContract("");
    setContractTimeline(null);
    setWorksTimeline(null);
    resetTimelines();
    toast.success("Timelines reset");
  };

  const generateContractTimeline = async () => {
    if (!selectedContract) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contracts/${selectedContract}/timeline/contract`);
      setContractTimeline(res.data);
      toast.success("Contract timeline generated");
    } catch (e) { toast.error("Failed to generate timeline"); }
    finally { setLoading(false); }
  };

  const generateWorksTimeline = async () => {
    if (!selectedContract) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contracts/${selectedContract}/timeline/works`);
      setWorksTimeline(res.data);
      toast.success("Works timeline generated");
    } catch (e) { toast.error("Failed to generate timeline"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="timeline-view" data-export-region>
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight uppercase font-['Chivo']">Timeline Summaries</h1>
          <p className="text-zinc-500 mt-1">Visualize contract obligations and works execution schedules</p>
        </div>
        <div className="flex gap-2 items-center">
          <ExportToolbar title="Timeline Summaries" pdfFilename="timelines" />
          <Button onClick={handleReset} variant="outline" className="border-2 border-zinc-300 hover:border-red-500 hover:bg-red-50 text-zinc-600 hover:text-red-600" data-testid="btn-reset-timelines">
            <RefreshCw size={16} className="mr-2" /> Reset
          </Button>
        </div>
      </div>

      <div className="flex gap-4 items-end">
        <div className="flex-1 max-w-md">
          <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2">Select Contract</label>
          <Select value={selectedContract} onValueChange={setSelectedContract}>
            <SelectTrigger className="border-2 border-zinc-200"><SelectValue placeholder="Choose contract" /></SelectTrigger>
            <SelectContent>
              {contracts.map((c) => (<SelectItem key={c.id} value={c.id}>{c.project_name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-2 w-full max-w-md">
          <TabsTrigger value="contract">Contract Timeline</TabsTrigger>
          <TabsTrigger value="works">Works Timeline</TabsTrigger>
        </TabsList>

        <TabsContent value="contract" className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-zinc-600">Key dates, milestones, and contractual obligations</p>
            <Button onClick={generateContractTimeline} disabled={loading || !selectedContract} className="bg-[#0047AB] text-white">
              {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Calendar className="mr-2" size={16} />}
              Generate Timeline
            </Button>
          </div>

          {contractTimeline && (
            <div className="space-y-6">
              {/* Summary */}
              {contractTimeline.summary && (
                <div className="p-4 bg-blue-50 border border-blue-200">
                  <p className="text-sm">{contractTimeline.summary}</p>
                </div>
              )}

              {/* Key Dates */}
              {contractTimeline.key_dates?.length > 0 && (
                <div className="border border-zinc-200 bg-white brutal-shadow p-6">
                  <h3 className="font-bold uppercase text-sm mb-4">Key Dates</h3>
                  <div className="relative">
                    <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-[#0047AB]" />
                    <div className="space-y-4 pl-10">
                      {contractTimeline.key_dates.map((event, i) => (
                        <div key={i} className="relative">
                          <div className="absolute -left-6 w-3 h-3 rounded-full bg-[#0047AB] border-2 border-white" />
                          <div className="p-3 bg-zinc-50 border border-zinc-200">
                            <div className="flex justify-between items-start">
                              <span className="font-medium">{event.title}</span>
                              <Badge variant="outline">{event.date}</Badge>
                            </div>
                            {event.description && <p className="text-sm text-zinc-500 mt-1">{event.description}</p>}
                            {event.responsible_party && <p className="text-xs text-zinc-400 mt-1">Responsible: {event.responsible_party}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Milestones */}
              {contractTimeline.milestones?.length > 0 && (
                <div className="border border-zinc-200 bg-white brutal-shadow p-6">
                  <h3 className="font-bold uppercase text-sm mb-4">Milestones</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {contractTimeline.milestones.map((m, i) => (
                      <div key={i} className="p-4 border border-zinc-200 flex items-start gap-3">
                        <Flag className="text-[#FF4F00]" size={20} />
                        <div>
                          <p className="font-medium">{m.title}</p>
                          <p className="text-sm text-zinc-500">{m.date}</p>
                          {m.deliverable && <p className="text-xs text-zinc-400 mt-1">Deliverable: {m.deliverable}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Obligations */}
              {contractTimeline.obligations?.length > 0 && (
                <div className="border border-zinc-200 bg-white brutal-shadow p-6">
                  <h3 className="font-bold uppercase text-sm mb-4">Contractual Obligations</h3>
                  <div className="space-y-2">
                    {contractTimeline.obligations.map((o, i) => (
                      <div key={i} className="p-3 bg-amber-50 border border-amber-200 flex items-start gap-3">
                        <AlertCircle className="text-amber-600 flex-shrink-0" size={18} />
                        <div className="flex-1">
                          <div className="flex justify-between">
                            <p className="font-medium text-sm">{o.title}</p>
                            <span className="text-xs text-zinc-500">{o.date}</span>
                          </div>
                          {o.description && <p className="text-xs text-zinc-600 mt-1">{o.description}</p>}
                          {o.consequence && <p className="text-xs text-red-600 mt-1">Consequence: {o.consequence}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="works" className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-zinc-600">Project phases, activities, and execution schedule</p>
            <Button onClick={generateWorksTimeline} disabled={loading || !selectedContract} className="bg-[#0047AB] text-white">
              {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Workflow className="mr-2" size={16} />}
              Generate Works Timeline
            </Button>
          </div>

          {worksTimeline && (
            <div className="space-y-6">
              {/* Scope Summary */}
              {worksTimeline.scope_summary && (
                <div className="p-4 bg-emerald-50 border border-emerald-200">
                  <p className="text-sm">{worksTimeline.scope_summary}</p>
                </div>
              )}

              {/* Phases */}
              {worksTimeline.phases?.length > 0 && (
                <div className="border border-zinc-200 bg-white brutal-shadow p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold uppercase text-sm">Project Phases</h3>
                    <span className="text-xs text-zinc-400">Edit week numbers to adjust dates (changes cascade to subsequent phases)</span>
                  </div>
                  <div className="space-y-3">
                    {worksTimeline.phases.map((phase, i) => (
                      <div key={i} className="relative">
                        <div className="flex items-center gap-4 p-4 border border-zinc-200">
                          <div className="w-10 h-10 rounded-full bg-[#0047AB] text-white flex items-center justify-center font-bold">{i + 1}</div>
                          <div className="flex-1">
                            <p className="font-medium">{phase.name}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-zinc-500">Start:</span>
                              <input
                                type="number"
                                value={phase.start_week ?? ""}
                                className="w-16 text-sm border border-zinc-300 px-2 py-0.5 text-center hover:border-[#0047AB] focus:border-[#0047AB] focus:outline-none"
                                min="0"
                                onChange={(e) => {
                                  const val = parseInt(e.target.value) || 0;
                                  const diff = val - (phase.start_week || 0);
                                  setWorksTimeline(prev => ({
                                    ...prev,
                                    phases: prev.phases.map((p, j) => {
                                      if (j === i) return { ...p, start_week: val, end_week: (p.end_week || 0) + diff };
                                      if (j > i) return { ...p, start_week: (p.start_week || 0) + diff, end_week: (p.end_week || 0) + diff };
                                      return p;
                                    })
                                  }));
                                }}
                                data-testid={`phase-start-${i}`}
                              />
                              <span className="text-xs text-zinc-500">End:</span>
                              <input
                                type="number"
                                value={phase.end_week ?? ""}
                                className="w-16 text-sm border border-zinc-300 px-2 py-0.5 text-center hover:border-[#0047AB] focus:border-[#0047AB] focus:outline-none"
                                min={phase.start_week || 0}
                                onChange={(e) => {
                                  const val = parseInt(e.target.value) || 0;
                                  const diff = val - (phase.end_week || 0);
                                  setWorksTimeline(prev => ({
                                    ...prev,
                                    phases: prev.phases.map((p, j) => {
                                      if (j === i) return { ...p, end_week: val };
                                      if (j > i) return { ...p, start_week: (p.start_week || 0) + diff, end_week: (p.end_week || 0) + diff };
                                      return p;
                                    })
                                  }));
                                }}
                                data-testid={`phase-end-${i}`}
                              />
                              <span className="text-xs text-zinc-400 ml-1">weeks</span>
                            </div>
                            {phase.description && <p className="text-xs text-zinc-400 mt-1">{phase.description}</p>}
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold">{(phase.end_week || 0) - (phase.start_week || 0)} wks</p>
                          </div>
                        </div>
                        {/* Simple Gantt bar */}
                        <div className="h-2 bg-zinc-100 mt-1">
                          <div 
                            className="h-full bg-[#0047AB]" 
                            style={{ 
                              marginLeft: `${((phase.start_week || 0) / 52) * 100}%`, 
                              width: `${(((phase.end_week || 0) - (phase.start_week || 0)) / 52) * 100}%` 
                            }} 
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Activities */}
              {worksTimeline.activities?.length > 0 && (
                <div className="border border-zinc-200 bg-white brutal-shadow p-6">
                  <h3 className="font-bold uppercase text-sm mb-4">Key Activities</h3>
                  <div className="overflow-x-auto">
                    <table className="brutal-table text-sm">
                      <thead>
                        <tr><th>Activity</th><th>Phase</th><th>Start (Week)</th><th>Duration (Weeks)</th><th>Resources</th></tr>
                      </thead>
                      <tbody>
                        {worksTimeline.activities.slice(0, 15).map((act, i) => (
                          <tr key={i}>
                            <td className="font-medium">{act.name}</td>
                            <td>{act.phase_id}</td>
                            <td>
                              <input
                                type="number"
                                value={act.start_week ?? ""}
                                className="w-16 text-sm border border-zinc-300 px-2 py-0.5 text-center hover:border-[#0047AB] focus:border-[#0047AB] focus:outline-none"
                                min="0"
                                onChange={(e) => {
                                  const val = parseInt(e.target.value) || 0;
                                  setWorksTimeline(prev => ({
                                    ...prev,
                                    activities: prev.activities.map((a, j) =>
                                      j === i ? { ...a, start_week: val } : a
                                    )
                                  }));
                                }}
                                data-testid={`activity-start-${i}`}
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                value={act.duration_weeks ?? ""}
                                className="w-16 text-sm border border-zinc-300 px-2 py-0.5 text-center hover:border-[#0047AB] focus:border-[#0047AB] focus:outline-none"
                                min="1"
                                onChange={(e) => {
                                  const val = parseInt(e.target.value) || 1;
                                  setWorksTimeline(prev => ({
                                    ...prev,
                                    activities: prev.activities.map((a, j) =>
                                      j === i ? { ...a, duration_weeks: val } : a
                                    )
                                  }));
                                }}
                                data-testid={`activity-duration-${i}`}
                              />
                            </td>
                            <td>{act.resources}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Critical Activities */}
              {worksTimeline.critical_activities?.length > 0 && (
                <div className="border border-red-200 bg-red-50 p-4">
                  <h3 className="font-bold text-red-700 uppercase text-sm mb-2 flex items-center gap-2">
                    <AlertTriangle size={16} /> Critical Path Activities
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {worksTimeline.critical_activities.map((act, i) => (
                      <Badge key={i} className="bg-red-100 text-red-700 border-red-300">{act}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

const CopilotView = () => {
  const { user } = useAuth();
  const { copilotData, setCopilotData, resetCopilot } = useFormData();
  const [messages, setMessages] = useState(copilotData.messages || []);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [suggestedPrompts, setSuggestedPrompts] = useState([]);
  const [showPrompts, setShowPrompts] = useState(copilotData.messages?.length === 0);
  const [conversationContext, setConversationContext] = useState(copilotData.conversationContext || {});
  const messagesEndRef = useRef(null);

  // Sync with context
  useEffect(() => {
    setCopilotData({ messages, conversationContext });
  }, [messages, conversationContext]);

  useEffect(() => { 
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); 
  }, [messages]);

  useEffect(() => {
    fetchSuggestedPrompts();
  }, []);

  const handleReset = () => {
    setMessages([]);
    setConversationContext({});
    setShowPrompts(true);
    resetCopilot();
    toast.success("Conversation cleared");
  };

  const fetchSuggestedPrompts = async () => {
    try {
      const res = await axios.get(`${API}/copilot/suggested-prompts`);
      setSuggestedPrompts(res.data.prompts || []);
    } catch (e) { console.log("Failed to fetch prompts"); }
  };

  const handleSend = async (promptText = null) => {
    const messageText = promptText || input;
    if (!messageText.trim()) return;
    
    setMessages(prev => [...prev, { role: "user", content: messageText }]);
    setInput("");
    setLoading(true);
    setShowPrompts(false);
    
    try {
      const response = await axios.post(`${API}/copilot/chat`, { 
        message: messageText,
        context: conversationContext,
        conversation_history: messages.slice(-10)
      });
      
      const assistantMsg = {
        role: "assistant", 
        content: response.data.response,
        actions: response.data.suggested_actions,
        clauses: response.data.related_clauses,
        followups: response.data.suggested_followup_prompts
      };
      setMessages(prev => [...prev, assistantMsg]);
    } catch (error) { 
      toast.error("Failed to get response"); 
      setMessages(prev => [...prev, { role: "assistant", content: "I encountered an error. Please try again." }]);
    } finally { 
      setLoading(false); 
    }
  };

  const handlePromptClick = (prompt) => {
    setInput(prompt.prompt);
    handleSend(prompt.prompt);
  };

  const promptCategories = [...new Set(suggestedPrompts.map(p => p.category))];

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col" data-testid="copilot-view">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight uppercase font-['Chivo']">AI Copilot</h1>
          <p className="text-zinc-500 mt-1">Your intelligent contract assistant - ask anything about contracts, clauses, risks, or get recommendations</p>
        </div>
        <Button onClick={handleReset} variant="outline" className="border-2 border-zinc-300 hover:border-red-500 hover:bg-red-50 text-zinc-600 hover:text-red-600" data-testid="btn-reset-copilot">
          <RefreshCw size={16} className="mr-2" /> Reset
        </Button>
      </div>
      
      <div className="flex-1 border-2 border-black brutal-shadow-lg flex flex-col overflow-hidden bg-white">
        <div className={`h-1 ${loading ? 'tracing-beam' : 'bg-[#0047AB]'}`} />
        
        <ScrollArea className="flex-1 p-6">
          {messages.length === 0 && showPrompts && (
            <div className="space-y-6">
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-gradient-to-br from-[#0047AB] to-[#FF4F00] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Bot className="text-white" size={40} />
                </div>
                <h2 className="text-xl font-bold font-['Chivo'] mb-2">How can I help you today?</h2>
                <p className="text-zinc-500 max-w-md mx-auto">Select a suggested prompt below or type your own question about contracts, clauses, or project management.</p>
              </div>
              
              {promptCategories.map((category) => (
                <div key={category} className="space-y-3">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-400">{category.replace(/_/g, ' ')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {suggestedPrompts.filter(p => p.category === category).slice(0, 4).map((prompt) => (
                      <button
                        key={prompt.id}
                        onClick={() => handlePromptClick(prompt)}
                        className="text-left p-4 border border-zinc-200 hover:border-[#0047AB] hover:bg-blue-50 transition-all group"
                      >
                        <div className="flex items-start gap-3">
                          <Sparkles size={16} className="text-[#FF4F00] mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-sm group-hover:text-[#0047AB]">{prompt.title}</p>
                            <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{prompt.prompt}</p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {messages.map((msg, i) => (
            <div key={i} className={`mb-4 ${msg.role === 'user' ? 'text-right' : ''}`}>
              <div className={`inline-block max-w-[85%] p-4 ${msg.role === 'user' ? 'bg-black text-white' : 'bg-zinc-50 border border-zinc-200'}`}>
                <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
                
                {msg.actions && msg.actions.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-zinc-200">
                    <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Suggested Actions</p>
                    <div className="space-y-1">
                      {msg.actions.map((action, j) => (
                        <div key={j} className="flex items-center gap-2 text-xs">
                          <Badge className={`${action.priority === 'high' ? 'bg-red-100 text-red-700' : action.priority === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'} border-none text-xs`}>
                            {action.priority}
                          </Badge>
                          <span>{action.action}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {msg.followups && msg.followups.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-zinc-200">
                    <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Follow-up Questions</p>
                    <div className="flex flex-wrap gap-2">
                      {msg.followups.map((fu, j) => (
                        <button
                          key={j}
                          onClick={() => handleSend(fu.prompt)}
                          className="text-xs px-3 py-1 bg-white border border-zinc-300 hover:border-[#0047AB] hover:bg-blue-50"
                        >
                          {fu.title}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
          
          {loading && (
            <div className="flex items-center gap-2 p-4 bg-zinc-50 inline-block">
              <Loader2 className="animate-spin" size={16} />
              <span className="text-sm text-zinc-500">Analyzing...</span>
            </div>
          )}
          <div ref={messagesEndRef} />
        </ScrollArea>
        
        <div className="p-4 border-t bg-zinc-50">
          <div className="flex gap-3">
            <input 
              type="text" 
              value={input} 
              onChange={(e) => setInput(e.target.value)} 
              onKeyPress={(e) => e.key === 'Enter' && handleSend()} 
              placeholder="Ask about contracts, clauses, risks, changes..." 
              className="flex-1 px-4 py-3 border-2 border-zinc-200 focus:border-black bg-white" 
            />
            <Button onClick={() => handleSend()} disabled={loading} className="bg-black text-white px-6">
              <Send size={18} />
            </Button>
          </div>
          {messages.length > 0 && (
            <button 
              onClick={handleReset} 
              className="text-xs text-zinc-500 hover:text-zinc-700 mt-2"
              data-testid="btn-clear-copilot"
            >
              Clear conversation & show suggestions
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const ContractsView = ({ contracts, setActiveView, setSelectedContract }) => {
  const csvData = contracts.map(c => ({ project: c.project_name, framework: c.framework, status: c.status || 'draft', risk: c.risk_score, source: c.source || 'generated' }));
  return (
    <div className="space-y-8" data-testid="contracts-view" data-export-region>
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-black tracking-tight uppercase font-['Chivo'] mb-2">My Contracts</h1>
        <ExportToolbar title="My Contracts" pdfFilename="my-contracts" csvData={csvData} csvFilename="contracts" />
      </div>
      {contracts.length === 0 ? (
        <div className="border border-dashed border-zinc-300 bg-zinc-50 p-12 text-center"><FileText className="mx-auto mb-4 text-zinc-400" size={48} /><p className="text-zinc-500">No contracts yet</p></div>
      ) : (
        <div className="border border-zinc-200 bg-white brutal-shadow overflow-hidden">
          <table className="brutal-table">
            <thead><tr><th>Project</th><th>Framework</th><th>Source</th><th>Status</th><th>Risk</th><th>Actions</th></tr></thead>
            <tbody>
              {contracts.map((c, i) => (
                <tr key={i}>
                  <td className="font-medium">{c.project_name}</td>
                  <td><Badge variant="outline">{c.framework}</Badge></td>
                  <td><Badge className={`${c.source === 'imported' ? 'bg-[#0047AB]' : 'bg-zinc-500'} text-white border-none text-[10px]`}>{c.source === 'imported' ? 'Imported' : `v${c.version || 1}`}</Badge></td>
                  <td><Badge className={`${c.status === 'approved' ? 'bg-emerald-500' : c.status === 'rejected' ? 'bg-red-500' : c.status === 'imported' ? 'bg-[#0047AB]' : 'bg-amber-500'} text-white border-none`}>{c.status || 'draft'}</Badge></td>
                  <td><span className={`font-bold ${c.risk_score > 70 ? 'text-[#FF4F00]' : 'text-emerald-500'}`}>{c.risk_score}</span></td>
                  <td>
                    <div className="flex gap-2">
                      <button onClick={() => { setSelectedContract(c.id); setActiveView("contract-detail"); }} className="text-[#0047AB] hover:underline text-sm flex items-center gap-1"><Eye size={14} /> View</button>
                      <a href={`${API}/contracts/download/${c.filename}`} className="text-zinc-500 hover:text-zinc-700"><Download size={14} /></a>
                      <a href={`${API}/contracts/${c.id}/pdf`} className="text-zinc-500 hover:text-zinc-700"><FileDown size={14} /></a>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Project Controls View
const ProjectControlsView = ({ contracts }) => {
  const { user } = useAuth();
  const [selectedContract, setSelectedContract] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [summary, setSummary] = useState(null);
  const [kpis, setKpis] = useState([]);
  const [risks, setRisks] = useState([]);
  const [changeOrders, setChangeOrders] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [evm, setEvm] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [allLessons, setAllLessons] = useState([]);
  const [userClauses, setUserClauses] = useState([]);
  const [aiRecommendations, setAiRecommendations] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAddRisk, setShowAddRisk] = useState(false);
  const [showAddChangeOrder, setShowAddChangeOrder] = useState(false);
  const [showAddMilestone, setShowAddMilestone] = useState(false);
  const [showAddLesson, setShowAddLesson] = useState(false);

  useEffect(() => {
    if (selectedContract) {
      fetchControlsData();
    }
  }, [selectedContract]);

  useEffect(() => {
    fetchAllLessons();
    if (user) fetchUserClauses();
  }, [user]);

  const fetchControlsData = async () => {
    setLoading(true);
    try {
      const [summaryRes, kpisRes, risksRes, cosRes, msRes, evmRes, lessonsRes] = await Promise.all([
        axios.get(`${API}/controls/${selectedContract}/summary`),
        axios.get(`${API}/controls/${selectedContract}/kpis`),
        axios.get(`${API}/controls/${selectedContract}/risks`),
        axios.get(`${API}/controls/${selectedContract}/change-orders`),
        axios.get(`${API}/controls/${selectedContract}/milestones`),
        axios.get(`${API}/controls/${selectedContract}/evm`),
        axios.get(`${API}/controls/${selectedContract}/lessons`)
      ]);
      setSummary(summaryRes.data);
      setKpis(kpisRes.data);
      setRisks(risksRes.data);
      setChangeOrders(cosRes.data);
      setMilestones(msRes.data);
      setEvm(evmRes.data);
      setLessons(lessonsRes.data);
    } catch (error) {
      console.error("Controls fetch error:", error);
      toast.error("Failed to load controls data");
    } finally {
      setLoading(false);
    }
    
    // Fetch AI recommendations separately to not block main data
    try {
      const aiRes = await axios.get(`${API}/controls/${selectedContract}/ai-recommendations`, { timeout: 15000 });
      setAiRecommendations(aiRes.data);
    } catch (e) { console.log("AI recommendations unavailable"); }
  };

  const fetchAllLessons = async () => {
    try {
      const res = await axios.get(`${API}/lessons`);
      setAllLessons(res.data);
    } catch (e) { console.log("Failed to fetch lessons"); }
  };

  const fetchUserClauses = async () => {
    try {
      const res = await axios.get(`${API}/user-clauses`);
      setUserClauses(res.data);
    } catch (e) { console.log("Failed to fetch user clauses"); }
  };

  const initializeKpis = async () => {
    try {
      await axios.post(`${API}/controls/${selectedContract}/kpis/initialize`);
      fetchControlsData();
      toast.success("KPIs initialized");
    } catch (e) { toast.error("Failed to initialize KPIs"); }
  };

  const updateKpi = async (kpiId, actual, planned, greenThreshold, amberThreshold, greenOperator, amberOperator) => {
    try {
      const payload = {};
      if (actual !== undefined && actual !== null) payload.actual = actual;
      if (planned !== undefined && planned !== null) payload.planned = planned;
      if (greenThreshold !== undefined && greenThreshold !== null) payload.green_threshold = greenThreshold;
      if (amberThreshold !== undefined && amberThreshold !== null) payload.amber_threshold = amberThreshold;
      if (greenOperator !== undefined && greenOperator !== null) payload.green_operator = greenOperator;
      if (amberOperator !== undefined && amberOperator !== null) payload.amber_operator = amberOperator;
      await axios.put(`${API}/controls/${selectedContract}/kpis/${kpiId}`, payload);
      fetchControlsData();
      toast.success("KPI updated");
    } catch (e) { toast.error("Failed to update KPI"); }
  };

  const updateEvm = async (pv, ev, ac) => {
    try {
      await axios.put(`${API}/controls/${selectedContract}/evm`, { planned_value: pv, earned_value: ev, actual_cost: ac });
      fetchControlsData();
      toast.success("EVM updated");
    } catch (e) { toast.error("Failed to update EVM"); }
  };

  const kpiCategories = ["financial", "schedule", "risk", "quality", "performance", "contract"];

  const getTrendIcon = (trend) => {
    if (trend === "up") return <ArrowUp size={14} className="text-emerald-500" />;
    if (trend === "down") return <ArrowDown size={14} className="text-red-500" />;
    return <Minus size={14} className="text-zinc-400" />;
  };

  const getStatusColor = (status) => {
    if (status === "on_track") return "bg-emerald-500";
    if (status === "at_risk") return "bg-amber-500";
    return "bg-red-500";
  };

  // Overview Tab
  const OverviewTab = () => (
    <div className="space-y-6">
      {summary && (
        <>
          {/* Health Score Card */}
          <div className={`border-2 p-6 ${summary.overall_health === 'healthy' ? 'border-emerald-500 bg-emerald-50' : summary.overall_health === 'at_risk' ? 'border-amber-500 bg-amber-50' : 'border-red-500 bg-red-50'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Overall Project Health</p>
                <p className="text-5xl font-black font-['Chivo']">{summary.health_score}</p>
                <Badge className={`mt-2 ${getStatusColor(summary.overall_health === 'healthy' ? 'on_track' : summary.overall_health === 'at_risk' ? 'at_risk' : 'critical')} text-white border-none uppercase`}>{summary.overall_health}</Badge>
              </div>
              <Activity size={64} className={`${summary.overall_health === 'healthy' ? 'text-emerald-500' : summary.overall_health === 'at_risk' ? 'text-amber-500' : 'text-red-500'}`} />
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bento-item p-4">
              <p className="text-xs uppercase text-zinc-500 flex items-center gap-0.5">KPIs <InfoTip text="Key Performance Indicators tracking project health across financial, schedule, quality, and risk dimensions." /></p>
              <p className="text-2xl font-black">{summary.kpi_summary.total}</p>
              <div className="flex gap-2 mt-2 text-xs">
                <span className="text-emerald-500">{summary.kpi_summary.on_track} OK</span>
                <span className="text-amber-500">{summary.kpi_summary.at_risk} Risk</span>
                <span className="text-red-500">{summary.kpi_summary.critical} Crit</span>
              </div>
            </div>
            <div className="bento-item p-4">
              <p className="text-xs uppercase text-zinc-500 flex items-center gap-0.5">Open Risks <InfoTip text="Number of active risks that have not yet been fully mitigated or closed." /></p>
              <p className="text-2xl font-black text-[#FF4F00]">{summary.risk_summary.open}</p>
              <p className="text-xs text-zinc-500 mt-2">{summary.risk_summary.high_severity} high severity</p>
            </div>
            <div className="bento-item p-4">
              <p className="text-xs uppercase text-zinc-500 flex items-center gap-0.5">Milestones <InfoTip text="Project milestone completion rate. Tracks progress against planned achievement dates." /></p>
              <p className="text-2xl font-black">{summary.milestone_summary.completion_rate}%</p>
              <p className="text-xs text-zinc-500 mt-2">{summary.milestone_summary.completed}/{summary.milestone_summary.total} complete</p>
            </div>
            <div className="bento-item p-4">
              <p className="text-xs uppercase text-zinc-500 flex items-center gap-0.5">Change Orders <InfoTip text="Pending change orders and their total cost impact on the project budget." /></p>
              <p className="text-2xl font-black">{summary.change_order_summary.pending}</p>
              <p className="text-xs text-zinc-500 mt-2">${(summary.change_order_summary.total_cost_impact || 0).toLocaleString()} impact</p>
            </div>
          </div>

          {/* EVM Summary */}
          {summary.evm_summary && Object.keys(summary.evm_summary).length > 0 && (
            <div className="border border-zinc-200 bg-white brutal-shadow p-6">
              <h3 className="font-bold font-['Chivo'] uppercase tracking-wide mb-4 flex items-center gap-1">Earned Value Summary <InfoTip text="Earned Value Management (EVM) integrates scope, schedule, and cost data. CPI and SPI should be >= 1.0 for healthy projects." /></h3>
              <div className="grid grid-cols-5 gap-4">
                <div className="text-center p-3 bg-zinc-50">
                  <p className="text-xs uppercase text-zinc-500 flex items-center justify-center gap-0.5">CPI <InfoTip text="Cost Performance Index = EV/AC. Values > 1.0 indicate under budget." /></p>
                  <p className={`text-2xl font-black ${summary.evm_summary.cpi >= 1 ? 'text-emerald-500' : 'text-red-500'}`}>{summary.evm_summary.cpi?.toFixed(2)}</p>
                </div>
                <div className="text-center p-3 bg-zinc-50">
                  <p className="text-xs uppercase text-zinc-500 flex items-center justify-center gap-0.5">SPI <InfoTip text="Schedule Performance Index = EV/PV. Values > 1.0 indicate ahead of schedule." /></p>
                  <p className={`text-2xl font-black ${summary.evm_summary.spi >= 1 ? 'text-emerald-500' : 'text-red-500'}`}>{summary.evm_summary.spi?.toFixed(2)}</p>
                </div>
                <div className="text-center p-3 bg-zinc-50">
                  <p className="text-xs uppercase text-zinc-500 flex items-center justify-center gap-0.5">% Complete <InfoTip text="Percentage of total project scope completed based on earned value." /></p>
                  <p className="text-2xl font-black">{summary.evm_summary.percent_complete?.toFixed(1)}%</p>
                </div>
                <div className="text-center p-3 bg-zinc-50">
                  <p className="text-xs uppercase text-zinc-500 flex items-center justify-center gap-0.5">Cost Var <InfoTip text="Cost Variance = EV - AC. Positive means under budget, negative means over budget." /></p>
                  <p className={`text-2xl font-black ${summary.evm_summary.cost_variance >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>${(summary.evm_summary.cost_variance || 0).toLocaleString()}</p>
                </div>
                <div className="text-center p-3 bg-zinc-50">
                  <p className="text-xs uppercase text-zinc-500 flex items-center justify-center gap-0.5">Schedule Var <InfoTip text="Schedule Variance = EV - PV. Positive means ahead of schedule, negative means behind." /></p>
                  <p className={`text-2xl font-black ${summary.evm_summary.schedule_variance >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>${(summary.evm_summary.schedule_variance || 0).toLocaleString()}</p>
                </div>
              </div>
            </div>
          )}

          {/* AI Recommendations */}
          {aiRecommendations && (
            <div className="border-2 border-[#0047AB] bg-blue-50 p-6">
              <h3 className="font-bold font-['Chivo'] uppercase tracking-wide mb-4 flex items-center gap-2 text-[#0047AB]"><Lightbulb size={20} /> AI Recommendations</h3>
              <p className="text-sm whitespace-pre-wrap">{aiRecommendations.recommendations}</p>
              {aiRecommendations.relevant_lessons?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-[#0047AB]/30">
                  <p className="text-xs uppercase text-[#0047AB] mb-2">Related Lessons Learned</p>
                  <div className="flex flex-wrap gap-2">
                    {aiRecommendations.relevant_lessons.map((l, i) => (
                      <Badge key={i} variant="outline" className="border-[#0047AB]">{l.title}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );

  // KPIs Tab
  const KPIsTab = () => {
    const [manualKpi, setManualKpi] = useState({ name: "", category: "financial", actual: "", planned: "", unit: "", green_threshold: "0.95", amber_threshold: "0.80", green_operator: ">=", amber_operator: ">=" });
    const [showManualInput, setShowManualInput] = useState(false);
    const [kpiHistory, setKpiHistory] = useState({});
    
    useEffect(() => {
      if (selectedContract) {
        axios.get(`${API}/controls/${selectedContract}/kpis/history`).then(r => setKpiHistory(r.data || {})).catch(() => {});
      }
    }, [selectedContract, kpis]);
    
    const addManualKpi = async () => {
      if (!manualKpi.name || !manualKpi.planned) { toast.error("Name and Planned value are required"); return; }
      try {
        await axios.post(`${API}/controls/${selectedContract}/kpis`, {
          name: manualKpi.name,
          category: manualKpi.category,
          actual: parseFloat(manualKpi.actual) || 0,
          planned: parseFloat(manualKpi.planned) || 0,
          unit: manualKpi.unit || "",
          green_threshold: parseFloat(manualKpi.green_threshold) || 0.95,
          amber_threshold: parseFloat(manualKpi.amber_threshold) || 0.80,
          green_operator: manualKpi.green_operator || ">=",
          amber_operator: manualKpi.amber_operator || ">="
        });
        setManualKpi({ name: "", category: "financial", actual: "", planned: "", unit: "", green_threshold: "0.95", amber_threshold: "0.80", green_operator: ">=", amber_operator: ">=" });
        fetchControlsData();
        toast.success("KPI added");
      } catch (e) { toast.error("Failed to add KPI"); }
    };
    
    return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-1">
          <h3 className="font-bold font-['Chivo'] uppercase">Key Performance Indicators</h3>
          <InfoTip text="KPIs measure project health across financial, schedule, risk, quality, performance, and contract dimensions. Enter your planned (target) values and actual values for each KPI to track progress." />
        </div>
        <div className="flex gap-2">
          {user && kpis.length === 0 && (
            <Button onClick={initializeKpis} className="bg-black text-white" data-testid="btn-init-kpis">Initialize Default KPIs</Button>
          )}
          {user && (
            <Button onClick={() => setShowManualInput(!showManualInput)} variant="outline" className="border-2" data-testid="btn-add-kpi">
              <Plus size={16} className="mr-1" /> Add KPI
            </Button>
          )}
        </div>
      </div>

      {/* KPI Input Prompt when empty */}
      {kpis.length === 0 && !showManualInput && (
        <div className="border-2 border-dashed border-zinc-300 p-8 text-center bg-zinc-50">
          <Target size={48} className="mx-auto mb-4 text-zinc-300" />
          <h4 className="font-bold text-lg mb-2">No KPIs configured yet</h4>
          <p className="text-zinc-500 mb-4 max-w-md mx-auto text-sm">
            Add your project KPIs to track performance. You can either <strong>initialize default KPIs</strong> (financial, schedule, risk, quality) or <strong>manually add</strong> specific KPIs for your project.
          </p>
          <div className="flex gap-3 justify-center">
            <Button onClick={initializeKpis} className="bg-black text-white" data-testid="btn-init-kpis-prompt">
              <Lightbulb size={16} className="mr-2" /> Initialize Defaults
            </Button>
            <Button onClick={() => setShowManualInput(true)} variant="outline" className="border-2" data-testid="btn-manual-kpi-prompt">
              <Plus size={16} className="mr-2" /> Add Manually
            </Button>
          </div>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3 max-w-lg mx-auto text-left">
            <div className="p-3 bg-white border border-zinc-200 text-xs">
              <p className="font-bold text-zinc-600">Financial KPIs</p>
              <p className="text-zinc-400">CPI, Budget Variance, Cost at Completion</p>
            </div>
            <div className="p-3 bg-white border border-zinc-200 text-xs">
              <p className="font-bold text-zinc-600">Schedule KPIs</p>
              <p className="text-zinc-400">SPI, Milestone Completion, Critical Path</p>
            </div>
            <div className="p-3 bg-white border border-zinc-200 text-xs">
              <p className="font-bold text-zinc-600">Quality KPIs</p>
              <p className="text-zinc-400">NCR Rate, Rework %, First Pass Yield</p>
            </div>
          </div>
        </div>
      )}

      {/* Manual KPI Input Form */}
      {showManualInput && (
        <div className="border-2 border-black bg-white brutal-shadow p-6" data-testid="manual-kpi-form">
          <h4 className="font-bold uppercase text-sm mb-1">Add Custom KPI</h4>
          <p className="text-xs text-zinc-500 mb-4">KPI Value is auto-calculated as Actual / Planned. Set permissible thresholds to define RAG status.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-bold uppercase text-zinc-500 block mb-1 flex items-center">KPI Name * <InfoTip text="A descriptive name for this KPI." /></label>
              <input type="text" value={manualKpi.name} onChange={e => setManualKpi(p => ({...p, name: e.target.value}))} className="w-full border-2 border-zinc-200 p-2 text-sm" placeholder="e.g., Schedule Variance" data-testid="kpi-name-input" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-zinc-500 block mb-1 flex items-center">Category <InfoTip text="Group your KPI into a category for organized tracking." /></label>
              <select value={manualKpi.category} onChange={e => setManualKpi(p => ({...p, category: e.target.value}))} className="w-full border-2 border-zinc-200 p-2 text-sm">
                <option value="financial">Financial</option>
                <option value="schedule">Schedule</option>
                <option value="risk">Risk</option>
                <option value="quality">Quality</option>
                <option value="performance">Performance</option>
                <option value="contract">Contract</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-zinc-500 block mb-1 flex items-center">Unit <InfoTip text="Unit of measurement: %, $, days, ratio, etc." /></label>
              <input type="text" value={manualKpi.unit} onChange={e => setManualKpi(p => ({...p, unit: e.target.value}))} className="w-full border-2 border-zinc-200 p-2 text-sm" placeholder="e.g., %, $, days" />
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
            <div>
              <label className="text-xs font-bold text-[#FF4F00] uppercase block mb-1 flex items-center">Actual <InfoTip text="The current measured value for this KPI." /></label>
              <input type="text" inputMode="decimal" value={manualKpi.actual} onChange={e => setManualKpi(p => ({...p, actual: e.target.value}))} className="w-full border-2 border-[#FF4F00] p-2 text-sm" placeholder="e.g., 85" data-testid="kpi-actual-input" />
            </div>
            <div>
              <label className="text-xs font-bold text-[#0047AB] uppercase block mb-1 flex items-center">Planned * <InfoTip text="The planned/target value. KPI = Actual / Planned." /></label>
              <input type="text" inputMode="decimal" value={manualKpi.planned} onChange={e => setManualKpi(p => ({...p, planned: e.target.value}))} className="w-full border-2 border-[#0047AB] p-2 text-sm" placeholder="e.g., 100" data-testid="kpi-planned-input" />
            </div>
            <div>
              <label className="text-xs font-bold text-emerald-600 uppercase block mb-1 flex items-center">Green Threshold <InfoTip text="Define the operator and value for GREEN status." /></label>
              <div className="flex gap-1">
                <select value={manualKpi.green_operator} onChange={e => setManualKpi(p => ({...p, green_operator: e.target.value}))} className="border-2 border-emerald-300 p-2 text-sm w-16" data-testid="kpi-green-operator">
                  <option value=">=">{'>='}</option>
                  <option value="<=">{'<='}</option>
                  <option value=">">{'>'}</option>
                  <option value="<">{'<'}</option>
                  <option value="=">{'='}</option>
                </select>
                <input type="text" inputMode="decimal" value={manualKpi.green_threshold} onChange={e => setManualKpi(p => ({...p, green_threshold: e.target.value}))} className="flex-1 border-2 border-emerald-300 p-2 text-sm" placeholder="0.95" data-testid="kpi-green-input" />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-amber-600 uppercase block mb-1 flex items-center">Amber Threshold <InfoTip text="Define the operator and value for AMBER status. Values not matching Green or Amber = RED." /></label>
              <div className="flex gap-1">
                <select value={manualKpi.amber_operator} onChange={e => setManualKpi(p => ({...p, amber_operator: e.target.value}))} className="border-2 border-amber-300 p-2 text-sm w-16" data-testid="kpi-amber-operator">
                  <option value=">=">{'>='}</option>
                  <option value="<=">{'<='}</option>
                  <option value=">">{'>'}</option>
                  <option value="<">{'<'}</option>
                  <option value="=">{'='}</option>
                </select>
                <input type="text" inputMode="decimal" value={manualKpi.amber_threshold} onChange={e => setManualKpi(p => ({...p, amber_threshold: e.target.value}))} className="flex-1 border-2 border-amber-300 p-2 text-sm" placeholder="0.80" data-testid="kpi-amber-input" />
              </div>
            </div>
          </div>
          {manualKpi.actual && manualKpi.planned && parseFloat(manualKpi.planned) !== 0 && (
            <div className="mt-3 p-3 bg-zinc-50 border border-zinc-200 text-sm">
              <span className="text-zinc-500">Preview: KPI = </span>
              <span className="font-bold">{(parseFloat(manualKpi.actual) / parseFloat(manualKpi.planned)).toFixed(4)}</span>
              <span className="text-zinc-500 ml-2">| Status: </span>
              {(() => {
                const v = parseFloat(manualKpi.actual) / parseFloat(manualKpi.planned);
                const g = parseFloat(manualKpi.green_threshold) || 0.95;
                const a = parseFloat(manualKpi.amber_threshold) || 0.80;
                const gOp = manualKpi.green_operator || ">=";
                const aOp = manualKpi.amber_operator || ">=";
                const cmp = (val, thr, op) => {
                  if (op === ">=") return val >= thr;
                  if (op === "<=") return val <= thr;
                  if (op === ">") return val > thr;
                  if (op === "<") return val < thr;
                  if (op === "=") return Math.abs(val - thr) < 0.0001;
                  return val >= thr;
                };
                if (cmp(v, g, gOp)) return <span className="font-bold text-emerald-600">GREEN</span>;
                if (cmp(v, a, aOp)) return <span className="font-bold text-amber-600">AMBER</span>;
                return <span className="font-bold text-red-600">RED</span>;
              })()}
            </div>
          )}
          <div className="flex gap-2 mt-4">
            <Button onClick={addManualKpi} className="bg-black text-white" data-testid="btn-save-kpi">Save KPI</Button>
            <Button onClick={() => setShowManualInput(false)} variant="outline" className="border-2">Cancel</Button>
          </div>
        </div>
      )}
      
      {kpiCategories.map(category => {
        const categoryKpis = kpis.filter(k => k.category === category);
        if (categoryKpis.length === 0) return null;
        const catInfo = KPI_INFO[category];
        
        return (
          <div key={category} className="border border-zinc-200 bg-white brutal-shadow">
            <div className="p-4 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between">
              <div className="flex items-center gap-1">
                <h4 className="font-bold uppercase text-sm tracking-wider">{category} KPIs</h4>
                {catInfo?._category && <InfoTip text={catInfo._category} />}
              </div>
              <Badge variant="outline" className="text-xs">{categoryKpis.length} indicators</Badge>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categoryKpis.map(kpi => {
                  const kpiInfo = catInfo?.[kpi.name];
                  const displayValue = kpi.value !== undefined ? kpi.value : 0;
                  const statusLabel = kpi.status === 'on_track' ? 'GREEN' : kpi.status === 'at_risk' ? 'AMBER' : 'RED';
                  const statusBg = kpi.status === 'on_track' ? 'bg-emerald-500' : kpi.status === 'at_risk' ? 'bg-amber-500' : 'bg-red-500';
                  return (
                  <div key={kpi.id} className="p-4 border border-zinc-200 hover:border-zinc-400 transition-colors" data-testid={`kpi-card-${kpi.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-1 flex-1 min-w-0">
                        <p className="text-xs uppercase text-zinc-500 truncate">{kpi.name}</p>
                        {kpiInfo && <InfoTip text={kpiInfo} />}
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {kpiHistory[kpi.id] && <KPISparkline data={kpiHistory[kpi.id]} />}
                        {getTrendIcon(kpi.trend)}
                        <span className={`px-1.5 py-0.5 text-[9px] font-bold text-white ${statusBg}`}>{statusLabel}</span>
                      </div>
                    </div>
                    {/* Calculated KPI Value */}
                    <div className="mb-1">
                      <p className="text-2xl font-black font-['Chivo']">
                        {kpi.unit === "$" ? `$${displayValue.toLocaleString()}` : typeof displayValue === 'number' ? displayValue.toFixed(displayValue % 1 === 0 ? 0 : 2) : displayValue}
                        {kpi.unit && kpi.unit !== "$" && <span className="text-sm text-zinc-400 ml-1">{kpi.unit}</span>}
                      </p>
                      <p className="text-[10px] text-zinc-400">= Actual / Planned</p>
                    </div>
                    {/* Actual & Planned row */}
                    <div className="flex gap-3 text-xs text-zinc-500 mb-1">
                      <span>Actual: <strong className="text-[#FF4F00]">{kpi.actual ?? 0}</strong></span>
                      <span>Planned: <strong className="text-[#0047AB]">{kpi.planned ?? kpi.target ?? 0}</strong></span>
                    </div>
                    {/* Threshold badges */}
                    <div className="flex gap-2 text-[10px] text-zinc-400 mb-2">
                      <span className="flex items-center gap-0.5"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />{kpi.green_operator || '>='}{kpi.green_threshold ?? 0.95}</span>
                      <span className="flex items-center gap-0.5"><span className="w-2 h-2 rounded-full bg-amber-500 inline-block" />{kpi.amber_operator || '>='}{kpi.amber_threshold ?? 0.80}</span>
                      <span className="flex items-center gap-0.5"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" />otherwise</span>
                    </div>
                    {user && (
                      <div className="pt-3 border-t border-zinc-100 space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] font-bold text-[#FF4F00] uppercase block mb-0.5">Actual</label>
                            <input type="text" inputMode="decimal" defaultValue={kpi.actual ?? 0} onBlur={(e) => { const v = parseFloat(e.target.value); if (!isNaN(v)) updateKpi(kpi.id, v); }} className="w-full text-xs p-1.5 border-2 border-[#FF4F00]/30 focus:border-[#FF4F00] outline-none" placeholder="Actual" data-testid={`kpi-actual-${kpi.id}`} />
                          </div>
                          <div>
                            <label className="text-[10px] font-bold text-[#0047AB] uppercase block mb-0.5">Planned</label>
                            <input type="text" inputMode="decimal" defaultValue={kpi.planned ?? kpi.target ?? 0} onBlur={(e) => { const v = parseFloat(e.target.value); if (!isNaN(v)) updateKpi(kpi.id, undefined, v); }} className="w-full text-xs p-1.5 border-2 border-[#0047AB]/30 focus:border-[#0047AB] outline-none" placeholder="Planned" data-testid={`kpi-planned-${kpi.id}`} />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] font-bold text-emerald-600 uppercase block mb-0.5 flex items-center">Green <InfoTip text="Define operator and threshold for GREEN (On Track) status." /></label>
                            <div className="flex gap-1">
                              <select defaultValue={kpi.green_operator || ">="} onChange={(e) => updateKpi(kpi.id, undefined, undefined, undefined, undefined, e.target.value)} className="text-xs p-1.5 border-2 border-emerald-200 focus:border-emerald-500 outline-none w-12" data-testid={`kpi-green-op-${kpi.id}`}>
                                <option value=">=">{'>='}</option>
                                <option value="<=">{'<='}</option>
                                <option value=">">{'>'}</option>
                                <option value="<">{'<'}</option>
                                <option value="=">{'='}</option>
                              </select>
                              <input type="text" inputMode="decimal" defaultValue={kpi.green_threshold ?? 0.95} onBlur={(e) => { const v = parseFloat(e.target.value); if (!isNaN(v)) updateKpi(kpi.id, undefined, undefined, v); }} className="flex-1 text-xs p-1.5 border-2 border-emerald-200 focus:border-emerald-500 outline-none" placeholder="0.95" data-testid={`kpi-green-${kpi.id}`} />
                            </div>
                          </div>
                          <div>
                            <label className="text-[10px] font-bold text-amber-600 uppercase block mb-0.5 flex items-center">Amber <InfoTip text="Define operator and threshold for AMBER (At Risk) status. Anything else = RED." /></label>
                            <div className="flex gap-1">
                              <select defaultValue={kpi.amber_operator || ">="} onChange={(e) => updateKpi(kpi.id, undefined, undefined, undefined, undefined, undefined, e.target.value)} className="text-xs p-1.5 border-2 border-amber-200 focus:border-amber-500 outline-none w-12" data-testid={`kpi-amber-op-${kpi.id}`}>
                                <option value=">=">{'>='}</option>
                                <option value="<=">{'<='}</option>
                                <option value=">">{'>'}</option>
                                <option value="<">{'<'}</option>
                                <option value="=">{'='}</option>
                              </select>
                              <input type="text" inputMode="decimal" defaultValue={kpi.amber_threshold ?? 0.80} onBlur={(e) => { const v = parseFloat(e.target.value); if (!isNaN(v)) updateKpi(kpi.id, undefined, undefined, undefined, v); }} className="flex-1 text-xs p-1.5 border-2 border-amber-200 focus:border-amber-500 outline-none" placeholder="0.80" data-testid={`kpi-amber-${kpi.id}`} />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );};

  // S-Curves & Trends Tab
  const SCurvesTab = () => {
    const canvasRef = React.useRef(null);
    const [pvInput, setPvInput] = useState(evm?.planned_value?.toString() || "");
    const [evInput, setEvInput] = useState(evm?.earned_value?.toString() || "");
    const [acInput, setAcInput] = useState(evm?.actual_cost?.toString() || "");

    const drawSCurve = React.useCallback(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      const w = canvas.width = canvas.offsetWidth * 2;
      const h = canvas.height = canvas.offsetHeight * 2;
      ctx.scale(2, 2);
      const cw = w/2, ch = h/2;
      const pad = { t: 30, r: 20, b: 45, l: 65 };
      const pw = cw - pad.l - pad.r, ph = ch - pad.t - pad.b;
      
      ctx.clearRect(0, 0, cw, ch);
      ctx.fillStyle = '#fafafa'; ctx.fillRect(0, 0, cw, ch);
      
      const pv = parseFloat(pvInput) || evm?.planned_value || 0;
      const ev = parseFloat(evInput) || evm?.earned_value || 0;
      const ac = parseFloat(acInput) || evm?.actual_cost || 0;
      const bac = Math.max(pv * 2, ev * 2, ac * 2, 100);
      const maxVal = bac * 1.1;
      const months = 12;
      
      // Grid
      ctx.strokeStyle = '#e4e4e7'; ctx.lineWidth = 0.5;
      for (let i = 0; i <= 5; i++) {
        const y = pad.t + (ph / 5) * i;
        ctx.beginPath(); ctx.moveTo(pad.l, y); ctx.lineTo(pad.l + pw, y); ctx.stroke();
        ctx.fillStyle = '#71717a'; ctx.font = '9px monospace'; ctx.textAlign = 'right';
        const val = maxVal * (1 - i/5);
        const label = val >= 1e6 ? `$${(val/1e6).toFixed(1)}M` : val >= 1e3 ? `$${(val/1e3).toFixed(0)}K` : `$${val.toFixed(0)}`;
        ctx.fillText(label, pad.l - 5, y + 3);
      }
      for (let i = 0; i <= months; i++) {
        const x = pad.l + (pw / months) * i;
        ctx.beginPath(); ctx.moveTo(x, pad.t); ctx.lineTo(x, pad.t + ph); ctx.stroke();
        ctx.fillStyle = '#71717a'; ctx.font = '9px monospace'; ctx.textAlign = 'center';
        ctx.fillText(`M${i}`, x, pad.t + ph + 15);
      }

      const genCurve = (total, noise = 0) => {
        const pts = [];
        for (let i = 0; i <= months; i++) {
          const t = i / months;
          const s = 1 / (1 + Math.exp(-10 * (t - 0.5)));
          pts.push(total * s * (1 + noise * (Math.sin(i * 1.7) * 0.05)));
        }
        return pts;
      };

      const drawLine = (pts, color, dash = []) => {
        ctx.strokeStyle = color; ctx.lineWidth = 2.5; ctx.setLineDash(dash);
        ctx.beginPath();
        pts.forEach((v, i) => {
          const x = pad.l + (pw / months) * i;
          const y = pad.t + ph - (v / maxVal) * ph;
          if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        });
        ctx.stroke();
        ctx.setLineDash([]);
        // Draw dots
        pts.forEach((v, i) => {
          const x = pad.l + (pw / months) * i;
          const y = pad.t + ph - (v / maxVal) * ph;
          ctx.fillStyle = color; ctx.beginPath(); ctx.arc(x, y, 2.5, 0, Math.PI * 2); ctx.fill();
        });
      };

      drawLine(genCurve(bac), '#0047AB');
      drawLine(genCurve(ev, 0.02), '#10b981');
      drawLine(genCurve(ac, 0.04), '#FF4F00', [4, 2]);

      // Legend
      [{ c: '#0047AB', l: 'BCWS (PV)', d: [] }, { c: '#10b981', l: 'BCWP (EV)', d: [] }, { c: '#FF4F00', l: 'ACWP (AC)', d: [4,2] }].forEach((item, i) => {
        const x = pad.l + i * 140;
        ctx.strokeStyle = item.c; ctx.lineWidth = 2; ctx.setLineDash(item.d);
        ctx.beginPath(); ctx.moveTo(x, ch - 15); ctx.lineTo(x + 20, ch - 15); ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = '#52525b'; ctx.font = '9px sans-serif'; ctx.textAlign = 'left';
        ctx.fillText(item.l, x + 25, ch - 11);
      });

      ctx.fillStyle = '#18181b'; ctx.font = 'bold 11px sans-serif'; ctx.textAlign = 'left';
      ctx.fillText('Earned Value S-Curve', pad.l, 15);
    }, [pvInput, evInput, acInput, evm]);

    React.useEffect(() => { drawSCurve(); }, [drawSCurve]);

    const pv = parseFloat(pvInput) || evm?.planned_value || 0;
    const ev = parseFloat(evInput) || evm?.earned_value || 0;
    const ac = parseFloat(acInput) || evm?.actual_cost || 0;
    const cpi = ac > 0 ? ev / ac : 0;
    const spi = pv > 0 ? ev / pv : 0;
    const cv = ev - ac;
    const sv = ev - pv;

    return (
      <div className="space-y-6">
        {/* Input section for PV, EV, AC */}
        <div className="border-2 border-black brutal-shadow bg-white p-6">
          <h3 className="font-bold font-['Chivo'] uppercase mb-4 flex items-center gap-1">Enter Planned & Actual Values <InfoTip text="Enter your Planned Value (BCWS), Earned Value (BCWP), and Actual Cost (ACWP) to generate S-Curves and calculate CPI, SPI, and variances." /></h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-bold uppercase text-[#0047AB] block mb-1 flex items-center">Planned Value (BCWS) $ <InfoTip text="Budgeted Cost of Work Scheduled. The authorized budget for work planned to be completed by a given date." /></label>
              <input type="text" inputMode="decimal" value={pvInput} onChange={e => setPvInput(e.target.value)} className="w-full border-2 border-[#0047AB] p-2.5 text-sm focus:outline-none" placeholder="25000000" data-testid="sc-pv-input" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-emerald-600 block mb-1 flex items-center">Earned Value (BCWP) $ <InfoTip text="Budgeted Cost of Work Performed. The value of work actually completed to date, measured against the budget." /></label>
              <input type="text" inputMode="decimal" value={evInput} onChange={e => setEvInput(e.target.value)} className="w-full border-2 border-emerald-500 p-2.5 text-sm focus:outline-none" placeholder="23000000" data-testid="sc-ev-input" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-[#FF4F00] block mb-1 flex items-center">Actual Cost (ACWP) $ <InfoTip text="Actual Cost of Work Performed. The total cost incurred for work completed to date." /></label>
              <input type="text" inputMode="decimal" value={acInput} onChange={e => setAcInput(e.target.value)} className="w-full border-2 border-[#FF4F00] p-2.5 text-sm focus:outline-none" placeholder="24000000" data-testid="sc-ac-input" />
            </div>
          </div>
          <Button onClick={() => updateEvm(parseFloat(pvInput)||0, parseFloat(evInput)||0, parseFloat(acInput)||0)} className="mt-4 bg-black text-white" data-testid="btn-update-evm">
            Update EVM & Recalculate
          </Button>
        </div>

        {/* S-Curve Canvas */}
        <div className="border border-zinc-200 bg-white brutal-shadow p-6">
          <canvas ref={canvasRef} className="w-full h-[300px]" style={{ imageRendering: 'auto' }} />
        </div>

        {/* Performance Indicators */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className={`border-2 p-4 text-center ${cpi >= 1 ? 'border-emerald-500 bg-emerald-50' : 'border-red-500 bg-red-50'}`}>
            <p className="text-xs uppercase font-bold text-zinc-500">CPI</p>
            <p className={`text-3xl font-black ${cpi >= 1 ? 'text-emerald-600' : 'text-red-600'}`}>{cpi.toFixed(3)}</p>
            <p className="text-[10px] text-zinc-500">{cpi >= 1 ? 'Under Budget' : 'Over Budget'}</p>
          </div>
          <div className={`border-2 p-4 text-center ${spi >= 1 ? 'border-emerald-500 bg-emerald-50' : 'border-red-500 bg-red-50'}`}>
            <p className="text-xs uppercase font-bold text-zinc-500">SPI</p>
            <p className={`text-3xl font-black ${spi >= 1 ? 'text-emerald-600' : 'text-red-600'}`}>{spi.toFixed(3)}</p>
            <p className="text-[10px] text-zinc-500">{spi >= 1 ? 'Ahead' : 'Behind Schedule'}</p>
          </div>
          <div className={`border-2 p-4 text-center ${cv >= 0 ? 'border-emerald-500 bg-emerald-50' : 'border-red-500 bg-red-50'}`}>
            <p className="text-xs uppercase font-bold text-zinc-500">Cost Variance</p>
            <p className={`text-2xl font-black ${cv >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>${cv >= 1e6 ? (cv/1e6).toFixed(2)+'M' : cv >= 1e3 ? (cv/1e3).toFixed(0)+'K' : cv.toFixed(0)}</p>
          </div>
          <div className={`border-2 p-4 text-center ${sv >= 0 ? 'border-emerald-500 bg-emerald-50' : 'border-red-500 bg-red-50'}`}>
            <p className="text-xs uppercase font-bold text-zinc-500">Schedule Variance</p>
            <p className={`text-2xl font-black ${sv >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>${sv >= 1e6 ? (sv/1e6).toFixed(2)+'M' : sv >= 1e3 ? (sv/1e3).toFixed(0)+'K' : sv.toFixed(0)}</p>
          </div>
        </div>

        {/* Trend Summary */}
        <div className="border-2 border-zinc-800 bg-zinc-900 text-white p-6">
          <h3 className="text-xs font-bold uppercase text-zinc-400 mb-3">Performance Assessment</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-zinc-400">Cost Performance:</p>
              <p className={`font-bold ${cpi >= 1 ? 'text-emerald-400' : 'text-red-400'}`}>
                {cpi >= 1.1 ? 'Significantly Under Budget' : cpi >= 1 ? 'On Budget / Under Budget' : cpi >= 0.9 ? 'Slightly Over Budget' : 'Significantly Over Budget'}
              </p>
            </div>
            <div>
              <p className="text-zinc-400">Schedule Performance:</p>
              <p className={`font-bold ${spi >= 1 ? 'text-emerald-400' : 'text-red-400'}`}>
                {spi >= 1.1 ? 'Ahead of Schedule' : spi >= 1 ? 'On Schedule' : spi >= 0.9 ? 'Slightly Behind' : 'Significantly Behind Schedule'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Risk Register Tab
  const RiskRegisterTab = () => {
    const [newRisk, setNewRisk] = useState({ title: "", description: "", category: "technical", probability: 3, impact: 3, mitigation_strategy: "", linked_clauses: [] });
    
    const createRisk = async () => {
      try {
        await axios.post(`${API}/controls/${selectedContract}/risks`, newRisk);
        setShowAddRisk(false);
        setNewRisk({ title: "", description: "", category: "technical", probability: 3, impact: 3, mitigation_strategy: "", linked_clauses: [] });
        fetchControlsData();
        toast.success("Risk added");
      } catch (e) { toast.error("Failed to add risk"); }
    };
    
    const updateRiskStatus = async (riskId, status) => {
      try {
        await axios.put(`${API}/controls/${selectedContract}/risks/${riskId}`, { status });
        fetchControlsData();
        toast.success("Risk status updated");
      } catch (e) { toast.error("Failed to update risk"); }
    };
    
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="font-bold font-['Chivo'] uppercase">Risk Register</h3>
          {user && <Button onClick={() => setShowAddRisk(true)} className="bg-black text-white"><Plus size={16} className="mr-2" /> Add Risk</Button>}
        </div>
        
        {/* Risk Matrix */}
        <div className="border border-zinc-200 bg-white brutal-shadow p-6">
          <h4 className="font-bold text-sm mb-4">Probability / Impact Matrix</h4>
          <div className="grid grid-cols-6 gap-1 text-xs">
            <div></div>
            {[1,2,3,4,5].map(i => <div key={i} className="text-center font-bold p-2">I{i}</div>)}
            {[5,4,3,2,1].map(p => (
              <React.Fragment key={p}>
                <div className="font-bold p-2">P{p}</div>
                {[1,2,3,4,5].map(i => {
                  const score = p * i;
                  const risksInCell = risks.filter(r => r.probability === p && r.impact === i);
                  return (
                    <div key={i} className={`p-2 text-center ${score >= 15 ? 'bg-red-100' : score >= 8 ? 'bg-amber-100' : 'bg-emerald-100'}`}>
                      {risksInCell.length > 0 && <span className="font-bold">{risksInCell.length}</span>}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
        
        {/* Risk List */}
        <div className="space-y-3">
          {risks.length === 0 ? (
            <div className="border border-dashed border-zinc-300 p-12 text-center"><AlertTriangle className="mx-auto mb-4 text-zinc-400" size={48} /><p className="text-zinc-500">No risks registered</p></div>
          ) : (
            risks.map(risk => (
              <div key={risk.id} className={`border-l-4 ${risk.risk_score >= 15 ? 'border-red-500 bg-red-50' : risk.risk_score >= 8 ? 'border-amber-500 bg-amber-50' : 'border-emerald-500 bg-emerald-50'} p-4`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={`${risk.risk_score >= 15 ? 'bg-red-500' : risk.risk_score >= 8 ? 'bg-amber-500' : 'bg-emerald-500'} text-white border-none`}>Score: {risk.risk_score}</Badge>
                      <Badge variant="outline">{risk.category}</Badge>
                      <Badge variant="outline">{risk.status}</Badge>
                    </div>
                    <h4 className="font-bold">{risk.title}</h4>
                    <p className="text-sm text-zinc-600 mt-1">{risk.description}</p>
                    {risk.mitigation_strategy && (
                      <p className="text-xs text-zinc-500 mt-2"><strong>Mitigation:</strong> {risk.mitigation_strategy}</p>
                    )}
                    {risk.linked_clauses?.length > 0 && (
                      <div className="flex gap-1 mt-2">
                        <Link size={12} className="text-zinc-400" />
                        {risk.linked_clauses.map((c, i) => <Badge key={i} variant="outline" className="text-xs">{c}</Badge>)}
                      </div>
                    )}
                  </div>
                  {user && (
                    <Select value={risk.status} onValueChange={(v) => updateRiskStatus(risk.id, v)}>
                      <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="mitigating">Mitigating</SelectItem>
                        <SelectItem value="mitigated">Mitigated</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                        <SelectItem value="accepted">Accepted</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
        
        {/* Add Risk Modal */}
        {showAddRisk && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowAddRisk(false)}>
            <div className="bg-white border-2 border-black brutal-shadow-xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
              <div className="p-4 border-b border-zinc-200 flex justify-between items-center">
                <h3 className="font-bold font-['Chivo'] uppercase">Add Risk</h3>
                <button onClick={() => setShowAddRisk(false)}><X size={20} /></button>
              </div>
              <div className="p-6 space-y-4">
                <input type="text" value={newRisk.title} onChange={(e) => setNewRisk({...newRisk, title: e.target.value})} placeholder="Risk Title *" className="underline-input w-full" />
                <textarea value={newRisk.description} onChange={(e) => setNewRisk({...newRisk, description: e.target.value})} placeholder="Description *" className="underline-input w-full min-h-[80px]" />
                <Select value={newRisk.category} onValueChange={(v) => setNewRisk({...newRisk, category: v})}>
                  <SelectTrigger><SelectValue placeholder="Category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="technical">Technical</SelectItem>
                    <SelectItem value="financial">Financial</SelectItem>
                    <SelectItem value="schedule">Schedule</SelectItem>
                    <SelectItem value="legal">Legal</SelectItem>
                    <SelectItem value="external">External</SelectItem>
                  </SelectContent>
                </Select>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs uppercase text-zinc-500">Probability (1-5)</label>
                    <input type="number" min="1" max="5" value={newRisk.probability} onChange={(e) => setNewRisk({...newRisk, probability: parseInt(e.target.value)})} className="underline-input w-full" />
                  </div>
                  <div>
                    <label className="text-xs uppercase text-zinc-500">Impact (1-5)</label>
                    <input type="number" min="1" max="5" value={newRisk.impact} onChange={(e) => setNewRisk({...newRisk, impact: parseInt(e.target.value)})} className="underline-input w-full" />
                  </div>
                </div>
                <textarea value={newRisk.mitigation_strategy} onChange={(e) => setNewRisk({...newRisk, mitigation_strategy: e.target.value})} placeholder="Mitigation Strategy" className="underline-input w-full" />
                <Button onClick={createRisk} className="w-full bg-black text-white">Add Risk</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // EVM Tab
  const EVMTab = () => {
    const [evmInput, setEvmInput] = useState({ pv: evm?.planned_value || 0, ev: evm?.earned_value || 0, ac: evm?.actual_cost || 0 });
    
    useEffect(() => {
      if (evm) setEvmInput({ pv: evm.planned_value, ev: evm.earned_value, ac: evm.actual_cost });
    }, [evm]);
    
    return (
      <div className="space-y-6">
        <h3 className="font-bold font-['Chivo'] uppercase">Earned Value Management</h3>
        
        {/* EVM Input */}
        {user && (
          <div className="border border-zinc-200 bg-white brutal-shadow p-6">
            <h4 className="font-bold text-sm mb-4">Update EVM Data</h4>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-xs uppercase text-zinc-500">Planned Value (PV)</label>
                <input type="number" value={evmInput.pv} onChange={(e) => setEvmInput({...evmInput, pv: parseFloat(e.target.value)})} className="underline-input w-full" />
              </div>
              <div>
                <label className="text-xs uppercase text-zinc-500">Earned Value (EV)</label>
                <input type="number" value={evmInput.ev} onChange={(e) => setEvmInput({...evmInput, ev: parseFloat(e.target.value)})} className="underline-input w-full" />
              </div>
              <div>
                <label className="text-xs uppercase text-zinc-500">Actual Cost (AC)</label>
                <input type="number" value={evmInput.ac} onChange={(e) => setEvmInput({...evmInput, ac: parseFloat(e.target.value)})} className="underline-input w-full" />
              </div>
            </div>
            <Button onClick={() => updateEvm(evmInput.pv, evmInput.ev, evmInput.ac)} className="mt-4 bg-black text-white">Update EVM</Button>
          </div>
        )}
        
        {/* EVM Metrics */}
        {evm && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bento-item p-4 text-center">
              <p className="text-xs uppercase text-zinc-500">BAC</p>
              <p className="text-2xl font-black">${(evm.budget_at_completion || 0).toLocaleString()}</p>
              <p className="text-xs text-zinc-400">Budget at Completion</p>
            </div>
            <div className="bento-item p-4 text-center">
              <p className="text-xs uppercase text-zinc-500">EAC</p>
              <p className={`text-2xl font-black ${evm.estimate_at_completion > evm.budget_at_completion ? 'text-red-500' : 'text-emerald-500'}`}>${(evm.estimate_at_completion || 0).toLocaleString()}</p>
              <p className="text-xs text-zinc-400">Estimate at Completion</p>
            </div>
            <div className="bento-item p-4 text-center">
              <p className="text-xs uppercase text-zinc-500">VAC</p>
              <p className={`text-2xl font-black ${evm.variance_at_completion >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>${(evm.variance_at_completion || 0).toLocaleString()}</p>
              <p className="text-xs text-zinc-400">Variance at Completion</p>
            </div>
            <div className="bento-item p-4 text-center">
              <p className="text-xs uppercase text-zinc-500">ETC</p>
              <p className="text-2xl font-black">${(evm.estimate_to_complete || 0).toLocaleString()}</p>
              <p className="text-xs text-zinc-400">Estimate to Complete</p>
            </div>
          </div>
        )}
        
        {/* Performance Indices */}
        {evm && (
          <div className="border border-zinc-200 bg-white brutal-shadow p-6">
            <h4 className="font-bold text-sm mb-4">Performance Indices</h4>
            <div className="grid grid-cols-2 gap-8">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium">Cost Performance Index (CPI)</span>
                  <span className={`text-2xl font-black ${evm.cost_performance_index >= 1 ? 'text-emerald-500' : 'text-red-500'}`}>{evm.cost_performance_index?.toFixed(3)}</span>
                </div>
                <Progress value={Math.min(evm.cost_performance_index * 100, 100)} className="h-2" />
                <p className="text-xs text-zinc-400 mt-1">{evm.cost_performance_index >= 1 ? 'Under budget' : 'Over budget'}</p>
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium">Schedule Performance Index (SPI)</span>
                  <span className={`text-2xl font-black ${evm.schedule_performance_index >= 1 ? 'text-emerald-500' : 'text-red-500'}`}>{evm.schedule_performance_index?.toFixed(3)}</span>
                </div>
                <Progress value={Math.min(evm.schedule_performance_index * 100, 100)} className="h-2" />
                <p className="text-xs text-zinc-400 mt-1">{evm.schedule_performance_index >= 1 ? 'Ahead of schedule' : 'Behind schedule'}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Milestones Tab
  const MilestonesTab = () => {
    const [newMilestone, setNewMilestone] = useState({ name: "", description: "", planned_date: "", is_critical: false, payment_linked: false, payment_amount: 0 });
    
    const createMilestone = async () => {
      try {
        await axios.post(`${API}/controls/${selectedContract}/milestones`, newMilestone);
        setShowAddMilestone(false);
        setNewMilestone({ name: "", description: "", planned_date: "", is_critical: false, payment_linked: false, payment_amount: 0 });
        fetchControlsData();
        toast.success("Milestone added");
      } catch (e) { toast.error("Failed to add milestone"); }
    };
    
    const updateMilestoneStatus = async (msId, status, actualDate = null) => {
      try {
        const updates = { status };
        if (actualDate) updates.actual_date = actualDate;
        await axios.put(`${API}/controls/${selectedContract}/milestones/${msId}`, updates);
        fetchControlsData();
        toast.success("Milestone updated");
      } catch (e) { toast.error("Failed to update milestone"); }
    };
    
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="font-bold font-['Chivo'] uppercase">Milestones</h3>
          {user && <Button onClick={() => setShowAddMilestone(true)} className="bg-black text-white"><Plus size={16} className="mr-2" /> Add Milestone</Button>}
        </div>
        
        {milestones.length === 0 ? (
          <div className="border border-dashed border-zinc-300 p-12 text-center"><Flag className="mx-auto mb-4 text-zinc-400" size={48} /><p className="text-zinc-500">No milestones defined</p></div>
        ) : (
          <div className="space-y-3">
            {milestones.map((ms, i) => (
              <div key={ms.id} className={`border ${ms.status === 'completed' ? 'border-emerald-500 bg-emerald-50' : ms.status === 'delayed' ? 'border-red-500 bg-red-50' : 'border-zinc-200 bg-white'} brutal-shadow p-4`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${ms.status === 'completed' ? 'bg-emerald-500' : ms.status === 'delayed' ? 'bg-red-500' : ms.is_critical ? 'bg-[#FF4F00]' : 'bg-zinc-300'} text-white font-bold text-sm`}>{i + 1}</div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-bold">{ms.name}</h4>
                        {ms.is_critical && <Badge className="bg-[#FF4F00] text-white border-none text-xs">Critical</Badge>}
                        {ms.payment_linked && <Badge className="bg-[#0047AB] text-white border-none text-xs">Payment: ${ms.payment_amount?.toLocaleString()}</Badge>}
                      </div>
                      {ms.description && <p className="text-sm text-zinc-600 mt-1">{ms.description}</p>}
                      <div className="flex gap-4 mt-2 text-xs text-zinc-500">
                        <span><Calendar size={12} className="inline mr-1" />Planned: {ms.planned_date}</span>
                        {ms.actual_date && <span><CheckCircle size={12} className="inline mr-1" />Actual: {ms.actual_date}</span>}
                      </div>
                    </div>
                  </div>
                  {user && (
                    <Select value={ms.status} onValueChange={(v) => updateMilestoneStatus(ms.id, v, v === 'completed' ? new Date().toISOString().split('T')[0] : null)}>
                      <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="delayed">Delayed</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Add Milestone Modal */}
        {showAddMilestone && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowAddMilestone(false)}>
            <div className="bg-white border-2 border-black brutal-shadow-xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
              <div className="p-4 border-b border-zinc-200 flex justify-between items-center">
                <h3 className="font-bold font-['Chivo'] uppercase">Add Milestone</h3>
                <button onClick={() => setShowAddMilestone(false)}><X size={20} /></button>
              </div>
              <div className="p-6 space-y-4">
                <input type="text" value={newMilestone.name} onChange={(e) => setNewMilestone({...newMilestone, name: e.target.value})} placeholder="Milestone Name *" className="underline-input w-full" />
                <textarea value={newMilestone.description} onChange={(e) => setNewMilestone({...newMilestone, description: e.target.value})} placeholder="Description" className="underline-input w-full" />
                <input type="date" value={newMilestone.planned_date} onChange={(e) => setNewMilestone({...newMilestone, planned_date: e.target.value})} className="underline-input w-full" />
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={newMilestone.is_critical} onChange={(e) => setNewMilestone({...newMilestone, is_critical: e.target.checked})} />
                    <span className="text-sm">Critical Path</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={newMilestone.payment_linked} onChange={(e) => setNewMilestone({...newMilestone, payment_linked: e.target.checked})} />
                    <span className="text-sm">Payment Linked</span>
                  </label>
                </div>
                {newMilestone.payment_linked && (
                  <input type="number" value={newMilestone.payment_amount} onChange={(e) => setNewMilestone({...newMilestone, payment_amount: parseFloat(e.target.value)})} placeholder="Payment Amount ($)" className="underline-input w-full" />
                )}
                <Button onClick={createMilestone} className="w-full bg-black text-white">Add Milestone</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Change Orders Tab  
  const ChangeOrdersTab = () => {
    const [newCO, setNewCO] = useState({ title: "", description: "", change_type: "scope", cost_impact: 0, schedule_impact_days: 0, linked_clauses: [] });
    
    const createChangeOrder = async () => {
      try {
        await axios.post(`${API}/controls/${selectedContract}/change-orders`, newCO);
        setShowAddChangeOrder(false);
        setNewCO({ title: "", description: "", change_type: "scope", cost_impact: 0, schedule_impact_days: 0, linked_clauses: [] });
        fetchControlsData();
        toast.success("Change order created");
      } catch (e) { toast.error("Failed to create change order"); }
    };
    
    const updateCOStatus = async (coId, status) => {
      try {
        await axios.put(`${API}/controls/${selectedContract}/change-orders/${coId}`, { status });
        fetchControlsData();
        toast.success("Change order updated");
      } catch (e) { toast.error("Failed to update change order"); }
    };
    
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="font-bold font-['Chivo'] uppercase">Change Orders / Variations</h3>
          {user && <Button onClick={() => setShowAddChangeOrder(true)} className="bg-black text-white"><Plus size={16} className="mr-2" /> Add Change Order</Button>}
        </div>
        
        {/* Summary */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bento-item p-4 text-center">
            <p className="text-3xl font-black">{changeOrders.length}</p>
            <p className="text-xs uppercase text-zinc-500">Total COs</p>
          </div>
          <div className="bento-item p-4 text-center">
            <p className="text-3xl font-black text-amber-500">{changeOrders.filter(c => c.status === 'pending').length}</p>
            <p className="text-xs uppercase text-zinc-500">Pending</p>
          </div>
          <div className="bento-item p-4 text-center">
            <p className="text-3xl font-black">${changeOrders.filter(c => c.status === 'approved').reduce((a, c) => a + (c.cost_impact || 0), 0).toLocaleString()}</p>
            <p className="text-xs uppercase text-zinc-500">Cost Impact</p>
          </div>
          <div className="bento-item p-4 text-center">
            <p className="text-3xl font-black">{changeOrders.filter(c => c.status === 'approved').reduce((a, c) => a + (c.schedule_impact_days || 0), 0)}</p>
            <p className="text-xs uppercase text-zinc-500">Days Impact</p>
          </div>
        </div>
        
        {/* CO List */}
        {changeOrders.length === 0 ? (
          <div className="border border-dashed border-zinc-300 p-12 text-center"><FileText className="mx-auto mb-4 text-zinc-400" size={48} /><p className="text-zinc-500">No change orders</p></div>
        ) : (
          <div className="border border-zinc-200 bg-white brutal-shadow overflow-hidden">
            <table className="brutal-table">
              <thead><tr><th>Title</th><th>Type</th><th>Cost Impact</th><th>Schedule Impact</th><th>Status</th><th>Action</th></tr></thead>
              <tbody>
                {changeOrders.map(co => (
                  <tr key={co.id}>
                    <td>
                      <p className="font-medium">{co.title}</p>
                      <p className="text-xs text-zinc-500">{co.description?.substring(0, 50)}...</p>
                    </td>
                    <td><Badge variant="outline">{co.change_type}</Badge></td>
                    <td className={co.cost_impact > 0 ? 'text-red-500' : co.cost_impact < 0 ? 'text-emerald-500' : ''}>${(co.cost_impact || 0).toLocaleString()}</td>
                    <td>{co.schedule_impact_days || 0} days</td>
                    <td><Badge className={`${co.status === 'approved' ? 'bg-emerald-500' : co.status === 'rejected' ? 'bg-red-500' : co.status === 'implemented' ? 'bg-[#0047AB]' : 'bg-amber-500'} text-white border-none`}>{co.status}</Badge></td>
                    <td>
                      {user && co.status === 'pending' && (
                        <div className="flex gap-2">
                          <button onClick={() => updateCOStatus(co.id, 'approved')} className="text-emerald-500 hover:text-emerald-700"><ThumbsUp size={16} /></button>
                          <button onClick={() => updateCOStatus(co.id, 'rejected')} className="text-red-500 hover:text-red-700"><ThumbsDown size={16} /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Add CO Modal */}
        {showAddChangeOrder && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowAddChangeOrder(false)}>
            <div className="bg-white border-2 border-black brutal-shadow-xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
              <div className="p-4 border-b border-zinc-200 flex justify-between items-center">
                <h3 className="font-bold font-['Chivo'] uppercase">Add Change Order</h3>
                <button onClick={() => setShowAddChangeOrder(false)}><X size={20} /></button>
              </div>
              <div className="p-6 space-y-4">
                <input type="text" value={newCO.title} onChange={(e) => setNewCO({...newCO, title: e.target.value})} placeholder="Title *" className="underline-input w-full" />
                <textarea value={newCO.description} onChange={(e) => setNewCO({...newCO, description: e.target.value})} placeholder="Description *" className="underline-input w-full min-h-[80px]" />
                <Select value={newCO.change_type} onValueChange={(v) => setNewCO({...newCO, change_type: v})}>
                  <SelectTrigger><SelectValue placeholder="Change Type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scope">Scope</SelectItem>
                    <SelectItem value="schedule">Schedule</SelectItem>
                    <SelectItem value="cost">Cost</SelectItem>
                    <SelectItem value="design">Design</SelectItem>
                  </SelectContent>
                </Select>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs uppercase text-zinc-500">Cost Impact ($)</label>
                    <input type="number" value={newCO.cost_impact} onChange={(e) => setNewCO({...newCO, cost_impact: parseFloat(e.target.value)})} className="underline-input w-full" />
                  </div>
                  <div>
                    <label className="text-xs uppercase text-zinc-500">Schedule Impact (Days)</label>
                    <input type="number" value={newCO.schedule_impact_days} onChange={(e) => setNewCO({...newCO, schedule_impact_days: parseInt(e.target.value)})} className="underline-input w-full" />
                  </div>
                </div>
                <Button onClick={createChangeOrder} className="w-full bg-black text-white">Create Change Order</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Lessons Learned Tab
  const LessonsLearnedTab = () => {
    const [newLesson, setNewLesson] = useState({ title: "", description: "", category: "risk", lesson_type: "improvement", impact_area: "medium", linked_clauses: [], recommended_clause_text: "", tags: [] });
    const [tagInput, setTagInput] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [filterCategory, setFilterCategory] = useState("");
    
    const createLesson = async () => {
      try {
        await axios.post(`${API}/lessons`, { ...newLesson, contract_id: selectedContract });
        setShowAddLesson(false);
        setNewLesson({ title: "", description: "", category: "risk", lesson_type: "improvement", impact_area: "medium", linked_clauses: [], recommended_clause_text: "", tags: [] });
        fetchControlsData();
        fetchAllLessons();
        toast.success("Lesson learned added");
      } catch (e) { toast.error("Failed to add lesson"); }
    };
    
    const createClauseFromLesson = async (lessonId) => {
      try {
        await axios.post(`${API}/lessons/${lessonId}/create-clause`);
        fetchUserClauses();
        toast.success("User clause created from lesson");
      } catch (e) { toast.error(e.response?.data?.detail || "Failed to create clause"); }
    };
    
    const addTag = () => {
      if (tagInput && !newLesson.tags.includes(tagInput)) {
        setNewLesson({...newLesson, tags: [...newLesson.tags, tagInput]});
        setTagInput("");
      }
    };
    
    const filteredLessons = allLessons.filter(l => {
      if (filterCategory && filterCategory !== "all" && l.category !== filterCategory) return false;
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        return l.title.toLowerCase().includes(term) || l.description.toLowerCase().includes(term) || l.tags?.some(t => t.toLowerCase().includes(term));
      }
      return true;
    });
    
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="font-bold font-['Chivo'] uppercase">Lessons Learned</h3>
          {user && <Button onClick={() => setShowAddLesson(true)} className="bg-black text-white"><Plus size={16} className="mr-2" /> Add Lesson</Button>}
        </div>
        
        {/* Search & Filter */}
        <div className="flex gap-4">
          <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search lessons..." className="flex-1 px-4 py-2 border border-zinc-200" />
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-40"><SelectValue placeholder="All Categories" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="risk">Risk</SelectItem>
              <SelectItem value="schedule">Schedule</SelectItem>
              <SelectItem value="cost">Cost</SelectItem>
              <SelectItem value="quality">Quality</SelectItem>
              <SelectItem value="communication">Communication</SelectItem>
              <SelectItem value="technical">Technical</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Lessons List */}
        {filteredLessons.length === 0 ? (
          <div className="border border-dashed border-zinc-300 p-12 text-center"><BookOpen className="mx-auto mb-4 text-zinc-400" size={48} /><p className="text-zinc-500">No lessons learned yet</p></div>
        ) : (
          <div className="space-y-4">
            {filteredLessons.map(lesson => (
              <div key={lesson.id} className={`border-l-4 ${lesson.lesson_type === 'success' ? 'border-emerald-500' : lesson.lesson_type === 'failure' ? 'border-red-500' : 'border-[#0047AB]'} bg-white brutal-shadow p-4`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={`${lesson.lesson_type === 'success' ? 'bg-emerald-500' : lesson.lesson_type === 'failure' ? 'bg-red-500' : 'bg-[#0047AB]'} text-white border-none`}>{lesson.lesson_type}</Badge>
                      <Badge variant="outline">{lesson.category}</Badge>
                      <Badge variant="outline" className={`${lesson.impact_area === 'high' ? 'border-red-500 text-red-500' : lesson.impact_area === 'medium' ? 'border-amber-500 text-amber-500' : 'border-zinc-500'}`}>{lesson.impact_area} impact</Badge>
                    </div>
                    <h4 className="font-bold">{lesson.title}</h4>
                    <p className="text-sm text-zinc-600 mt-1">{lesson.description}</p>
                    {lesson.linked_clauses?.length > 0 && (
                      <div className="flex gap-1 mt-2 items-center">
                        <Link size={12} className="text-zinc-400" />
                        {lesson.linked_clauses.map((c, i) => <Badge key={i} variant="outline" className="text-xs">{c}</Badge>)}
                      </div>
                    )}
                    {lesson.tags?.length > 0 && (
                      <div className="flex gap-1 mt-2 items-center">
                        <Tag size={12} className="text-zinc-400" />
                        {lesson.tags.map((t, i) => <Badge key={i} variant="outline" className="text-xs bg-zinc-100">{t}</Badge>)}
                      </div>
                    )}
                    {lesson.recommended_clause_text && (
                      <div className="mt-3 p-3 bg-blue-50 border-l-2 border-[#0047AB]">
                        <p className="text-xs uppercase text-[#0047AB] mb-1">Recommended Clause</p>
                        <p className="text-sm">{lesson.recommended_clause_text.substring(0, 200)}...</p>
                      </div>
                    )}
                  </div>
                  {user && lesson.recommended_clause_text && (
                    <Button onClick={() => createClauseFromLesson(lesson.id)} variant="outline" className="text-xs"><Lightbulb size={14} className="mr-1" /> Create Clause</Button>
                  )}
                </div>
                <p className="text-xs text-zinc-400 mt-2">By {lesson.user_name} • {new Date(lesson.created_at).toLocaleDateString()}</p>
              </div>
            ))}
          </div>
        )}
        
        {/* User-Defined Clauses */}
        {userClauses.length > 0 && (
          <div className="border border-zinc-200 bg-white brutal-shadow p-6">
            <h4 className="font-bold font-['Chivo'] uppercase mb-4">Your Custom Clauses ({userClauses.length})</h4>
            <div className="space-y-3">
              {userClauses.map(clause => (
                <div key={clause.id} className="p-3 bg-zinc-50 border-l-2 border-[#0047AB]">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium">{clause.title}</span>
                    <Badge variant="outline">{clause.category}</Badge>
                    <Badge className={`${clause.risk_level === 'high' ? 'bg-[#FF4F00]' : clause.risk_level === 'medium' ? 'bg-amber-500' : 'bg-emerald-500'} text-white border-none text-xs`}>{clause.risk_level}</Badge>
                  </div>
                  <p className="text-sm text-zinc-600">{clause.text.substring(0, 150)}...</p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Add Lesson Modal */}
        {showAddLesson && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-auto" onClick={() => setShowAddLesson(false)}>
            <div className="bg-white border-2 border-black brutal-shadow-xl max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()}>
              <div className="p-4 border-b border-zinc-200 flex justify-between items-center">
                <h3 className="font-bold font-['Chivo'] uppercase">Add Lesson Learned</h3>
                <button onClick={() => setShowAddLesson(false)}><X size={20} /></button>
              </div>
              <div className="p-6 space-y-4 max-h-[70vh] overflow-auto">
                <input type="text" value={newLesson.title} onChange={(e) => setNewLesson({...newLesson, title: e.target.value})} placeholder="Lesson Title *" className="underline-input w-full" />
                <textarea value={newLesson.description} onChange={(e) => setNewLesson({...newLesson, description: e.target.value})} placeholder="Description - What happened and what was learned? *" className="underline-input w-full min-h-[100px]" />
                <div className="grid grid-cols-3 gap-4">
                  <Select value={newLesson.category} onValueChange={(v) => setNewLesson({...newLesson, category: v})}>
                    <SelectTrigger><SelectValue placeholder="Category" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="risk">Risk</SelectItem>
                      <SelectItem value="schedule">Schedule</SelectItem>
                      <SelectItem value="cost">Cost</SelectItem>
                      <SelectItem value="quality">Quality</SelectItem>
                      <SelectItem value="communication">Communication</SelectItem>
                      <SelectItem value="technical">Technical</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={newLesson.lesson_type} onValueChange={(v) => setNewLesson({...newLesson, lesson_type: v})}>
                    <SelectTrigger><SelectValue placeholder="Type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="success">Success</SelectItem>
                      <SelectItem value="failure">Failure</SelectItem>
                      <SelectItem value="improvement">Improvement</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={newLesson.impact_area} onValueChange={(v) => setNewLesson({...newLesson, impact_area: v})}>
                    <SelectTrigger><SelectValue placeholder="Impact" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">High Impact</SelectItem>
                      <SelectItem value="medium">Medium Impact</SelectItem>
                      <SelectItem value="low">Low Impact</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs uppercase text-zinc-500">Tags</label>
                  <div className="flex gap-2 mt-1">
                    <input type="text" value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && addTag()} placeholder="Add tag..." className="flex-1 px-3 py-2 border border-zinc-200" />
                    <Button type="button" onClick={addTag} variant="outline">Add</Button>
                  </div>
                  {newLesson.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {newLesson.tags.map((t, i) => (
                        <Badge key={i} variant="outline" className="cursor-pointer" onClick={() => setNewLesson({...newLesson, tags: newLesson.tags.filter((_, idx) => idx !== i)})}>{t} ×</Badge>
                      ))}
                    </div>
                  )}
                </div>
                <textarea value={newLesson.recommended_clause_text} onChange={(e) => setNewLesson({...newLesson, recommended_clause_text: e.target.value})} placeholder="Recommended Clause Text (for future contracts)" className="underline-input w-full min-h-[80px]" />
                <Button onClick={createLesson} className="w-full bg-black text-white">Add Lesson</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const tabs = [
    { id: "overview", label: "Overview", icon: PieChart },
    { id: "kpis", label: "KPIs", icon: Gauge },
    { id: "scurves", label: "S-Curves & Trends", icon: Activity },
    { id: "risks", label: "Risk Register", icon: AlertTriangle },
    { id: "evm", label: "EVM", icon: TrendingUp },
    { id: "milestones", label: "Milestones", icon: Flag },
    { id: "change-orders", label: "Change Orders", icon: FileText },
    { id: "lessons", label: "Lessons Learned", icon: BookOpen }
  ];

  return (
    <div className="space-y-6" data-testid="controls-view" data-export-region>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight uppercase font-['Chivo'] mb-2">Project Controls</h1>
          <p className="text-zinc-500">Comprehensive project monitoring, KPIs, risk register, and lessons learned</p>
        </div>
        <ExportToolbar 
          title="Project Controls" 
          pdfFilename="project-controls" 
          csvData={kpis.length > 0 ? kpis.map(k => ({ name: k.name, category: k.category, actual: k.actual, planned: k.planned, value: k.value, status: k.status, green_threshold: k.green_threshold, amber_threshold: k.amber_threshold })) : null} 
          csvFilename="kpis" 
        />
      </div>

      {/* Contract Selector */}
      <div className="border border-zinc-200 bg-white brutal-shadow p-4">
        <label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Select Contract</label>
        <Select value={selectedContract} onValueChange={setSelectedContract}>
          <SelectTrigger className="w-full md:w-96" data-testid="select-contract-controls">
            <SelectValue placeholder="Choose a contract to view controls" />
          </SelectTrigger>
          <SelectContent>
            {contracts.map(c => (
              <SelectItem key={c.id} value={c.id}>{c.project_name} ({c.framework})</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedContract ? (
        loading ? (
          <div className="text-center py-12">Loading controls data...</div>
        ) : (
          <>
            {/* Tabs */}
            <div className="border-b border-zinc-200 flex overflow-x-auto">
              {tabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === tab.id ? 'border-black text-black' : 'border-transparent text-zinc-500 hover:text-zinc-700'}`}>
                    <Icon size={16} />{tab.label}
                  </button>
                );
              })}
            </div>

            {/* Tab Content */}
            <div className="mt-6">
              {activeTab === "overview" && <OverviewTab />}
              {activeTab === "kpis" && <KPIsTab />}
              {activeTab === "scurves" && <SCurvesTab />}
              {activeTab === "risks" && <RiskRegisterTab />}
              {activeTab === "evm" && <EVMTab />}
              {activeTab === "milestones" && <MilestonesTab />}
              {activeTab === "change-orders" && <ChangeOrdersTab />}
              {activeTab === "lessons" && <LessonsLearnedTab />}
            </div>
          </>
        )
      ) : (
        <div className="border border-dashed border-zinc-300 bg-zinc-50 p-12 text-center">
          <Gauge className="mx-auto mb-4 text-zinc-400" size={48} />
          <p className="text-zinc-500">Select a contract to view project controls</p>
        </div>
      )}
    </div>
  );
};

// Main App
function App() {
  const [activeView, setActiveView] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [clauses, setClauses] = useState([]);
  const [selectedContract, setSelectedContract] = useState(null);
  const [showSubscription, setShowSubscription] = useState(false);
  const [showSignIn, setShowSignIn] = useState(!localStorage.getItem('gpi_token'));
  const [showDisclaimer, setShowDisclaimer] = useState(() => {
    // Show disclaimer every session (cleared when browser tab closes)
    if (localStorage.getItem('gpi_token') && !sessionStorage.getItem('gpi_disclaimer_seen')) {
      return true;
    }
    return false;
  });

  const fetchData = async () => {
    try {
      const [contractsRes, clausesRes] = await Promise.all([axios.get(`${API}/contracts`), axios.get(`${API}/clauses`)]);
      setContracts(contractsRes.data);
      setClauses(clausesRes.data);
    } catch (error) { console.error("Failed to fetch:", error); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSignInAuth = (token, user) => {
    localStorage.setItem('gpi_token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setShowSignIn(false);
    setShowDisclaimer(true);
  };
  
  const handleDisclaimerAccept = () => {
    sessionStorage.setItem('gpi_disclaimer_seen', 'true');
    setShowDisclaimer(false);
    window.location.reload();
  };

  if (showSignIn) {
    return (
      <AuthProvider>
        <Toaster position="top-right" />
        {showDisclaimer && <DataSecurityDisclaimer onAccept={handleDisclaimerAccept} />}
        <SignInPage onAuth={handleSignInAuth} />
      </AuthProvider>
    );
  }

  const renderView = () => {
    switch (activeView) {
      case "dashboard": return <DashboardView setActiveView={setActiveView} contracts={contracts} clauses={clauses} />;
      case "generator": return <ContractGeneratorView onContractGenerated={fetchData} clauses={clauses} setActiveView={setActiveView} />;
      case "analyzer": return <ContractAnalyzerView />;
      case "clauses": return <ClauseLibraryView clauses={clauses} />;
      case "expanded-clauses": return <ExpandedClauseLibraryView />;
      case "analytics": return <DashboardAnalyticsView contracts={contracts} />;
      case "controls": return <ProjectControlsView contracts={contracts} />;
      case "risk-analysis": return <RiskAnalysisView contracts={contracts} />;
      case "changes": return <ChangeManagementView contracts={contracts} />;
      case "timelines": return <TimelineView contracts={contracts} />;
      case "correspondences": return <CorrespondencesView contracts={contracts} />;
      case "financial": return <FinancialSummaryView contracts={contracts} />;
      case "copilot": return <CopilotView />;
      case "ai-engine": return <EnhancedAIView contracts={contracts} />;
      case "negotiation": return <NegotiationSimulatorView contracts={contracts} />;
      case "scenarios": return <ScenarioSimulatorView contracts={contracts} />;
      case "structpro": return <StructProView setActiveView={setActiveView} />;
      case "delay-analysis": return <DelayAnalysisView contracts={contracts} />;
      case "delay": return <DelayPredictionView />;
      case "claims": return <ClaimPreventionView clauses={clauses} />;
      case "compare": return <ContractComparisonView contracts={contracts} />;
      case "pmtools": return <PMToolsView />;
      case "pm-integration": return <PMIntegrationView />;
      case "realtime": return <RealTimeCollabView contracts={contracts} />;
      case "contracts": return <ContractsView contracts={contracts} setActiveView={setActiveView} setSelectedContract={setSelectedContract} />;
      case "contract-detail": return <ContractDetailView contractId={selectedContract} onBack={() => setActiveView("contracts")} />;
      case "kpi-dashboard": return <KPIDashboardView />;
      case "organization": return <OrganizationView />;
      default: return <DashboardView setActiveView={setActiveView} contracts={contracts} clauses={clauses} />;
    }
  };

  return (
    <AuthProvider>
      <FormDataProvider>
        <TooltipProvider>
          <div className="min-h-screen bg-zinc-50">
            <Toaster position="top-right" />
            {showDisclaimer && <DataSecurityDisclaimer onAccept={handleDisclaimerAccept} />}
            <SubscriptionModal isOpen={showSubscription} onClose={() => setShowSubscription(false)} />
            <div className="flex">
              <Sidebar activeView={activeView} setActiveView={setActiveView} isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
              <main className="flex-1 min-h-screen">
                <SubscriptionBanner onUpgrade={() => setShowSubscription(true)} />
                <header className="md:hidden p-4 bg-white border-b border-zinc-200 flex items-center justify-between sticky top-0 z-30">
                  <button onClick={() => setSidebarOpen(true)} className="p-2"><Menu size={24} /></button>
                  <h1 className="font-black font-['Chivo'] uppercase">GPI<span className="text-[#FF4F00]">.</span>Contracts</h1>
                  <div className="w-10" />
                </header>
                <div className="p-6 md:p-8 lg:p-12">{renderView()}</div>
              </main>
            </div>
          </div>
        </TooltipProvider>
      </FormDataProvider>
    </AuthProvider>
  );
}

export default App;
